<template>
  <div class="attendance-dashboard" :class="{ 'employee-view': viewMode === 'my', 'team-view': viewMode === 'team', 'org-view': viewMode === 'org' }">
    
    <!-- View Switcher for HR/Manager Roles (toolbar sits tight under navbar PageTitle) -->
    <b-row v-if="canViewTeam" class="mb-0 mt-0 attendance-toolbar-row">
      <b-col cols="12" class="pt-0 pb-0">
        <div class="view-switcher-container attendance-page-toolbar">
          <div class="attendance-page-toolbar__inner">
            <div class="view-mode-buttons" role="group" aria-label="Attendance view">
              <b-button
                v-for="option in (viewModeOptions || [])"
                :key="option.value"
                :variant="(viewMode || 'my') === option.value ? 'primary' : 'outline-primary'"
                size="sm"
                class="view-mode-btn-icon-only view-mode-btn"
                :class="{ 'view-mode-btn--active': (viewMode || 'my') === option.value }"
                :aria-label="option.text"
                :aria-pressed="(viewMode || 'my') === option.value ? 'true' : 'false'"
                @click="onViewModeButtonClick(option.value)"
              >
                <feather-icon :icon="option.icon" size="16" class="view-mode-btn__icon" />
                <span class="view-mode-btn__label">{{ option.shortText || option.text }}</span>
              </b-button>
            </div>
            <div class="attendance-page-toolbar__filters">
              <b-button
                v-if="!hasActiveFilters"
                size="sm"
                variant="outline-secondary"
                class="filter-btn"
                @click="openFilterModal"
              >
                <feather-icon icon="FilterIcon" size="16" />
                <span class="filter-btn__label">Filters</span>
              </b-button>
              <template v-else>
                <b-button
                  size="sm"
                  variant="info"
                  class="filter-btn filter-btn--active"
                  @click="openFilterModal"
                >
                  <feather-icon icon="FilterIcon" size="16" />
                  <span class="filter-btn__label">Filters</span>
                  <span class="ml-1 d-none d-md-inline" v-if="dateRangeFilter">{{ formatDateRange(dateRangeFilter) }}</span>
                  <span class="ml-1" v-if="selectedAccountManager && canApplyOnBehalfAll">•</span>
                  <span class="ml-1" v-if="selectedTeamWiseEmployee && canApplyOnBehalf">•</span>
                  <span class="ml-1" v-if="selectedTeamMember && canApplyOnBehalf && !canApplyOnBehalfAll">•</span>
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  class="clear-filter-btn"
                  aria-label="Clear All Filters"
                  @click="clearAllFilters"
                >
                  <feather-icon icon="XIcon" size="14" />
                </b-button>
              </template>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ========== EMPLOYEE VIEW (revamped) ========== -->
    <template v-if="viewMode === 'my'">
      <!-- Top: KPI strip + Quick Actions (shared row height) -->
      <b-row class="mb-0 employee-top-row align-items-stretch">
        <b-col lg="8" md="12" class="mb-0 employee-kpi-col">
          <attendance-stats
            :view-mode="viewMode"
            :can-apply-on-behalf-all="canApplyOnBehalfAll"
            :selected-account-manager="selectedAccountManager"
            :selected-team-wise-employee="selectedTeamWiseEmployee"
            :can-manage-leaves="canManageLeaves"
            :team-summary="teamSummary"
            :displayed-team-members-count="displayedTeamMembersCount"
            :pending-h-r-approvals-length="pendingHRApprovals.length"
            :display-k-p-i-stats="displayKPIStats"
            :upcoming-team-leaves-length="upcomingTeamLeaves.length"
            :team-regularization-count="teamRegularizationCount"
            :kpi-stats="kpiStats"
            :leave-breakdown-html="leaveBreakdownHtml"
            @approval-pending-click="handleApprovalPendingClick"
          />
        </b-col>
        <b-col lg="4" md="12" class="mb-0 pl-lg-1 employee-actions-col">
          <quick-actions-card
            inline
            @apply-leave="openApplyLeaveModal"
            @apply-wfh="openWFHModal"
            @request-comp-off="openRequestCompOffBalanceModal"
            @apply-comp-off="openApplyCompOffModal"
          />
        </b-col>
      </b-row>

      <!-- Main: Calendar | Upcoming | Announcements (explicit 3 columns) -->
      <b-row class="mb-0 employee-main-row align-items-stretch">
        <b-col xl="4" lg="4" md="12" class="mb-0 mb-3 mb-lg-0 employee-main-col employee-calendar-col">
          <attendance-calendar
            compact
            :view-mode="viewMode"
            :selected-month.sync="selectedMonth"
            :selected-year.sync="selectedYear"
            :month-options="monthOptions"
            :year-options="yearOptions"
            :week-days="weekDays"
            :calendar-weeks="calendarWeeks"
            :get-day-tooltip="getDayTooltip"
            @date-click="handleDateClick"
            @apply-leave="onCalendarApplyLeave"
            @apply-wfh="onCalendarApplyWfh"
            @apply-comp-off="onCalendarApplyCompOff"
            @request-comp-off="onCalendarRequestCompOff"
          />
        </b-col>
        <b-col xl="4" lg="4" md="12" class="mb-0 mb-3 mb-lg-0 pl-lg-1 employee-main-col employee-upcoming-col">
          <employee-upcoming-panel
            :holidays="upcomingHolidays"
            :company-wfh-days="upcomingCompanyWfhDays"
            :leaves="upcomingLeaves"
            :leaves-loading="loadingLeaves"
            :can-create-holiday="canManageHolidays"
            :can-create-company-wfh="canManageCompanyWfhDays"
            :format-date="formatDate"
            :format-leave-type="formatLeaveType"
            :format-approval-status="formatApprovalStatus"
            @create-holiday="openCreateHolidayModal"
            @create-company-wfh-day="openCreateCompanyWfhDayModal"
            @leave-click="openLeaveDetailModal"
          />
        </b-col>
        <b-col xl="4" lg="4" md="12" class="mb-0 mb-4 mb-lg-0 pl-lg-1 employee-main-col employee-announcements-col">
          <!-- Inline panel (parent-scoped CSS) — always visible; do not use AnnouncementsCard here -->
          <div class="employee-announcements-panel">
            <div class="employee-announcements-panel__header">
              <h5 class="mb-0">
                <feather-icon icon="BellIcon" size="15" class="mr-2 announcement-header-icon" />
                Announcements
              </h5>
              <div class="employee-announcements-panel__actions">
                <b-button
                  v-if="canDownloadAnnouncements"
                  size="sm"
                  variant="outline-success"
                  class="announcement-action-btn"
                  aria-label="Download Birthdays & Anniversaries"
                  @click="openCelebrationDownloadModal"
                >
                  <feather-icon icon="DownloadIcon" size="12" />
                </b-button>
                <b-button
                  v-if="canManageAnnouncements"
                  size="sm"
                  variant="primary"
                  class="announcement-action-btn"
                  aria-label="Add Announcement"
                  @click="openCreateAnnouncementModal"
                >
                  <feather-icon icon="PlusIcon" size="12" />
                </b-button>
              </div>
            </div>
            <template v-if="(displayedAnnouncementsWithUniqueKeys || []).length">
              <div class="employee-announcements-panel__list">
                <div
                  v-for="item in displayedAnnouncementsWithUniqueKeys"
                  :key="item.id"
                  class="employee-announcements-panel__item"
                  :class="{
                    'is-birthday': item.type === 'birthday',
                    'is-anniversary': item.type === 'anniversary',
                  }"
                >
                  <div
                    class="employee-announcements-panel__badge"
                    :class="{
                      'badge-birthday': item.type === 'birthday',
                      'badge-anniversary': item.type === 'anniversary',
                    }"
                  >
                    <feather-icon
                      :icon="item.type === 'birthday' ? 'HeartIcon' : (item.type === 'anniversary' ? 'AwardIcon' : 'InfoIcon')"
                      size="14"
                    />
                  </div>
                  <div class="employee-announcements-panel__content">
                    <div class="d-flex justify-content-between align-items-start">
                      <div class="employee-announcements-panel__title">{{ item.title }}</div>
                      <b-button
                        v-if="canCancelAnnouncement(item) && item.type === 'announcement'"
                        variant="link"
                        size="sm"
                        class="p-0 ml-2"
                        aria-label="Cancel announcement"
                        @click="cancelAnnouncement(item.id)"
                      >
                        <feather-icon icon="XIcon" size="16" class="text-danger" />
                      </b-button>
                    </div>
                    <div class="employee-announcements-panel__message">{{ item.message }}</div>
                    <div class="employee-announcements-panel__date">
                      <feather-icon icon="CalendarIcon" size="12" />
                      {{
                        (item.type === 'birthday' || item.type === 'anniversary')
                          ? formatDateMonthOnly(item.birthday_date || item.anniversary_date || item.date)
                          : formatDate(item.created_at || item.date)
                      }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="employee-announcements-panel__empty">
              <feather-icon icon="BellOffIcon" size="20" class="mb-1" />
              <p class="mb-0">No announcements</p>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Leaves & Regularization Tabs -->
      <b-row class="mb-0 employee-leaves-reg-row">
        <b-col cols="12" class="employee-leaves-reg-col">
          <leaves-regularization-tabs
            id="leaves-regularization"
            :active-tab.sync="leavesRegularizationActiveTab"
            :leaves-data="leavesData"
            :regularization-data="regularizationData"
            :displayed-leaves-data="displayedLeavesData"
            :displayed-regularization-data="displayedRegularizationData"
            :displayed-leaves-count="displayedLeavesCount"
            :displayed-regularization-count="displayedRegularizationCount"
            :display-limit="regularizationDisplayLimit"
            :fields="regularizationFields"
            :leaves-tab-fields="leavesTabFields"
            :view-mode="viewMode"
            :show-download-button="false"
            :show-leaves-download-button="false"
            :has-active-filters="hasActiveRegularizationFilters"
            :status-filter="teamLeaveFilter"
            :can-manage-leaves="canManageLeaves"
            :can-approve-leave-all-stages="canApproveLeaveAllStages"
            :can-approve-leave="canApproveLeave"
            :can-approve-regularization-all-stages="canApproveRegularizationAllStages"
            :can-apply-regularization="canApplyRegularization"
            :get-user-name="getUserName"
            :format-date="formatDate"
            :format-time="formatTime"
            :get-approval-status-variant="getApprovalStatusVariant"
            :format-approval-status="formatApprovalStatus"
            :get-issue-variant="getIssueVariant"
            :is-manager-of-employee="isManagerOfEmployee"
            :needs-leave-approval="needsLeaveApproval"
            :is-leave-pending-manager-approval="isLeavePendingManagerApproval"
            :can-cancel-leave="canCancelLeave"
            :is-regularization-raised="isRegularizationRaised"
            :needs-manager-approval="needsManagerApproval"
            :get-employee-leave-balance="getEmployeeLeaveBalance"
            @download-excel="downloadRegularizationExcel"
            @download-leaves-excel="downloadLeavesExcel"
            @clear-filters="clearRegularizationFilters"
            @open-filter-modal="openRegularizationFilterModal"
            @leave-click="openLeaveDetailModal"
            @approve-leave="approveLeave"
            @reject-leave="rejectLeave"
            @cancel-leave="cancelLeave"
            @approve-regularization="openApproveRegularizationModal"
            @reject-regularization="openRejectRegularizationModal"
            @open-regularization-modal="openRegularizationModal"
            @load-more-leaves="loadMoreLeaves"
            @reset-leaves-display="resetLeavesDisplay"
            @load-more-regularizations="loadMoreRegularizations"
            @reset-regularization-display="resetRegularizationDisplay"
          />
        </b-col>
      </b-row>
    </template>

    <!-- ========== TEAM VIEW (Team Leader–first) ========== -->
    <template v-else-if="viewMode === 'team'">
      <!-- Top: Team KPI strip + Quick Actions -->
      <b-row class="mb-0 team-top-row align-items-stretch">
        <b-col lg="8" md="12" class="mb-0 team-kpi-col">
          <attendance-stats
            :view-mode="viewMode"
            :can-apply-on-behalf-all="canApplyOnBehalfAll"
            :can-apply-on-behalf="canApplyOnBehalf"
            :selected-account-manager="selectedAccountManager"
            :selected-team-member="selectedTeamMember"
            :selected-team-wise-employee="selectedTeamWiseEmployee"
            :can-manage-leaves="canManageLeaves"
            :team-summary="teamSummary"
            :displayed-team-members-count="displayedTeamMembersCount"
            :pending-h-r-approvals-length="pendingHRApprovals.length"
            :display-k-p-i-stats="displayKPIStats"
            :upcoming-team-leaves-length="upcomingTeamLeaves.length"
            :out-today-count="teamOutTodayCount"
            :present-today-count="teamPresentTodayCount"
            :absent-today-count="teamAbsentTodayCount"
            :wfh-today-count="teamWfhTodayCount"
            :regularization-to-approve-count="teamRegularizationToApproveCount"
            :leave-approvals-to-approve-count="(pendingLeaveApprovalsForQueue || []).length"
            :team-regularization-count="teamRegularizationCount"
            :kpi-stats="kpiStats"
            :leave-breakdown-html="leaveBreakdownHtml"
            @approval-pending-click="handleApprovalPendingClick"
            @reg-to-approve-click="handleRegToApproveClick"
          />
        </b-col>
        <b-col lg="4" md="12" class="mb-0 pl-lg-1 team-actions-col">
          <quick-actions-card
            inline
            @apply-leave="openApplyLeaveModal"
            @apply-wfh="openWFHModal"
            @request-comp-off="openRequestCompOffBalanceModal"
            @apply-comp-off="openApplyCompOffModal"
          />
        </b-col>
      </b-row>

      <!-- Aggregate: mid-row + leaves in one stack so ≤991 can order Leaves before Leave Balance -->
      <div v-if="isTeamLeaderAggregateView" class="team-mid-leaves-stack">
        <!-- Shared mid-row: Snapshot | Today's attendance | Leave Balance -->
        <team-mid-row
          panel-title="Team snapshot"
          :pending-leave-count="(pendingLeaveApprovalsForQueue || []).length"
          :probation-ending="effectivePeopleOnProbation"
          :probation-source-missing="probationSourceMissing && !(effectivePeopleOnProbation || []).length"
          :leave-conflicts="effectiveLeaveConflicts"
          :frequent-regularization="alerts.frequent_regularization || frequentRegularizationCases"
          :upcoming-leaves-next7="upcomingLeavesNext7Days"
          :birthdays-this-week="birthdaysThisWeek"
          :announcement-count="activeAnnouncementTeaserCount"
          :team-members="teamMembers"
          :get-user-name="getUserName"
          :format-date="formatDate"
          :punch-filter-active="!!(teamPunchRosterFilter && teamPunchRosterFilter.active)"
          :download-loading="punchDownloadLoading"
          :present="filteredTeamTodayAttendanceRoster.present"
          :absent="filteredTeamTodayAttendanceRoster.absent"
          :wfh="filteredTeamTodayAttendanceRoster.wfh"
          :is-non-working-day="filteredTeamTodayAttendanceRoster.isNonWorkingDay"
          :non-working-label="filteredTeamTodayAttendanceRoster.nonWorkingLabel"
          :filter-active="!!(teamPunchRosterFilter && teamPunchRosterFilter.active)"
          :leave-balance-items="teamLeaveBalanceTable"
          :round-to-half="roundToHalf"
          :on-leave-today-count="(teamTodayAttendanceRoster && teamTodayAttendanceRoster.onLeaveCount) || 0"
          :on-leave-today-ids="(teamTodayAttendanceRoster && teamTodayAttendanceRoster.onLeaveEmpIds) || []"
          @pending-leaves-click="handleApprovalPendingClick"
          @leave-click="openLeaveDetailModal"
          @announcements-click="scrollToLeaderAnnouncements"
          @apply-punch-filter="applyTeamPunchRosterFilter"
          @clear-punch-filter="clearTeamPunchRosterFilter"
          @download-punch-data="downloadTeamSnapshotPunchData"
          @download-leave-balance="downloadTeamLeaveBalance"
        />

        <!-- Announcements (full-width below mid-row — managers read; HR creates in Org View) -->
        <b-row
          id="leader-announcements"
          class="mb-0 team-announcements-row align-items-stretch"
        >
          <b-col cols="12" class="mb-0 team-main-col team-announcements-col">
            <announcements-card
              dense
              class="team-announcements-panel"
              :announcements="displayedAnnouncementsWithUniqueKeys"
              :total-count="totalAnnouncementsCount"
              :can-create="false"
              :can-download="canDownloadAnnouncements"
              :can-cancel="() => false"
              :format-date="formatDate"
              :format-date-month-only="formatDateMonthOnly"
              :download-duration-label="celebrationDownloadDurationLabel"
              @download="openCelebrationDownloadModal"
            />
          </b-col>
        </b-row>

        <!-- Conflicts / probation tables — hide when empty (alerts already summarized above) -->
        <b-row
          v-if="hasTeamConflictsOrProbationAlerts"
          class="mb-0 team-alerts-row"
        >
          <b-col cols="12" class="mb-0 team-alerts-col">
            <alerts-notifications-panel
              hide-chart
              hide-leave-balance
              compact
              :leave-conflicts="effectiveLeaveConflicts"
              :probation-ending="effectiveProbationEnding"
              :team-leave-balance="teamLeaveBalanceTable"
              :team-leave-balance-fields="teamLeaveBalanceFields"
              :leave-conflicts-fields="leaveConflictsFields"
              :probation-ending-fields="probationEndingFields"
              :leave-balance-chart-options="leaveBalanceChartOptions"
              :leave-balance-chart-series="leaveBalanceChartSeries"
              :loading="loading"
              :get-user-name="getUserName"
              :format-date="formatDate"
              :round-to-half="roundToHalf"
              :team-members="teamMembers"
              :view-mode="viewMode"
              @download-leave-conflicts="downloadLeaveConflicts"
              @download-probation-ending="downloadProbationEnding"
              @download-team-leave-balance="downloadTeamLeaveBalance"
            />
          </b-col>
        </b-row>

        <!-- Leaves & Regularization — mobile order places this before Leave Balance -->
        <b-row class="mb-0 team-leaves-reg-row">
          <b-col cols="12">
            <leaves-regularization-tabs
              id="leaves-regularization"
              :active-tab.sync="leavesRegularizationActiveTab"
              :leaves-data="leavesData"
              :regularization-data="regularizationData"
              :displayed-leaves-data="displayedLeavesData"
              :displayed-regularization-data="displayedRegularizationData"
              :displayed-leaves-count="displayedLeavesCount"
              :displayed-regularization-count="displayedRegularizationCount"
              :display-limit="regularizationDisplayLimit"
              :fields="regularizationFields"
              :leaves-tab-fields="leavesTabFields"
              :view-mode="viewMode"
              :show-download-button="canViewTeamDashboard && unifiedRegularizationData.length > 0"
              :show-leaves-download-button="canViewTeamDashboard && leavesData.length > 0"
              :has-active-filters="hasActiveRegularizationFilters"
              :status-filter="teamLeaveFilter"
              :can-manage-leaves="canManageLeaves"
              :can-approve-leave-all-stages="canApproveLeaveAllStages"
              :can-approve-leave="canApproveLeave"
              :can-approve-regularization-all-stages="canApproveRegularizationAllStages"
              :can-apply-regularization="canApplyRegularization"
              :get-user-name="getUserName"
              :format-date="formatDate"
              :format-time="formatTime"
              :get-approval-status-variant="getApprovalStatusVariant"
              :format-approval-status="formatApprovalStatus"
              :get-issue-variant="getIssueVariant"
              :is-manager-of-employee="isManagerOfEmployee"
              :needs-leave-approval="needsLeaveApproval"
              :is-leave-pending-manager-approval="isLeavePendingManagerApproval"
              :can-cancel-leave="canCancelLeave"
              :is-regularization-raised="isRegularizationRaised"
              :needs-manager-approval="needsManagerApproval"
              :get-employee-leave-balance="getEmployeeLeaveBalance"
              @download-excel="downloadRegularizationExcel"
              @download-leaves-excel="downloadLeavesExcel"
              @clear-filters="clearRegularizationFilters"
              @open-filter-modal="openRegularizationFilterModal"
              @leave-click="openLeaveDetailModal"
              @approve-leave="approveLeave"
              @reject-leave="rejectLeave"
              @cancel-leave="cancelLeave"
              @approve-regularization="openApproveRegularizationModal"
              @reject-regularization="openRejectRegularizationModal"
              @open-regularization-modal="openRegularizationModal"
              @load-more-leaves="loadMoreLeaves"
              @reset-leaves-display="resetLeavesDisplay"
              @load-more-regularizations="loadMoreRegularizations"
              @reset-regularization-display="resetRegularizationDisplay"
            />
          </b-col>
        </b-row>
      </div>

      <!-- Filtered team-member view: leave balance + announcements (no Holidays & WFH). -->
      <template v-else>
        <b-row class="mb-0 team-main-row align-items-stretch">
          <b-col xl="8" lg="7" md="12" class="mb-0 team-main-col team-leave-balance-col">
            <team-leave-balance-panel
              :items="teamLeaveBalanceTable"
              :get-user-name="getUserName"
              :round-to-half="roundToHalf"
              :on-leave-today-count="(teamTodayAttendanceRoster && teamTodayAttendanceRoster.onLeaveCount) || 0"
              :on-leave-today-ids="(teamTodayAttendanceRoster && teamTodayAttendanceRoster.onLeaveEmpIds) || []"
              @download="downloadTeamLeaveBalance"
            />
          </b-col>
          <b-col xl="4" lg="5" md="12" class="mb-0 pl-lg-1 team-main-col team-sidebar-col">
            <announcements-card
              dense
              class="team-announcements-panel"
              :announcements="displayedAnnouncementsWithUniqueKeys"
              :total-count="totalAnnouncementsCount"
              :can-create="false"
              :can-download="canDownloadAnnouncements"
              :can-cancel="() => false"
              :format-date="formatDate"
              :format-date-month-only="formatDateMonthOnly"
              :download-duration-label="celebrationDownloadDurationLabel"
              @download="openCelebrationDownloadModal"
            />
          </b-col>
        </b-row>

        <b-row class="mb-0 team-leaves-reg-row">
          <b-col cols="12">
            <leaves-regularization-tabs
              id="leaves-regularization"
              :active-tab.sync="leavesRegularizationActiveTab"
              :leaves-data="leavesData"
              :regularization-data="regularizationData"
              :displayed-leaves-data="displayedLeavesData"
              :displayed-regularization-data="displayedRegularizationData"
              :displayed-leaves-count="displayedLeavesCount"
              :displayed-regularization-count="displayedRegularizationCount"
              :display-limit="regularizationDisplayLimit"
              :fields="regularizationFields"
              :leaves-tab-fields="leavesTabFields"
              :view-mode="viewMode"
              :show-download-button="canViewTeamDashboard && unifiedRegularizationData.length > 0"
              :show-leaves-download-button="canViewTeamDashboard && leavesData.length > 0"
              :has-active-filters="hasActiveRegularizationFilters"
              :status-filter="teamLeaveFilter"
              :can-manage-leaves="canManageLeaves"
              :can-approve-leave-all-stages="canApproveLeaveAllStages"
              :can-approve-leave="canApproveLeave"
              :can-approve-regularization-all-stages="canApproveRegularizationAllStages"
              :can-apply-regularization="canApplyRegularization"
              :get-user-name="getUserName"
              :format-date="formatDate"
              :format-time="formatTime"
              :get-approval-status-variant="getApprovalStatusVariant"
              :format-approval-status="formatApprovalStatus"
              :get-issue-variant="getIssueVariant"
              :is-manager-of-employee="isManagerOfEmployee"
              :needs-leave-approval="needsLeaveApproval"
              :is-leave-pending-manager-approval="isLeavePendingManagerApproval"
              :can-cancel-leave="canCancelLeave"
              :is-regularization-raised="isRegularizationRaised"
              :needs-manager-approval="needsManagerApproval"
              :get-employee-leave-balance="getEmployeeLeaveBalance"
              @download-excel="downloadRegularizationExcel"
              @download-leaves-excel="downloadLeavesExcel"
              @clear-filters="clearRegularizationFilters"
              @open-filter-modal="openRegularizationFilterModal"
              @leave-click="openLeaveDetailModal"
              @approve-leave="approveLeave"
              @reject-leave="rejectLeave"
              @cancel-leave="cancelLeave"
              @approve-regularization="openApproveRegularizationModal"
              @reject-regularization="openRejectRegularizationModal"
              @open-regularization-modal="openRegularizationModal"
              @load-more-leaves="loadMoreLeaves"
              @reset-leaves-display="resetLeavesDisplay"
              @load-more-regularizations="loadMoreRegularizations"
              @reset-regularization-display="resetRegularizationDisplay"
            />
          </b-col>
        </b-row>
      </template>
    </template>

    <!-- ========== ORG VIEW ========== -->
    <template v-else>
      <!-- Top: Org KPI strip + Quick Actions (parity with Team View) -->
      <b-row class="mb-0 team-top-row align-items-stretch">
        <b-col lg="8" md="12" class="mb-0 team-kpi-col">
          <attendance-stats
            :view-mode="viewMode"
            :can-apply-on-behalf-all="canApplyOnBehalfAll"
            :can-apply-on-behalf="canApplyOnBehalf"
            :selected-account-manager="selectedAccountManager"
            :selected-team-member="selectedTeamMember"
            :selected-team-wise-employee="selectedTeamWiseEmployee"
            :can-manage-leaves="canManageLeaves"
            :team-summary="teamSummary"
            :displayed-team-members-count="displayedTeamMembersCount"
            :pending-h-r-approvals-length="pendingHRApprovals.length"
            :display-k-p-i-stats="displayKPIStats"
            :upcoming-team-leaves-length="upcomingTeamLeaves.length"
            :out-today-count="teamOutTodayCount"
            :present-today-count="teamPresentTodayCount"
            :absent-today-count="teamAbsentTodayCount"
            :wfh-today-count="teamWfhTodayCount"
            :regularization-to-approve-count="teamRegularizationToApproveCount"
            :leave-approvals-to-approve-count="(pendingLeaveApprovalsForQueue || []).length"
            :team-regularization-count="teamRegularizationCount"
            :kpi-stats="kpiStats"
            :leave-breakdown-html="leaveBreakdownHtml"
            @approval-pending-click="handleApprovalPendingClick"
            @reg-to-approve-click="handleRegToApproveClick"
          />
        </b-col>
        <b-col lg="4" md="12" class="mb-0 pl-lg-1 team-actions-col">
          <quick-actions-card
            inline
            @apply-leave="openApplyLeaveModal"
            @apply-wfh="openWFHModal"
            @request-comp-off="openRequestCompOffBalanceModal"
            @apply-comp-off="openApplyCompOffModal"
          />
        </b-col>
      </b-row>

      <!-- Mid-row + Leaves stack: ≤991 orders Leaves before Leave Balance -->
      <div class="team-mid-leaves-stack">
        <!-- Shared mid-row (identical DOM to Team): Snapshot | Today's attendance | Leave Balance -->
        <team-mid-row
          v-if="isOrgLeaderAggregateView"
          panel-title="Org snapshot"
          :pending-leave-count="(pendingLeaveApprovalsForQueue || []).length"
          :probation-ending="effectivePeopleOnProbation"
          :probation-source-missing="probationSourceMissing && !(effectivePeopleOnProbation || []).length"
          :leave-conflicts="effectiveLeaveConflicts"
          :frequent-regularization="alerts.frequent_regularization || frequentRegularizationCases"
          :upcoming-leaves-next7="upcomingLeavesNext7Days"
          :birthdays-this-week="birthdaysThisWeek"
          :announcement-count="activeAnnouncementTeaserCount"
          :team-members="teamMembers"
          :get-user-name="getUserName"
          :format-date="formatDate"
          :punch-filter-active="!!(teamPunchRosterFilter && teamPunchRosterFilter.active)"
          :download-loading="punchDownloadLoading"
          :present="filteredTeamTodayAttendanceRoster.present"
          :absent="filteredTeamTodayAttendanceRoster.absent"
          :wfh="filteredTeamTodayAttendanceRoster.wfh"
          :is-non-working-day="filteredTeamTodayAttendanceRoster.isNonWorkingDay"
          :non-working-label="filteredTeamTodayAttendanceRoster.nonWorkingLabel"
          :filter-active="!!(teamPunchRosterFilter && teamPunchRosterFilter.active)"
          :leave-balance-items="teamLeaveBalanceTable"
          :round-to-half="roundToHalf"
          :on-leave-today-count="(teamTodayAttendanceRoster && teamTodayAttendanceRoster.onLeaveCount) || 0"
          :on-leave-today-ids="(teamTodayAttendanceRoster && teamTodayAttendanceRoster.onLeaveEmpIds) || []"
          @pending-leaves-click="handleApprovalPendingClick"
          @leave-click="openLeaveDetailModal"
          @announcements-click="scrollToLeaderAnnouncements"
          @apply-punch-filter="applyTeamPunchRosterFilter"
          @clear-punch-filter="clearTeamPunchRosterFilter"
          @download-punch-data="downloadTeamSnapshotPunchData"
          @download-leave-balance="downloadTeamLeaveBalance"
        />

        <!-- Org Announcements live in the HR side panel (9+3) — not a full-width row -->

        <!-- Conflicts / probation (non-aggregate Org) — Alerts live in Org snapshot when aggregate -->
        <b-row v-if="!isOrgLeaderAggregateView" class="mb-0 team-alerts-row">
          <b-col cols="12" class="mb-0">
            <alerts-notifications-panel
              :leave-conflicts="effectiveLeaveConflicts"
              :probation-ending="effectiveProbationEnding"
              :team-leave-balance="teamLeaveBalanceTable"
              :team-leave-balance-fields="teamLeaveBalanceFields"
              :leave-conflicts-fields="leaveConflictsFields"
              :probation-ending-fields="probationEndingFields"
              :leave-balance-chart-options="leaveBalanceChartOptions"
              :leave-balance-chart-series="leaveBalanceChartSeries"
              :loading="loading"
              :get-user-name="getUserName"
              :format-date="formatDate"
              :round-to-half="roundToHalf"
              :team-members="teamMembers"
              :view-mode="viewMode"
              @download-leave-conflicts="downloadLeaveConflicts"
              @download-probation-ending="downloadProbationEnding"
              @download-team-leave-balance="downloadTeamLeaveBalance"
            />
          </b-col>
        </b-row>

        <!-- Leaves & Regularization (9) + HR side panel (3) — Org only -->
        <b-row class="mb-0 org-leaves-schedule-row team-leaves-reg-row align-items-stretch">
          <b-col xl="9" lg="9" md="12" class="mb-0 org-leaves-col">
            <leaves-regularization-tabs
              id="leaves-regularization"
              :active-tab.sync="leavesRegularizationActiveTab"
              :leaves-data="leavesData"
              :regularization-data="regularizationData"
              :displayed-leaves-data="displayedLeavesData"
              :displayed-regularization-data="displayedRegularizationData"
              :displayed-leaves-count="displayedLeavesCount"
              :displayed-regularization-count="displayedRegularizationCount"
              :display-limit="regularizationDisplayLimit"
              :fields="regularizationFields"
              :leaves-tab-fields="leavesTabFields"
              :view-mode="viewMode"
              :show-download-button="canViewTeamDashboard && unifiedRegularizationData.length > 0"
              :show-leaves-download-button="canViewTeamDashboard && leavesData.length > 0"
              :has-active-filters="hasActiveRegularizationFilters"
              :status-filter="teamLeaveFilter"
              :can-manage-leaves="canManageLeaves"
              :can-approve-leave-all-stages="canApproveLeaveAllStages"
              :can-approve-leave="canApproveLeave"
              :can-approve-regularization-all-stages="canApproveRegularizationAllStages"
              :can-apply-regularization="canApplyRegularization"
              :get-user-name="getUserName"
              :format-date="formatDate"
              :format-time="formatTime"
              :get-approval-status-variant="getApprovalStatusVariant"
              :format-approval-status="formatApprovalStatus"
              :get-issue-variant="getIssueVariant"
              :is-manager-of-employee="isManagerOfEmployee"
              :needs-leave-approval="needsLeaveApproval"
              :is-leave-pending-manager-approval="isLeavePendingManagerApproval"
              :can-cancel-leave="canCancelLeave"
              :is-regularization-raised="isRegularizationRaised"
              :needs-manager-approval="needsManagerApproval"
              :get-employee-leave-balance="getEmployeeLeaveBalance"
              @download-excel="downloadRegularizationExcel"
              @download-leaves-excel="downloadLeavesExcel"
              @clear-filters="clearRegularizationFilters"
              @open-filter-modal="openRegularizationFilterModal"
              @leave-click="openLeaveDetailModal"
              @approve-leave="approveLeave"
              @reject-leave="rejectLeave"
              @cancel-leave="cancelLeave"
              @approve-regularization="openApproveRegularizationModal"
              @reject-regularization="openRejectRegularizationModal"
              @open-regularization-modal="openRegularizationModal"
              @load-more-leaves="loadMoreLeaves"
              @reset-leaves-display="resetLeavesDisplay"
              @load-more-regularizations="loadMoreRegularizations"
              @reset-regularization-display="resetRegularizationDisplay"
            />
          </b-col>
          <b-col xl="3" lg="3" md="12" class="mb-0 pl-lg-1 org-schedule-col">
            <company-schedule-panel
              ref="companySchedulePanel"
              :announcements="displayedAnnouncementsWithUniqueKeys"
              :leaves="upcomingLeaves"
              :holidays="upcomingHolidays"
              :company-wfh-days="upcomingCompanyWfhDays"
              :leaves-loading="loadingLeaves"
              :can-create-announcement="canManageAnnouncements"
              :can-download-announcements="canDownloadAnnouncements"
              :can-cancel-announcement="canCancelAnnouncement"
              :can-create-holiday="canManageHolidays"
              :can-create-company-wfh="canManageCompanyWfhDays"
              :format-date="formatDate"
              :format-date-month-only="formatDateMonthOnly"
              :format-leave-type="formatLeaveType"
              :format-approval-status="formatApprovalStatus"
              :get-user-name="getUserName"
              @create-announcement="openCreateAnnouncementModal"
              @cancel-announcement="cancelAnnouncement"
              @download-announcements="openCelebrationDownloadModal"
              @create-holiday="openCreateHolidayModal"
              @create-company-wfh-day="openCreateCompanyWfhDayModal"
              @leave-click="openLeaveDetailModal"
            />
          </b-col>
        </b-row>
      </div>
    </template>

    <!-- Create Holiday Modal -->
    <create-holiday-modal
      :holiday-form="holidayForm"
      :creating-holiday="creatingHoliday"
      :location-options="locationOptions"
      @ok="handleCreateHoliday"
      @hidden="resetHolidayForm"
    />

    <!-- Create Company-Wide WFH Day Modal -->
    <create-company-wfh-day-modal
      :company-wfh-day-form="companyWfhDayForm"
      :creating-company-wfh-day="creatingCompanyWfhDay"
      :location-options="locationOptions"
      @ok="handleCreateCompanyWfhDay"
      @hidden="resetCompanyWfhDayForm"
    />

    <!-- Create Announcement Modal -->
    <create-announcement-modal
      :visible.sync="showCreateAnnouncementModal"
      :announcement-form="announcementForm"
      :creating-announcement="creatingAnnouncement"
      @ok="handleCreateAnnouncement"
      @hidden="resetAnnouncementForm"
    />

    <!-- Apply Leave Modal -->
    <leave-application-modal
      ref="leaveApplicationModal"
      :leave-form="leaveForm"
      :view-mode="viewMode"
      :can-manage-leaves="canManageLeaves"
      :can-apply-on-behalf="canApplyOnBehalf"
      :display-k-p-i-stats="modalLeaveStats"
      :available-leave-breakdown="availableLeaveBreakdown"
      :get-employee-options-for-leave="getEmployeeOptionsForLeave"
      :is-on-probation="isOnProbation"
      :manager-absent="managerAbsent"
      :filtered-leave-type-options="filteredLeaveTypeOptions"
      :selected-leave-type-supports-half-day="selectedLeaveTypeSupportsHalfDay"
      :display-calendar-month-year="displayCalendarMonthYear"
      :week-day-labels="weekDays"
      :leave-calendar-days="leaveCalendarDays"
      :calculated-leave-days="getLeaveDaysCount()"
      :format-date-for-display="formatDateForDisplay"
      :get-calendar-day-classes="getCalendarDayClasses"
      @hidden="resetLeaveForm"
      @update:leave-form="leaveForm = $event"
      @handle-leave-employee-change="handleLeaveEmployeeChange"
      @handle-leave-type-change="handleLeaveTypeChange"
      @handle-leave-duration-change="handleLeaveDurationChange"
      @save-as-draft="saveAsDraft"
      @handle-apply-leave="handleApplyLeave"
      @navigate-calendar-month="navigateCalendarMonth"
      @select-calendar-date="selectCalendarDate"
      @clear-date-selection="clearDateSelection"
    />

    <!-- Apply WFH Modal -->
    <WFHModal
      ref="wfhModal"
      :wfh-form="wfhForm"
      :view-mode="viewMode"
      :can-manage-leaves="canManageLeaves"
      :can-apply-on-behalf="canApplyOnBehalf"
      :employee-options="getEmployeeOptionsForWFH"
      :is-on-probation="isOnProbation"
      :manager-absent="managerAbsent"
      :is-permanent-wfh="isPermanentWFH"
      :selected-employee-is-permanent-wfh="selectedEmployeeIsPermanentWFH"
      :selected-employee-wfh-count="selectedEmployeeWFHCount"
      :current-quarter-wfh-count="currentQuarterWFHCount"
      :display-calendar-month-year="displayWFHCalendarMonthYear"
      :week-day-labels="weekDayLabels"
      :calendar-days="wfhCalendarDays"
      :get-user-name="getUserName"
      :format-date-for-display="formatDateForDisplay"
      :get-calendar-day-classes="getWFHCalendarDayClasses"
      @submit="handleApplyWFH"
      @reset="resetWFHForm"
      @employee-change="handleWFHEmployeeChange"
      @navigate-calendar="navigateWFHCalendarMonth"
      @select-date="selectWFHCalendarDate"
      @clear-date="clearWFHDateSelection"
      @update:comment="wfhForm.comment = $event"
      @update:partial-wfh="wfhForm.partial_wfh = $event"
      @update:is-half-day="wfhForm.is_half_day = $event"
      @update:half-day-type="wfhForm.half_day_type = $event"
    />

    <!-- Request Compensatory Off Balance Modal (Step 1: Request Balance Credit) -->
    <request-comp-off-balance-modal
      :comp-off-balance-request-form="compOffBalanceRequestForm"
      :show-work-calendar="showCompOffBalanceWorkCalendar"
      :view-mode="viewMode"
      :can-manage-leaves="canManageLeaves"
      :can-apply-on-behalf="canApplyOnBehalf"
      :employee-options="getEmployeeOptionsForCompOff"
      :calendar-month-year="displayCompOffBalanceWorkCalendarMonthYear"
      :calendar-days="compOffBalanceWorkCalendarDays"
      :week-day-labels="weekDayLabels"
      :get-day-classes="getCompOffBalanceWorkCalendarDayClasses"
      :format-date-for-display="formatDateForDisplay"
      @ok="handleRequestCompOffBalance"
      @hidden="resetCompOffBalanceRequestForm"
      @toggle-work-calendar="showCompOffBalanceWorkCalendar = !showCompOffBalanceWorkCalendar"
      @navigate-month="navigateCompOffBalanceWorkCalendarMonth"
      @select-date="selectCompOffBalanceWorkCalendarDate"
      @clear-selection="clearCompOffBalanceWorkDateSelection"
      @view-requests="showCompOffBalanceRequestsModal"
    />

    <!-- Apply Compensatory Off Leave Modal (Step 2: Use Existing Balance) -->
    <apply-comp-off-modal
      :comp-off-form="compOffForm"
      :displayed-co-balance="displayedCoBalance"
      :displayed-co-balance-employee-name="displayedCoBalanceEmployeeName"
      :is-on-probation="isOnProbation"
      :view-mode="viewMode"
      :can-manage-leaves="canManageLeaves"
      :can-apply-on-behalf="canApplyOnBehalf"
      :employee-options="getEmployeeOptionsForCompOff"
      :show-calendar="showCompOffDateCalendar"
      :calendar-month-year="displayCompOffCalendarMonthYear"
      :calendar-days="compOffCalendarDays"
      :week-day-labels="weekDayLabels"
      :get-day-classes="getCompOffCalendarDayClasses"
      :format-date-for-display="formatDateForDisplay"
      @ok="handleApplyCompOff"
      @hidden="resetCompOffForm"
      @employee-change="handleCompOffEmployeeChange"
      @toggle-calendar="showCompOffDateCalendar = !showCompOffDateCalendar"
      @navigate-month="navigateCompOffCalendarMonth"
      @select-date="selectCompOffCalendarDate"
      @clear-selection="clearCompOffDateSelection"
    />

    <!-- View CO Balance Requests Modal -->
    <view-comp-off-requests-modal
      :loading-comp-off-requests="loadingCompOffRequests"
      :comp-off-balance-requests="compOffBalanceRequests"
      :comp-off-requests-fields="compOffRequestsFields"
      :format-date="formatDate"
      :get-status-variant="getStatusVariant"
      @create-request="$bvModal.hide('modal-view-compoff-requests'); openRequestCompOffBalanceModal()"
      @view-detail="viewCompOffRequestDetail"
    />

    <!-- CO Balance Request Detail Modal -->
    <!-- CO Balance Request Detail Modal -->
    <c-o-request-detail-modal
      :selected-comp-off-request="selectedCompOffRequest"
      :co-balance="coBalance"
      :current-user-department="currentUserDepartment"
      :format-date="formatDate"
      :get-status-variant="getStatusVariant"
      :get-user-name="getUserName"
      :get-applicant-department="getApplicantDepartment"
      :parse-co-request-reason="parseCoRequestReason"
      @hidden="selectedCompOffRequest = null"
    />

    <!-- Regularization Modal -->
    <regularization-modal
      ref="regularizationModal"
      :regularization-form="regularizationForm"
      :selected-regularization-item="selectedRegularizationItem"
      :leave-type-options="leaveTypeOptions"
      :show-in-time-field="showInTimeField"
      :show-out-time-field="showOutTimeField"
      :format-date="formatDate"
      :get-biometric-intime="getBiometricIntime"
      :get-biometric-outtime="getBiometricOuttime"
      :get-biometric-worktime="getBiometricWorktime"
      @submit="handleRegularize"
      @reset="resetRegularizationForm"
    />

    <!-- Celebration Download Date-Range Modal -->
    <celebration-download-modal
      :visible="showCelebrationDownloadModal"
      :celebration-download-from="celebrationDownloadFrom"
      :celebration-download-to="celebrationDownloadTo"
      :celebration-active-preset="celebrationActivePreset"
      :celebration-presets="celebrationPresets"
      :celebration-range-summary="celebrationRangeSummary"
      @ok="downloadBirthdaysAnniversaries"
      @update:visible="showCelebrationDownloadModal = $event"
      @set-range="setCelebrationRange"
      @update:celebrationDownloadFrom="celebrationDownloadFrom = $event"
      @update:celebrationDownloadTo="celebrationDownloadTo = $event"
      @clear-preset="celebrationActivePreset = null"
    />

    <!-- Punch In/Out Data Download Modal -->
    <!-- Punch In/Out Data Download Modal -->
    <punch-data-download-modal
      :visible="showPunchDataDownloadModal"
      :punch-download-from="punchDownloadFrom"
      :punch-download-to="punchDownloadTo"
      :punch-download-active-preset="punchDownloadActivePreset"
      :punch-download-presets="punchDownloadPresets"
      :punch-download-range-summary="punchDownloadRangeSummary"
      @ok="downloadPunchData"
      @update:visible="showPunchDataDownloadModal = $event"
      @set-range="setPunchDownloadRange"
      @update:punchDownloadFrom="punchDownloadFrom = $event"
      @update:punchDownloadTo="punchDownloadTo = $event"
      @clear-preset="punchDownloadActivePreset = null"
    />

    <!-- Filter Modal -->
    <!-- Filter Modal -->
    <filters-modal
      :visible="showFilterModal"
      :can-view-team="canViewTeam"
      :view-mode-options="viewModeOptions"
      :view-mode-temp="viewModeTemp"
      :from-date-filter-temp="fromDateFilterTemp"
      :to-date-filter-temp="toDateFilterTemp"
      :can-apply-on-behalf-all="canApplyOnBehalfAll"
      :selected-account-manager-temp="selectedAccountManagerTemp"
      :account-manager-options="accountManagerOptions"
      :selected-team-wise-employee-temp="selectedTeamWiseEmployeeTemp"
      :team-wise-employee-options="teamWiseEmployeeOptions"
      :is-account-manager="isAccountManager"
      :is-admin="isAdmin"
      :is-master="isMaster"
      :selected-team-member-temp="selectedTeamMemberTemp"
      :team-member-options="teamMemberOptions"
      @ok="handleFilterApply"
      @update:visible="showFilterModal = $event"
      @update:viewModeTemp="viewModeTemp = $event"
      @update:fromDateFilterTemp="fromDateFilterTemp = $event"
      @update:toDateFilterTemp="toDateFilterTemp = $event"
      @update:selectedAccountManagerTemp="selectedAccountManagerTemp = $event"
      @update:selectedTeamWiseEmployeeTemp="selectedTeamWiseEmployeeTemp = $event"
      @update:selectedTeamMemberTemp="selectedTeamMemberTemp = $event"
    />
    
    <!-- Regularization Filter Modal -->
    <!-- Regularization Filter Modal -->
    <regularization-filters-modal
      :visible="showRegularizationFilterModal"
      :view-mode="viewMode"
      :regularization-employee-filter-temp="regularizationEmployeeFilterTemp"
      :regularization-filter-employee-options="regularizationFilterEmployeeOptions"
      :regularization-from-date-filter-temp="regularizationFromDateFilterTemp"
      :regularization-to-date-filter-temp="regularizationToDateFilterTemp"
      :regularization-leave-type-filter-temp="regularizationLeaveTypeFilterTemp"
      :leave-type-filter-options="leaveTypeFilterOptions"
      @ok="applyRegularizationFilters"
      @update:visible="showRegularizationFilterModal = $event"
      @update:regularizationEmployeeFilterTemp="regularizationEmployeeFilterTemp = $event"
      @update:regularizationFromDateFilterTemp="regularizationFromDateFilterTemp = $event"
      @update:regularizationToDateFilterTemp="regularizationToDateFilterTemp = $event"
      @update:regularizationLeaveTypeFilterTemp="regularizationLeaveTypeFilterTemp = $event"
    />
    
    <!-- Date Range Filter Modal (kept for backward compatibility) -->
    <b-modal
      id="modal-date-range-filter"
      ref="modal-date-range-filter"
      v-model="showDateRangePicker"
      title="Select Date Range"
      @ok="handleDateRangeFilterApply"
      @hidden="showDateRangePicker = false"
      size="md"
      style="display: none;"
    >
    </b-modal>

    <!-- Reject Regularization Modal -->
    <reject-regularization-modal
      ref="modal-reject-regularization"
      :regularization="selectedRegularizationForRejection"
      :employee-name="selectedRegularizationForRejection ? getUserName(selectedRegularizationForRejection.emp_id) : ''"
      :reason.sync="regularizationRejectionReason"
      @confirm="rejectRegularization"
      @hidden="selectedRegularizationForRejection = null; regularizationRejectionReason = ''"
    />

    <!-- Approve Regularization Modal -->
    <approve-regularization-modal
      ref="modal-approve-regularization"
      :regularization="selectedRegularizationForApproval"
      :employee-name="selectedRegularizationForApproval ? getUserName(selectedRegularizationForApproval.emp_id) : ''"
      :comment.sync="regularizationApprovalComment"
      @confirm="handleApproveRegularizationConfirm"
      @hidden="selectedRegularizationForApproval = null; regularizationApprovalComment = ''"
    />

    <!-- Approve Leave Modal -->
    <approve-leave-modal
      ref="modal-approve-leave"
      :leave="selectedLeaveForApproval"
      :balance="approveLeaveEmployeeBalance"
      :employee-name="selectedLeaveForApproval ? getUserName(selectedLeaveForApproval.emp_id) : ''"
      :show-paid-option="selectedLeaveForApproval ? (isLeavePendingHRApproval(selectedLeaveForApproval) && isApplicantOnProbation(selectedLeaveForApproval)) : false"
      @confirm="onApproveLeaveConfirm"
      @hidden="selectedLeaveForApproval = null; approvalComment = ''; approvalStatusToSet = 'Approved'; approvalIsCompensatory = false; approvalIsPaid = true"
    />

    <!-- Reject Leave Modal -->
    <reject-leave-modal
      ref="modal-reject-leave"
      :leave="selectedLeaveForRejection"
      :employee-name="selectedLeaveForRejection ? getUserName(selectedLeaveForRejection.emp_id) : ''"
      @confirm="onRejectLeaveConfirm"
      @hidden="selectedLeaveForRejection = null; rejectionComment = ''"
    />

    <!-- Leave Detail Modal -->
    <leave-detail-modal
      ref="modal-leave-detail"
      :selected-leave-for-detail="selectedLeaveForDetail"
      :leave-detail-comment="leaveDetailComment"
      :leave-detail-is-paid="leaveDetailIsPaid"
      :view-mode="viewMode"
      :can-manage-leaves="canManageLeaves"
      :selected-employee-leave-balance="selectedEmployeeLeaveBalance"
      :co-balance="coBalance"
      :current-user-department="currentUserDepartment"
      :can-approve-leave-manager-stage="canApproveLeaveManagerStage"
      :can-approve-leave-h-r-stage="canApproveLeaveHRStage"
      :can-approve-leave-all-stages="canApproveLeaveAllStages"
      :format-leave-type="formatLeaveType"
      :format-approval-status="formatApprovalStatus"
      :get-approval-status-variant="getApprovalStatusVariant"
      :format-date="formatDate"
      :get-user-name="getUserName"
      :get-applicant-department="getApplicantDepartment"
      :parse-co-request-reason="parseCoRequestReason"
      :needs-leave-approval="needsLeaveApproval"
      :can-cancel-leave="canCancelLeave"
      :is-manager-of-employee="isManagerOfEmployee"
      :is-leave-pending-manager-approval="isLeavePendingManagerApproval"
      :is-leave-pending-h-r-approval="isLeavePendingHRApproval"
      :is-applicant-on-probation="isApplicantOnProbation"
      @hidden="selectedLeaveForDetail = null; leaveDetailComment = ''; leaveDetailIsPaid = true"
      @edit="editLeaveFromDetail"
      @cancel="cancelLeaveFromDetail"
      @cancel-leave="cancelLeaveFromDetail"
      @approve="approveLeaveFromDetail"
      @reject="rejectLeaveFromDetail"
      @update:leaveDetailComment="leaveDetailComment = $event"
      @update:leaveDetailIsPaid="leaveDetailIsPaid = $event"
    />

    <!-- Modal for Leaves Taken List -->
    <!-- Modal for Leaves Taken List -->
    <leaves-taken-list-modal
      :filtered-leaves-for-list="filteredLeavesForList"
      :leaves-taken-list-fields="leavesTakenListFields"
      :selected-leave-type-for-list="selectedLeaveTypeForList"
      :format-date="formatDate"
      :format-leave-type="formatLeaveType"
      :get-approval-status-variant="getApprovalStatusVariant"
      :format-approval-status="formatApprovalStatus"
    />
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BBadge,
  BModal,
  VBModal,
  BForm,
  BFormGroup,
  BFormSelect,
  BFormSelectOption,
  BFormTextarea,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BTable,
  BTabs,
  BTab,
  BSpinner,
  BAlert,
  BFormRadioGroup,
} from 'bootstrap-vue'
import moment from 'moment'
import attendanceService from '@/services/attendance-service'
import userService from '@/services/user-service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ATTENDANCE_PERMISSIONS } from '@/utils/attendance-permissions'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

import AttendanceStats from './components/AttendanceStats.vue'
import AttendanceCalendar from './components/AttendanceCalendar.vue'
import TeamMidRow from './components/TeamMidRow.vue'
import TeamLeaveBalancePanel from './components/TeamLeaveBalancePanel.vue'
import LeaveApplicationModal from './components/LeaveApplicationModal.vue'
import RegularizationModal from './components/RegularizationModal.vue'
import AlertsNotificationsPanel from './components/AlertsNotificationsPanel.vue'
import LeavesRegularizationTabs from './components/LeavesRegularizationTabs.vue'
import AnnouncementsCard from './components/AnnouncementsCard.vue'
import CompanySchedulePanel from './components/CompanySchedulePanel.vue'
import QuickActionsCard from './components/QuickActionsCard.vue'
import EmployeeUpcomingPanel from './components/EmployeeUpcomingPanel.vue'
import WFHModal from './components/WFHModal.vue'
import ApproveLeaveModal from './components/modals/ApproveLeaveModal.vue'
import RejectLeaveModal from './components/modals/RejectLeaveModal.vue'
import ApproveRegularizationModal from './components/modals/ApproveRegularizationModal.vue'
import RejectRegularizationModal from './components/modals/RejectRegularizationModal.vue'
import CreateHolidayModal from './components/modals/CreateHolidayModal.vue'
import CreateCompanyWfhDayModal from './components/modals/CreateCompanyWfhDayModal.vue'
import CreateAnnouncementModal from './components/modals/CreateAnnouncementModal.vue'
import RequestCompOffBalanceModal from './components/modals/RequestCompOffBalanceModal.vue'
import ApplyCompOffModal from './components/modals/ApplyCompOffModal.vue'
import ViewCompOffRequestsModal from './components/modals/ViewCompOffRequestsModal.vue'
import CORequestDetailModal from './components/modals/CORequestDetailModal.vue'
import CelebrationDownloadModal from './components/modals/CelebrationDownloadModal.vue'
import PunchDataDownloadModal from './components/modals/PunchDataDownloadModal.vue'
import FiltersModal from './components/modals/FiltersModal.vue'
import RegularizationFiltersModal from './components/modals/RegularizationFiltersModal.vue'
import LeaveDetailModal from './components/modals/LeaveDetailModal.vue'
import LeavesTakenListModal from './components/modals/LeavesTakenListModal.vue'

import { isPresentOrHalfDayLeaveType, isWFHLeaveRecord, isWFHLeaveType } from '@/utils/attendance-leave-types'
import leaveActionsMixin from './mixins/leaveActionsMixin'
import regularizationActionsMixin from './mixins/regularizationActionsMixin'
import teamDataMixin from './mixins/teamDataMixin'
import attendanceFiltersMixin from './mixins/attendanceFiltersMixin'
import computedMixin from './mixins/computedMixin'
import attendanceDataMixin from './mixins/attendanceDataMixin'

// Debounce utility function
function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const context = this // Preserve 'this' context
    const later = () => {
      clearTimeout(timeout)
      func.apply(context, args) // Use .apply to preserve context
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export default {
  name: 'AttendanceDashboard',
  mixins: [leaveActionsMixin, regularizationActionsMixin, teamDataMixin, attendanceFiltersMixin, computedMixin, attendanceDataMixin],
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BBadge,
    BModal,
    VBModal,
    BForm,
    BFormGroup,
    BFormSelect,
    BFormSelectOption,
    BFormTextarea,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BTable,
    BTabs,
    BTab,
    BSpinner,
    BAlert,
    BFormRadioGroup,
    AttendanceStats,
    AttendanceCalendar,
    TeamMidRow,
    TeamLeaveBalancePanel,
    LeaveApplicationModal,
    RegularizationModal,
    AlertsNotificationsPanel,
    LeavesRegularizationTabs,
    AnnouncementsCard,
    CompanySchedulePanel,
    QuickActionsCard,
    EmployeeUpcomingPanel,
    WFHModal,
    ApproveLeaveModal,
    RejectLeaveModal,
    ApproveRegularizationModal,
    RejectRegularizationModal,
    CreateHolidayModal,
    CreateCompanyWfhDayModal,
    CreateAnnouncementModal,
    RequestCompOffBalanceModal,
    ApplyCompOffModal,
    ViewCompOffRequestsModal,
    CORequestDetailModal,
    CelebrationDownloadModal,
    PunchDataDownloadModal,
    FiltersModal,
    RegularizationFiltersModal,
    LeaveDetailModal,
    LeavesTakenListModal,
  },
  directives: {
    'b-modal': VBModal,
  },
  computed: {
    // API birthdays/anniversaries often share emp_id with no id — ensure unique keys for v-for
    displayedAnnouncementsWithUniqueKeys() {
      return (this.displayedAnnouncements || []).map((item, index) => {
        if (item.id != null && item.id !== '') {
          return item
        }
        const type = item.type || 'announcement'
        const empId = item.emp_id || 'unknown'
        const date = item.date || item.birthday_date || item.anniversary_date || item.created_at || index
        return {
          ...item,
          id: `${type}-${empId}-${date}-${index}`,
        }
      })
    },
  },
  created() {
    // Create debounced handlers for watchers
    this.handleSelectedMonthChange = debounce(async function() {
      // Load calendar data for the new month
      if (this.viewMode === 'my' || (this.viewMode === 'team' && (this.selectedTeamMember || this.selectedTeamWiseEmployee))) {
        this.loadCalendarData(this.selectedMonth, this.selectedYear)
      }
      // Reload full data to update all sections
      await this.loadData()
      // Always explicitly re-fetch regularization cases for the newly selected month/year.
      // loadData() only calls loadRegularizationCases as a fallback when the dashboard API
      // returns empty data, so we force a refresh here to ensure the Regularization tab
      // shows data for the selected month regardless of what the dashboard API returned.
      const regEmpId = (this.viewMode === 'team' || this.viewMode === 'org') && !this.selectedTeamMember && !this.selectedTeamWiseEmployee
        ? null
        : (this.selectedTeamMember || this.selectedTeamWiseEmployee || this.emp_id)
      await this.loadRegularizationCases(regEmpId)
    }.bind(this), 300)
    
    this.handleSelectedYearChange = debounce(async function() {
      // Load calendar data for the new year
      if (this.viewMode === 'my' || (this.viewMode === 'team' && (this.selectedTeamMember || this.selectedTeamWiseEmployee))) {
        this.loadCalendarData(this.selectedMonth, this.selectedYear)
      }
      // Reload full data to update all sections
      await this.loadData()
      // Always explicitly re-fetch regularization cases for the newly selected year.
      const regEmpId = (this.viewMode === 'team' || this.viewMode === 'org') && !this.selectedTeamMember && !this.selectedTeamWiseEmployee
        ? null
        : (this.selectedTeamMember || this.selectedTeamWiseEmployee || this.emp_id)
      await this.loadRegularizationCases(regEmpId)
    }.bind(this), 300)
  },
  watch: {
    selectedMonth: {
      handler() {
        this.handleSelectedMonthChange()
      },
      immediate: false
    },
    selectedYear: {
      handler() {
        this.handleSelectedYearChange()
      },
      immediate: false
    },
    displayKPIStats: {
      handler(newVal, oldVal) {
        if (newVal.leaveBalance !== oldVal.leaveBalance) {
          this.updateLeaveBalanceChart()
        }
      },
    },
    selectedAccountManagerTemp: {
      handler(newValue) {
        // When Account Manager is selected in the filter modal, load employees
        if (newValue && (this.viewModeTemp === 'org' || this.viewModeTemp === 'team')) {
          this.loadEmployeesForAccountManager(newValue)
        } else if (!newValue) {
          // Clear employees when Account Manager is cleared
          this.teamWiseEmployeesForModal = []
          this.selectedTeamWiseEmployeeTemp = null
        }
      },
      immediate: false
    },
  },
  mounted() {
    // Initialize date format cache
    if (!this._dateFormatCache) {
      this._dateFormatCache = new Map()
    }
    this.initializeUser()
    
    // Load leave types from database API
    this.loadLeaveTypesFromDB()

    // Load declared Company-Wide WFH Days — independent of viewMode/loadData
    // (no consolidated-dashboard field for this yet, backend gap), so it's
    // loaded once here the same way Holidays/Announcements would be if they
    // weren't already threaded through the consolidated dashboard payload.
    this.loadCompanyWfhDays()
    
    // Always load 'my' view (individual dashboard) by default for all roles
    // Use $nextTick to ensure computed properties are available
    this.$nextTick(() => {
      // Ensure viewMode is set to 'my' (individual dashboard) by default
      if (this.viewMode !== 'my') {
        this.viewMode = 'my'
      }
      // Load data for individual dashboard
        this.loadData()
    })
    
    this.checkManagerStatus()
  },
  methods: {
    // Bridge methods for extracted modal components
    onApproveLeaveConfirm({ comment, isPaid }) {
      this.approvalComment = comment
      this.approvalIsPaid = isPaid
      this.handleApproveLeaveConfirm()
    },
    onRejectLeaveConfirm({ comment }) {
      this.rejectionComment = comment
      this.handleRejectLeaveConfirm()
    },

    // ============================================
    // LEGACY METHODS - TO BE DEPRECATED
    // These methods will be removed once backend APIs provide pre-calculated data
    // The backend will handle all leave calculations, validations, and business logic
    // ============================================
    
    /**
     * @deprecated Backend will provide leave_balance_breakdown in dashboard API
     * Calculate leave breakdown from employee config stats and usage
     */
    calculateBreakdownFromStats(stats, usage) {
      const breakup = []
      
      // Ensure usage is always an object
      const usageObj = usage || {}
      
      // Ensure stats is always an object
      const statsObj = stats || {}
      
      // 1. Privilege + Carry Forward Deduction Logic
      const totalCFTotal = statsObj.carry_forward || 0
      const plAccrued = moment().month() + 1 // 1 per month
      const plTaken = usageObj['privilege_leave'] || 0

      // Priority: Deduct from CF first
      let remainingCF = Math.max(0, totalCFTotal - plTaken)
      let remainingPL = plAccrued
      if (plTaken > totalCFTotal) {
        remainingPL = Math.max(0, plAccrued - (plTaken - totalCFTotal))
      }

      if (totalCFTotal > 0 || plAccrued > 0) {
        breakup.push({ label: 'Carry Forward (Remaining)', value: remainingCF })
        breakup.push({ label: 'Privilege Leave (Accrued)', value: remainingPL })
      }

      // 2. Casual Leave
      if (statsObj.casual_leave) {
        const remaining = Math.max(0, statsObj.casual_leave - (usageObj['casual_leave'] || 0))
        breakup.push({ label: 'Casual Leave', value: remaining })
      }

      // 3. Sick / Medical
      const sickBase = (statsObj.sick_leave || 0) + (statsObj.medical_leave || 0)
      if (sickBase > 0) {
        const remaining = Math.max(0, sickBase - (usageObj['sick_leave'] || 0))
        breakup.push({ label: 'Sick / Medical Leave', value: remaining })
      }

      // 4. Compensatory
      if (statsObj.compensatory_balance) {
        const remaining = Math.max(0, statsObj.compensatory_balance - (usageObj['compensatory_balance'] || 0))
        breakup.push({ label: 'Compensatory Leave', value: remaining })
      }

      // 5. Paternity/Maternity
      if (statsObj.paternity_leave) {
        const remaining = Math.max(0, statsObj.paternity_leave - (usageObj['paternity_leave'] || 0))
        breakup.push({ label: 'Paternity Leave', value: remaining })
      }
      if (statsObj.maternity_leave) {
        const remaining = Math.max(0, statsObj.maternity_leave - (usageObj['maternity_leave'] || 0))
        breakup.push({ label: 'Maternity Leave', value: remaining })
      }
      
      return breakup
    },
    
    isWFHLeave(leave) {
      return isWFHLeaveRecord(leave)
    },
    isCORequestLeave(leave) {
      if (!leave) return false
      const leaveType = (leave.leave_type || leave.type || '').toUpperCase()
      if (leaveType === 'CO_REQUEST' || leaveType === 'CO REQUEST') return true
      if (leave.comment && String(leave.comment).includes('CO Balance Request')) return true
      return false
    },
    /** WFH and CO balance requests are not counted in leave KPIs (available / taken). */
    isExcludedFromLeaveKpi(leave) {
      return this.isWFHLeave(leave) || this.isCORequestLeave(leave)
    },
    isPendingLeaveRecord(leave) {
      if (!leave) return false
      const status = (leave.status || '').trim().toLowerCase()
      if (!status || status === 'approved' || status === 'cancelled' ||
          status === 'hr reject' || status.includes('reject') || status.includes('cancel')) {
        return false
      }
      return status.includes('pending') || status === 'pending'
    },
    getLeaveRecordDays(leave) {
      if (!leave) return 0
      const leaveType = leave.type || leave.leave_type || ''
      const leaveTypeLower = leaveType.toLowerCase()
      const isHalfDay = leaveType === '1st Half Day' ||
        leaveType === '2nd Half Day' ||
        leaveTypeLower.includes('half day') ||
        leave.half_day === true
      if (isHalfDay) {
        if (leave.from_date && leave.to_date) {
          const from = moment(leave.from_date)
          const to = moment(leave.to_date)
          return (to.diff(from, 'days') + 1) * 0.5
        }
        return 0.5
      }
      if (leave.total_days !== undefined && leave.total_days !== null) {
        return parseFloat(leave.total_days) || 0
      }
      if (leave.from_date && leave.to_date) {
        return moment(leave.to_date).diff(moment(leave.from_date), 'days') + 1
      }
      return 1
    },
    getPendingLeaveDays(empId) {
      const normalizedEmpId = empId ? String(empId) : String(this.emp_id || '')
      const allowMissingEmpId = !empId || normalizedEmpId === String(this.emp_id)
      const belongsToEmployee = (leave) => {
        if (!leave) return false
        if (!leave.emp_id) return allowMissingEmpId
        return String(leave.emp_id) === normalizedEmpId
      }
      return (this.myLeaves || [])
        .filter(leave => belongsToEmployee(leave) && this.isPendingLeaveRecord(leave) && !this.isExcludedFromLeaveKpi(leave))
        .reduce((sum, leave) => sum + this.getLeaveRecordDays(leave), 0)
    },
    getPendingLeavesForEmployee(empId) {
      const normalizedEmpId = empId ? String(empId) : String(this.emp_id || '')
      const allowMissingEmpId = !empId || normalizedEmpId === String(this.emp_id)
      return (this.myLeaves || []).filter(leave => {
        if (!leave) return false
        if (leave.emp_id && String(leave.emp_id) !== normalizedEmpId) return false
        if (leave.emp_id == null && !allowMissingEmpId) return false
        return this.isPendingLeaveRecord(leave) && !this.isExcludedFromLeaveKpi(leave)
      })
    },
    findBreakdownIndexForLeave(breakdown, leave) {
      if (!breakdown || !leave) return -1
      const leaveType = (leave.leave_type || leave.type || '').toUpperCase()
      const leaveTypeLower = leaveType.toLowerCase()
      const matches = (type, label) => {
        const t = (type || '').toLowerCase()
        const l = (label || '').toLowerCase()
        if (leaveType === 'CO' || leaveTypeLower.includes('compensatory') || leaveTypeLower === 'comp off') {
          return t.includes('comp') || l.includes('compensatory') || l.includes('comp off')
        }
        if (leaveType === 'PL' || leaveTypeLower.includes('privilege')) {
          return t.includes('privilege') || l.includes('privilege') || t === 'pl'
        }
        if (leaveType === 'CL' || leaveTypeLower.includes('casual')) {
          return t.includes('casual') || l.includes('casual') || t === 'cl'
        }
        if (leaveType === 'SL' || leaveTypeLower.includes('sick') || leaveTypeLower.includes('medical')) {
          return t.includes('sick') || t.includes('medical') || l.includes('sick') || t === 'sl'
        }
        if (leaveTypeLower.includes('paternity')) return t.includes('paternity') || l.includes('paternity')
        if (leaveTypeLower.includes('maternity')) return t.includes('maternity') || l.includes('maternity')
        if (leaveTypeLower.includes('exam')) return t.includes('exam') || l.includes('exam')
        if (leaveTypeLower.includes('carry')) return t.includes('carry') || l.includes('carry')
        if (leaveTypeLower.includes('half day')) return t.includes('privilege') || l.includes('privilege')
        return l.includes(leaveTypeLower) || t.includes(leaveTypeLower)
      }
      return breakdown.findIndex(item => matches(item.type, item.label))
    },
    getDisplayLeaveBreakdown(empId) {
      const breakdown = (this.availableLeaveBreakdown || []).map(item => {
        const allocated = parseFloat(item.allocated ?? item.assigned ?? item.accrued) || 0
        const used = parseFloat(item.used) || 0
        const apiAvailable = parseFloat(item.available ?? item.value) || 0
        return {
          ...item,
          allocated,
          used,
          pending: 0,
          value: apiAvailable,
          available: apiAvailable,
        }
      })
      this.getPendingLeavesForEmployee(empId).forEach(leave => {
        const days = this.getLeaveRecordDays(leave)
        const idx = this.findBreakdownIndexForLeave(breakdown, leave)
        if (idx >= 0) {
          breakdown[idx].pending = (breakdown[idx].pending || 0) + days
          breakdown[idx].value = Math.max(0, breakdown[idx].available - days)
          breakdown[idx].available = breakdown[idx].value
        }
      })
      return breakdown
    },
    /** Compact leave balance for Leaves table (Team/Org) — uses existing teamLeaveBalanceTable, no new API */
    getEmployeeLeaveBalance(empId) {
      if (!empId) return null
      const row = (this.teamLeaveBalanceTable || []).find(m => String(m.emp_id) === String(empId))
      if (!row) return null
      const balance = this.roundToHalf
        ? this.roundToHalf(row.leaveBalance)
        : row.leaveBalance
      const taken = this.roundToHalf
        ? this.roundToHalf(row.leavesApplied)
        : row.leavesApplied
      return { balance, taken, leaveBalance: balance, leavesApplied: taken }
    },
    calculateLeavesAvailedFromLeaves(leaves) {
      if (!leaves || !Array.isArray(leaves)) return 0
      if (typeof this.calculateLeaveUsageForEmployee !== 'function') return 0
      const usage = this.calculateLeaveUsageForEmployee(leaves)
      return Object.values(usage).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
    },
    getLeavesAvailedForEmployee(empId) {
      if (!empId) return 0
      const normalizedEmpId = String(empId)
      const selectedEmpId = this.selectedTeamMember || this.selectedTeamWiseEmployee
      const isCurrentlySelectedEmployee = selectedEmpId && String(selectedEmpId) === normalizedEmpId

      const belongsToEmployee = (leave, allowMissingEmpId = false) => {
        if (!leave) return false
        if (!leave.emp_id) return allowMissingEmpId
        return String(leave.emp_id) === normalizedEmpId
      }

      const approvedLeavesFrom = (leaves, allowMissingEmpId = false) => (leaves || []).filter(leave =>
        belongsToEmployee(leave, allowMissingEmpId) &&
        (leave.status || '').toLowerCase() === 'approved' &&
        !this.isExcludedFromLeaveKpi(leave)
      )

      // Priority 1: myLeaves from personal dashboard API (most accurate for filtered employee views)
      if (isCurrentlySelectedEmployee && this.myLeaves && this.myLeaves.length > 0) {
        return this.calculateLeavesAvailedFromLeaves(approvedLeavesFrom(this.myLeaves, true))
      }

      const myLeavesForEmployee = approvedLeavesFrom(this.myLeaves)
      if (myLeavesForEmployee.length > 0 || (this.myLeaves || []).some(leave => belongsToEmployee(leave))) {
        return this.calculateLeavesAvailedFromLeaves(myLeavesForEmployee)
      }

      // Priority 2: allTeamLeaves when myLeaves has no records for this employee
      const teamLeavesForEmployee = approvedLeavesFrom(this.allTeamLeaves)
      if (teamLeavesForEmployee.length > 0 || (this.allTeamLeaves || []).some(leave => belongsToEmployee(leave))) {
        return this.calculateLeavesAvailedFromLeaves(teamLeavesForEmployee)
      }

      if (this.teamDashboardData && this.teamDashboardData.team_leave_balances) {
        const perMember = this.teamDashboardData.team_leave_balances.per_member || []
        const memberData = perMember.find(m => String(m.emp_id) === normalizedEmpId)
        if (memberData && memberData.breakdown && Array.isArray(memberData.breakdown) && memberData.breakdown.length > 0) {
          return memberData.breakdown
            .filter(item => item.type !== 'quarterly_wfm' && item.type !== 'wfm' && item.type !== 'wfh')
            .reduce((sum, item) => sum + (parseFloat(item.used) || 0), 0)
        }
        if (memberData && memberData.total_used != null) {
          return Math.max(0, (parseFloat(memberData.total_used) || 0) - (parseFloat(memberData.wfh_count) || 0))
        }
      }

      const stats = (this.teamMemberLeaveBalances || []).find(s => String(s.emp_id) === normalizedEmpId)
      if (stats) {
        return Math.max(0, (parseFloat(stats.leavesTaken) || 0) - (parseFloat(stats.WFHCount) || 0))
      }

      const apiStats = (this.dashboardData && this.dashboardData.kpi_stats) || {}
      return Math.max(0, (parseFloat(apiStats.leaves_taken) || 0) - (parseFloat(apiStats.wfh_count) || 0))
    },
    /**
     * @deprecated Backend will provide leaves_taken breakdown in dashboard API
     * Calculate leave usage (taken leaves) from employee's leave records
     */
    calculateLeaveUsageForEmployee(leaves) {
      // Calculate leave usage from a specific employee's leaves
      const usage = {}
      if (!leaves || !Array.isArray(leaves)) return usage

      leaves.forEach(leave => {
        // Only count approved leaves
        if ((leave.status || '').toLowerCase() !== 'approved') return

        const leaveType = leave.type || leave.leave_type || 'Unknown'
        const leaveTypeLower = leaveType.toLowerCase()

        // SKIP Work From Home - it's not a leave and should NOT be deducted from balance
        if (this.isExcludedFromLeaveKpi(leave)) {
          return
        }
        
        // Check if it's a half-day leave
        const isHalfDay = leaveType === '1st Half Day' ||
                         leaveType === '2nd Half Day' ||
                         leaveTypeLower.includes('half day') ||
                         leave.half_day === true
        
        let mappedType = leaveType

        // Normalize types for mapping to breakdown fields
        if (leaveTypeLower.includes('casual')) {
          mappedType = 'casual_leave'
        } else if (leaveTypeLower.includes('sick') || leaveTypeLower.includes('medical')) {
          mappedType = 'sick_leave'
        } else if (leaveTypeLower.includes('privilege') || leaveTypeLower.includes('pto')) {
          mappedType = 'privilege_leave'
        } else if (isHalfDay) {
          // Half days are counted as privilege leave
          mappedType = 'privilege_leave'
        } else if (leaveTypeLower.includes('compensatory')) {
          mappedType = 'compensatory_balance'
        } else if (leaveTypeLower.includes('paternity')) {
          mappedType = 'paternity_leave'
        } else if (leaveTypeLower.includes('maternity')) {
          mappedType = 'maternity_leave'
        } else if (leaveTypeLower.includes('exam')) {
          mappedType = 'exam_leave'
        }

        // Calculate days - for half days, always use 0.5 per day in range
        let days = 0
        if (isHalfDay) {
          if (leave.from_date && leave.to_date) {
            const from = moment(leave.from_date)
            const to = moment(leave.to_date)
            const daysInRange = to.diff(from, 'days') + 1
            days = daysInRange * 0.5
          } else {
            days = 0.5
          }
        } else {
          days = leave.total_days !== undefined && leave.total_days !== null 
            ? parseFloat(leave.total_days) 
            : 0
        }
        
        usage[mappedType] = (usage[mappedType] || 0) + days
      })
      return usage
    },
    calculateTeamLeaveBreakdown() {
      // Aggregate leave breakdowns from all team members
      const aggregatedBreakdown = {
        carry_forward: 0,
        casual_leave: 0,
        sick_leave: 0,
        medical_leave: 0,
        compensatory_balance: 0,
        paternity_leave: 0,
        maternity_leave: 0,
      }
      
      const aggregatedUsage = {
        privilege_leave: 0,
        casual_leave: 0,
        sick_leave: 0,
        compensatory_balance: 0,
        paternity_leave: 0,
        maternity_leave: 0,
      }
      
      // Aggregate breakdowns and usage from all team members
      this.teamMemberConfigs.forEach(memberConfig => {
        if (!memberConfig.config) return
        
        const config = memberConfig.config
        let breakdown = config.leaveBalanceBreakdown || config.leave_balance_breakdown
        
        // Parse breakdown if it's a string
        if (typeof breakdown === 'string') {
          try {
            breakdown = JSON.parse(breakdown)
          } catch (e) {
            breakdown = null
          }
        }
        
        if (breakdown && typeof breakdown === 'object') {
          // Aggregate assigned balances
          aggregatedBreakdown.carry_forward += breakdown.carry_forward || 0
          aggregatedBreakdown.casual_leave += breakdown.casual_leave || 0
          aggregatedBreakdown.sick_leave += breakdown.sick_leave || 0
          aggregatedBreakdown.medical_leave += breakdown.medical_leave || 0
          aggregatedBreakdown.compensatory_balance += breakdown.compensatory_balance || 0
          aggregatedBreakdown.paternity_leave += breakdown.paternity_leave || 0
          aggregatedBreakdown.maternity_leave += breakdown.maternity_leave || 0
        }
      })
      
      // Aggregate usage from all team leaves
      if (this.allTeamLeaves && Array.isArray(this.allTeamLeaves)) {
        const approvedLeaves = this.allTeamLeaves.filter(leave => {
          const status = (leave.status || '').toLowerCase()
          return status === 'approved' || status === 'manager approved - pending hr'
        })
        
        approvedLeaves.forEach(leave => {
          const leaveType = (leave.leave_type || '').toLowerCase()
          const isHalfDay = leaveType.includes('half day') || 
                           leave.leave_type === '1st Half Day' || 
                           leave.leave_type === '2nd Half Day' ||
                           leave.half_day === true
          
          let days = 0
          if (isHalfDay) {
            if (leave.from_date && leave.to_date) {
              const from = moment(leave.from_date)
              const to = moment(leave.to_date)
              const daysInRange = to.diff(from, 'days') + 1
              days = daysInRange * 0.5
            } else {
              days = 0.5
            }
          } else {
            days = parseFloat(leave.total_days || 0)
          }
          
          if (leaveType.includes('casual')) {
            aggregatedUsage.casual_leave += days
          } else if (leaveType.includes('sick') || leaveType.includes('medical')) {
            aggregatedUsage.sick_leave += days
          } else if (leaveType.includes('privilege') || leaveType.includes('pto') || isHalfDay) {
            aggregatedUsage.privilege_leave += days
          } else if (leaveType.includes('compensatory')) {
            aggregatedUsage.compensatory_balance += days
          } else if (leaveType.includes('paternity')) {
            aggregatedUsage.paternity_leave += days
          } else if (leaveType.includes('maternity')) {
            aggregatedUsage.maternity_leave += days
          }
        })
      }
      
      // Calculate remaining balances
      const breakup = []
      
      // 1. Privilege + Carry Forward
      const totalCFTotal = aggregatedBreakdown.carry_forward
      const plAccrued = this.teamMemberConfigs.length * (moment().month() + 1) // 1 per month per employee
      const plTaken = aggregatedUsage.privilege_leave
      
      // Priority: Deduct from CF first
      let remainingCF = Math.max(0, totalCFTotal - plTaken)
      let remainingPL = plAccrued
      if (plTaken > totalCFTotal) {
        remainingPL = Math.max(0, plAccrued - (plTaken - totalCFTotal))
      }
      
      if (totalCFTotal > 0 || plAccrued > 0) {
        breakup.push({ label: 'Carry Forward (Remaining)', value: remainingCF })
        breakup.push({ label: 'Privilege Leave (Accrued)', value: remainingPL })
      }
      
      // 2. Casual Leave
      const casualRemaining = Math.max(0, aggregatedBreakdown.casual_leave - aggregatedUsage.casual_leave)
      if (aggregatedBreakdown.casual_leave > 0 || casualRemaining > 0) {
        breakup.push({ label: 'Casual Leave', value: casualRemaining })
      }
      
      // 3. Sick / Medical
      const sickBase = aggregatedBreakdown.sick_leave + aggregatedBreakdown.medical_leave
      const sickRemaining = Math.max(0, sickBase - aggregatedUsage.sick_leave)
      if (sickBase > 0 || sickRemaining > 0) {
        breakup.push({ label: 'Sick / Medical Leave', value: sickRemaining })
      }
      
      // 4. Compensatory
      const compRemaining = Math.max(0, aggregatedBreakdown.compensatory_balance - aggregatedUsage.compensatory_balance)
      if (aggregatedBreakdown.compensatory_balance > 0 || compRemaining > 0) {
        breakup.push({ label: 'Compensatory Leave', value: compRemaining })
      }
      
      // 5. Paternity
      const paternityRemaining = Math.max(0, aggregatedBreakdown.paternity_leave - aggregatedUsage.paternity_leave)
      if (aggregatedBreakdown.paternity_leave > 0 || paternityRemaining > 0) {
        breakup.push({ label: 'Paternity Leave', value: paternityRemaining })
      }
      
      // 6. Maternity
      const maternityRemaining = Math.max(0, aggregatedBreakdown.maternity_leave - aggregatedUsage.maternity_leave)
      if (aggregatedBreakdown.maternity_leave > 0 || maternityRemaining > 0) {
        breakup.push({ label: 'Maternity Leave', value: maternityRemaining })
      }
      
      return breakup
    },
    scrollToLeaderAnnouncements() {
      // Org: announcements are the first tab of the HR side panel
      if (this.viewMode === 'org') {
        this.scrollToCompanySchedule(0)
        return
      }
      this.$nextTick(() => {
        const el = document.getElementById('leader-announcements')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    scrollToCompanySchedule(tabIndex) {
      this.$nextTick(() => {
        const el = document.getElementById('company-schedule')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        const panel = this.$refs.companySchedulePanel
        if (panel && typeof panel.open === 'function') {
          panel.open(tabIndex)
        }
      })
    },
    handleApprovalPendingClick() {
      const queueCount =
        (this.pendingLeaveApprovalsForQueue || []).length +
        (this.pendingRegularizationsForApproval || []).length
      const kpiCount = this.displayKPIStats ? this.displayKPIStats.approvalPending : 0
      if (queueCount === 0 && kpiCount === 0) {
        return
      }

      // Clear Leaves & Regularization modal filters that would hide the approval queue
      this.regularizationSourceTypeFilter = 'all'
      this.regularizationEmployeeFilter = null
      this.regularizationLeaveTypeFilter = null
      this.regularizationSourceTypeFilterTemp = 'all'
      this.regularizationEmployeeFilterTemp = null
      this.regularizationLeaveTypeFilterTemp = null

      // Review all / Leave approvals → Leaves tab filtered to approvable pending set
      this.teamLeaveFilter = 'pending'
      this.teamRegularizationFilter = 'pending'
      this.leavesRegularizationActiveTab = 0
      if (typeof this.resetLeavesDisplay === 'function') {
        this.resetLeavesDisplay()
      }

      this.$nextTick(() => {
        const el = document.getElementById('leaves-regularization') || document.querySelector('.leaves-regularization-section')
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    handleRegToApproveClick() {
      // Always open Regularization tab (even when count is 0 — Review all from empty footer path)
      this.teamRegularizationFilter = 'pending'
      this.leavesRegularizationActiveTab = 1
      this.$nextTick(() => {
        if (typeof this.scrollToRegularization === 'function') {
          this.scrollToRegularization()
          return
        }
        const el = document.getElementById('leaves-regularization') || document.querySelector('.leaves-regularization-section')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    /** Out today footer — Leaves tab without pending-filter duplication */
    handleViewLeavesTab() {
      this.teamLeaveFilter = 'all'
      this.leavesRegularizationActiveTab = 0
      this.$nextTick(() => {
        const el = document.getElementById('leaves-regularization') || document.querySelector('.leaves-regularization-section')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    isPresentOrHalfDay(leaveType) {
      return isPresentOrHalfDayLeaveType(leaveType, this.leaveTypesFromDB)
    },
    transformLeavesToRegularizationFormat(leaves) {
      // Transform leave records to match regularization table format
      // Show only 1 entry per leave (not expanded by date)
      const transformed = []
      
      // Safety check: ensure leaves is an array
      if (!Array.isArray(leaves) || leaves.length === 0) {
        return transformed
      }
      
      leaves.forEach(leave => {
        // Require from_date only — to_date often omitted on single-day pending requests
        if (!leave || !leave.from_date) return
        const toDateRaw = leave.to_date || leave.from_date
        
        // Use from_date for the Date column (the actual leave date, not application date)
        const leaveDate = moment(leave.from_date).format('YYYY-MM-DD')
        
        // Format from_date and to_date for IN Time and OUT Time columns (format: DD MMM YYYY)
        const fromDateFormatted = moment(leave.from_date).format('DD MMM YYYY')
        const toDateFormatted = moment(toDateRaw).format('DD MMM YYYY')
        
        transformed.push({
          datestring: leaveDate, // Date column shows the actual leave date (from_date)
          date: leaveDate,
          emp_id: leave.emp_id || leave.employee_id,
          issue_type: (leave.partial_wfh === true && (leave.leave_type === 'WFH' || leave.leave_type === 'Work From Home'))
            ? 'WFH (Partial)'
            : this.formatLeaveType(leave.leave_type) || 'Leave',
          partial_wfh: leave.partial_wfh === true,
          worktime: 'N/A',
          intime: fromDateFormatted, // IN Time column shows from_date
          outtime: toDateFormatted, // OUT Time column shows to_date
          status: leave.status || 'Pending',
          regularization_status: leave.status || 'Pending',
          manager_remarks: leave.manager_remarks || leave.manager_comment || null,
          manager_comment: leave.manager_remarks || leave.manager_comment || null,
          remarks: leave.comment || leave.remarks || null,
          comment: leave.comment || leave.remarks || null,
          has_punch_data: false,
          has_biometric: false,
          hr_update_dt: leave.hr_update_dt || null,
          manager_update_dt: leave.manager_update_dt || null,
          hr_id: leave.hr_id || null,
          id: leave.id,
          attendance_id: leave.id,
          data_source_status: 'Leave',
          source_type: 'Leave',
          leave_id: leave.id,
          leave_type: leave.leave_type,
          from_date: leave.from_date,
          to_date: toDateRaw,
          total_days: leave.total_days != null ? leave.total_days : (moment(toDateRaw).diff(moment(leave.from_date), 'days') + 1),
          applied_date: leave.applied_date || leave.created_at,
          department: leave.department,
          is_probation: leave.is_probation,
        })
      })
      
      return transformed
    },
    initializeUser() {
      const userData = JSON.parse(localStorage.getItem('userData'))
      if (userData) {
        this.emp_id = userData.emp_id
        this.empcode = userData.empcode || userData.emp_id
        this.mgr_id = userData.mgr_id
        this.userRole = userData.roles?.[0] || ''
        // Get roleID - check if it exists in userData
        this.userRoleID = userData.roleID || userData.role_id || this.getRoleIDFromRole(this.userRole)
        // Current user's department (for department-based leave approval flow)
        this.currentUserDepartment = (userData.department && String(userData.department).trim()) || ''
        // Set current view to self initially
        this.currentViewEmpId = this.emp_id
        
        // Initialize view mode options based on role and permissions
        this.initializeViewModeOptions()
        
        // Set default viewMode based on role:
        // - HR (role 5) or HR Director (role 10): default to 'team' view showing all employees
        // - Account Manager (role 3): default to 'team' view showing their team
        // - Others: default to 'my' view
        
        // Check role by roleID (5 = HR, 10 = HR Director, 3 = Account Manager)
        const isHRRole = this.userRoleID === 5 || this.userRoleID === 10
        const isManagerRole = this.userRoleID === 3
        
        // Also check by role name as fallback
        const roleName = (this.userRole || '').toUpperCase()
        const isManagerByRoleName = roleName.includes('ACCOUNT-MANAGER') || 
                                   roleName.includes('ACCOUNT MANAGER') ||
                                   roleName.includes('PROGRAM-MANAGER') ||
                                   roleName.includes('PROGRAM MANAGER')
        
        if (isHRRole || isManagerRole || isManagerByRoleName) {
          // HR/HR Director or Account Manager: default to team view
          // Note: canViewTeam might not be available yet, so we'll verify in mounted()
          this.viewMode = 'team'
        } else {
          // Other users: set based on permissions
          if (!this.canViewEmployee && !this.canViewTeam) {
            // If user has neither permission, default to 'my' but they shouldn't be here
            this.viewMode = 'my'
          } else if (!this.canViewTeam && this.canViewEmployee) {
            // If user can only view employee, set to employee view
            this.viewMode = 'my'
          } else if (this.canViewTeam) {
            // If user can view team, keep current viewMode or default to 'my'
            if (!this.viewMode || this.viewMode === 'my') {
              this.viewMode = 'my'
            }
          }
        }
        
        // Team members and account managers are now loaded earlier in the function
      }
    },
    initializeViewModeOptions() {
      // Base options based on permissions
      // Employee View is available if user has 'read' permission on 'employee'
      const options = []
      
      // Add Employee View if user has permission
      if (this.canViewEmployee) {
        options.push({ text: 'Employee View', shortText: 'My', value: 'my', icon: 'UserIcon' })
      }
      
      // Add Team View if user can view team
      if (this.canViewTeam) {
        options.push({ text: 'Team View', shortText: 'Team', value: 'team', icon: 'UsersIcon' })
      }
      
      // Add Org View if user has permission to view organization-wide data
      if (this.canViewOrg) {
        options.push({ text: 'Org View', shortText: 'Org', value: 'org', icon: 'GridIcon' })
      }
      
      this.viewModeOptions = options
      
      // If user can only view employee (not team), ensure viewMode is set to 'my'
      if (!this.canViewTeam && this.canViewEmployee) {
        this.viewMode = 'my'
      }
    },
    getRoleIDFromRole(role) {
      // Fallback: Map role names to roleIDs if backend doesn't provide roleID
      // This should only be used as a last resort - backend should always provide roleID
      const roleMap = {
        'HR-DIRECTOR': 10,
        'HR': 5,
        'ACCOUNT-MANAGER': 3,
        'account-manager': 3,
        'Account Manager': 3,
      }
      return roleMap[role] || null
    },
    getApiRoleFromUserRole(userRole) {
      // Map user role to API role format expected by the regularization API
      const roleMap = {
        'HR-DIRECTOR': 'hr-director',
        'HR': 'hr',
        'ACCOUNT-MANAGER': 'account-manager',
        'account-manager': 'account-manager',
        'Account Manager': 'account-manager',
        'PROGRAM-MANAGER': 'program-manager',
        'program-manager': 'program-manager',
        'PROJECT-MANAGER': 'project-manager',
        'project-manager': 'project-manager',
        'ADMIN': 'admin',
        'admin': 'admin',
        'MASTER': 'master',
        'master': 'master',
      }
      // If role is in map, return mapped value, otherwise return lowercase version
      return roleMap[userRole] || (userRole ? userRole.toLowerCase() : 'employee')
    },
    /**
     * NEW SIMPLIFIED loadData using consolidated backend APIs
     * This replaces multiple API calls with single consolidated calls
     * Once backend APIs are ready, this will be the primary method
     */
    async loadDataV2() {
      // Cancel any pending requests
      if (this.abortController) {
        this.abortController.abort()
      }
      this.abortController = new AbortController()
      
      this.loading = true
      
      try {
        const params = {
          role: this.userRole,
          view_mode: this.viewMode,
          month: this.selectedMonth,
          year: this.selectedYear
        }
        
        if (this.viewMode === 'my' || ((this.viewMode === 'team' || this.viewMode === 'org') && (this.selectedTeamMember || this.selectedTeamWiseEmployee))) {
          // My View or viewing specific team member/employee - use personal dashboard API
          const targetEmpId = this.selectedTeamMember || this.selectedTeamWiseEmployee || this.emp_id
          params.selected_employee_id = targetEmpId !== this.emp_id ? targetEmpId : null
          
          const res = await attendanceService.getDashboardData(this.emp_id, params)
          if (res && res.status === 'OK' && res.data) {
            
            // Update dashboardData properties using Vue.set for reactivity
            if (res.data.kpi_stats) {
              this.$set(this.dashboardData, 'kpi_stats', res.data.kpi_stats)
            }
            if (res.data.leave_balance_breakdown) {
              this.$set(this.dashboardData, 'leave_balance_breakdown', res.data.leave_balance_breakdown)
            }
            if (res.data.leave_balance_raw) {
              this.$set(this.dashboardData, 'leave_balance_raw', res.data.leave_balance_raw)
            }
            // Only update calendar_data if it's actually present and non-empty in the response
            // This prevents overwriting calendar data that was loaded separately via loadCalendarData()
            if (res.data.calendar_data && Array.isArray(res.data.calendar_data) && res.data.calendar_data.length > 0) {
              this.$set(this.dashboardData, 'calendar_data', res.data.calendar_data)
            }
            if (res.data.chart_data) {
              this.$set(this.dashboardData, 'chart_data', res.data.chart_data)
            }
            if (res.data.config) {
              this.$set(this.dashboardData, 'config', res.data.config)
            }
            if (res.data.leaves) {
              this.$set(this.dashboardData, 'leaves', res.data.leaves)
            }
            // Load regularization cases - check if API provided them and if they're non-empty
            if (res.data.regularization_cases && Array.isArray(res.data.regularization_cases) && res.data.regularization_cases.length > 0) {
              // Ensure emp_id is always set so the unifiedRegularizationData filter keeps the records
              const viewEmpId = targetEmpId
              const todayLocal = moment().format('YYYY-MM-DD')
              const casesWithEmpId = res.data.regularization_cases
                .map(c => c.emp_id ? c : { ...c, emp_id: viewEmpId })
                // Exclude today's local calendar day (day may still be in progress)
                .filter(c => {
                  const caseDate = c.datestring || c.date
                  if (!caseDate) return true
                  return moment(caseDate).format('YYYY-MM-DD') !== todayLocal
                })
              this.$set(this.dashboardData, 'regularization_cases', casesWithEmpId)
              // Also update legacy property for backward compatibility
              this.regularizationCases = casesWithEmpId
            } else {
              // API didn't provide regularization cases or provided empty array - load them separately
              try {
                // In team view showing all team, pass null to load team data; otherwise pass specific emp_id
                const regularizationEmpId = (this.viewMode === 'team' && !this.selectedTeamMember && !this.selectedTeamWiseEmployee) 
                  ? null 
                  : (this.selectedTeamMember || this.selectedTeamWiseEmployee || this.emp_id)
                await this.loadRegularizationCases(regularizationEmpId)
                if (this.canManageLeaves) {
                  await this.loadPendingRegularizations()
                }
              } catch (err) {
                console.error('Error loading regularization cases:', err)
                // Don't throw - allow component to continue loading
              }
            }
            if (res.data.holidays) {
              this.$set(this.dashboardData, 'holidays', res.data.holidays)
            }
            if (res.data.announcements) {
              this.$set(this.dashboardData, 'announcements', res.data.announcements)
            }
            if (res.data.birthdays) {
              this.$set(this.dashboardData, 'birthdays', res.data.birthdays)
              // Also update legacy property for announcements display
              this.birthdays = res.data.birthdays
            }
            if (res.data.work_anniversaries) {
              this.$set(this.dashboardData, 'work_anniversaries', res.data.work_anniversaries)
              // Also update legacy property for announcements display
              this.workAnniversaries = res.data.work_anniversaries
            }

            // Update legacy properties for backward compatibility
            if (res.data.kpi_stats) {
              this.leaveStats = {
                leaveBalance: res.data.kpi_stats.leave_balance || 0,
                leavesTaken: res.data.kpi_stats.leaves_taken || 0,
                WFHCount: res.data.kpi_stats.wfh_count || 0
              }
            }
            if (res.data.leaves) {
              this.myLeaves = res.data.leaves
            }
            // regularizationCases is already set above in the new API data section
            // Only update here if it wasn't set (shouldn't happen, but keeping for safety)
            if (!this.regularizationCases || this.regularizationCases.length === 0) {
              if (res.data.regularization_cases && Array.isArray(res.data.regularization_cases) && res.data.regularization_cases.length > 0) {
                const todayLocal = moment().format('YYYY-MM-DD')
                this.regularizationCases = res.data.regularization_cases.filter(c => {
                  const caseDate = c && (c.datestring || c.date)
                  if (!caseDate) return true
                  return moment(caseDate).format('YYYY-MM-DD') !== todayLocal
                })
              }
            }
            if (res.data.holidays) {
              this.holidays = res.data.holidays
            }
            if (res.data.config) {
              this.employeeConfig = res.data.config
              this.isOnProbation = res.data.config.is_probation || false
              this.isPermanentWFH = res.data.config.is_permanent_wfh || false
            }
            if (res.data.announcements) {
              this.announcements = res.data.announcements
            }
            if (res.data.birthdays) {
              this.birthdays = res.data.birthdays
            }
            if (res.data.work_anniversaries) {
              this.workAnniversaries = res.data.work_anniversaries
            }
            
            // If calendar_data is missing or empty, load it from dedicated calendar API
            if (!res.data.calendar_data || res.data.calendar_data.length === 0) {
              await this.loadCalendarData(this.selectedMonth, this.selectedYear)
            }
          }
          
        } else if (this.viewMode === 'team') {
          // Team View - use team dashboard API
          const filters = {
            selected_manager_id: this.selectedAccountManager,
            selected_employee_id: this.selectedTeamMember || this.selectedTeamWiseEmployee
          }
          
          // Determine role for API
          let apiRole = 'employee'
          if (this.isHRDirector) apiRole = 'hr-director'
          else if (this.isHR) apiRole = 'hr'
          else if (this.isAccountManager) apiRole = 'account-manager'
          else if (this.isAdmin) apiRole = 'admin'
          else if (this.isMaster) apiRole = 'master'
          
          const res = await attendanceService.getTeamDashboard(this.emp_id, apiRole, filters)
          if (res && res.status === 'OK' && res.data) {
            this.teamDashboardData = res.data
            
            // Update legacy properties for backward compatibility
            if (res.data.team_members) {
              this.teamMembers = res.data.team_members
            }
            // Don't set allTeamLeaves from pending_leave_approvals here - loadAllTeamLeaves() will load all leaves (not just pending)
            // pending_leave_approvals is stored in teamDashboardData and accessed via pendingLeaveApprovalsFromAPI computed property
            if (res.data.pending_regularization_approvals) {
              this.pendingRegularizations = res.data.pending_regularization_approvals
            }
            if (res.data.team_summary) {
              this.teamStats = {
                totalLeavesAvailed: res.data.team_summary.leaves_availed || 0,
                totalWFHCount: res.data.team_summary.wfh_count || 0,
                totalRegularization: res.data.team_summary.regularization_cases || 0,
                membersOnLeave: res.data.team_summary.members_on_leave || 0
              }
            }
          }

          // Load account managers for filter dropdown (team view)
          await this.loadAccountManagers()

          if (this.selectedAccountManager && this.teamWiseEmployees && this.teamWiseEmployees.length > 0) {
            this.teamMembers = [...this.teamWiseEmployees]
          }
          
          // Load all team leaves (not just pending) for team view
          // This is needed for unifiedRegularizationData to transform leaves into regularization format
          try {
            if (this.selectedAccountManager && this.teamWiseEmployees && this.teamWiseEmployees.length > 0) {
              await this.loadAccountManagerTeamData()
            } else {
              await this.loadAllTeamLeaves()
            }
          } catch (err) {
            console.error('Error loading all team leaves in team view:', err)
            // Don't throw - allow component to continue loading
          }
          
          // Load regularization cases for team view (showing all team members)
          try {
            const regularizationEmpId = this.selectedTeamMember || this.selectedTeamWiseEmployee
              || (this.selectedAccountManager ? this.selectedAccountManager : null)
            await this.loadRegularizationCases(regularizationEmpId)
            if (this.canManageLeaves) {
              await this.loadPendingRegularizations()
            }
          } catch (err) {
            console.error('Error loading regularization cases in team view:', err)
            // Don't throw - allow component to continue loading
          }

          // Today's punch map for Present/Absent Team KPIs
          try {
            await this.loadTeamTodayAttendance()
          } catch (err) {
            console.error('Error loading team today attendance:', err)
            this.teamTodayPunchRecords = []
          }

          // People on probation (HR user list / probation API — Team snapshot Needs attention)
          try {
            await this.loadProbationData()
          } catch (err) {
            console.error('Error loading probation data in team view:', err)
            this.probationData = []
            this.probationSourceMissing = true
          }
          
          // Also load personal dashboard data for calendar context
          const personalRes = await attendanceService.getDashboardData(this.emp_id, {
            role: this.userRole,
            view_mode: 'my',
            month: this.selectedMonth,
            year: this.selectedYear
          })
          
          if (personalRes && personalRes.status === 'OK' && personalRes.data) {
            // Merge personal data but don't override team data
            if (!this.dashboardData) {
              this.dashboardData = personalRes.data
            } else {
              // Merge calendar and other personal data
              this.$set(this.dashboardData, 'calendar_data', personalRes.data.calendar_data)
              this.$set(this.dashboardData, 'config', personalRes.data.config)
            }
            
            if (personalRes.data.holidays) {
              this.holidays = personalRes.data.holidays
            }
            if (personalRes.data.announcements) {
              this.announcements = personalRes.data.announcements
            }
          }
        } else if (this.viewMode === 'org') {
          // Organization View - use organization dashboard API
          const filters = {
            selected_manager_id: this.selectedAccountManager,
            selected_employee_id: this.selectedTeamMember || this.selectedTeamWiseEmployee
          }
          
          const res = await attendanceService.getOrganizationDashboard(filters)
          if (res && res.status === 'OK' && res.data) {
            // Store org dashboard data in teamDashboardData for compatibility
            // (org view uses similar structure to team view)
            this.teamDashboardData = res.data
            
            // Update legacy properties for backward compatibility
            if (res.data.team_members || res.data.all_employees) {
              this.teamMembers = res.data.team_members || res.data.all_employees || []
            }
            if (res.data.pending_regularization_approvals) {
              this.pendingRegularizations = res.data.pending_regularization_approvals
            }
            if (res.data.team_summary || res.data.org_summary) {
              const summary = res.data.team_summary || res.data.org_summary
              this.teamStats = {
                totalLeavesAvailed: summary.leaves_availed || 0,
                totalWFHCount: summary.wfh_count || 0,
                totalRegularization: summary.regularization_cases || 0,
                membersOnLeave: summary.members_on_leave || 0
              }
            }
          }

          // Load account managers for filter dropdown (org view)
          await this.loadAccountManagers()
          
          // When an account manager is selected, use their team for section data (not logged-in user's team)
          if (this.selectedAccountManager && this.teamWiseEmployees && this.teamWiseEmployees.length > 0) {
            this.teamMembers = [...this.teamWiseEmployees]
          }
          
          // Load team/org leaves — scoped to account manager's team when filtered
          try {
            if (this.selectedAccountManager && this.teamWiseEmployees && this.teamWiseEmployees.length > 0) {
              await this.loadAccountManagerTeamData()
            } else {
              await this.loadAllTeamLeaves()
            }
          } catch (err) {
            console.error('Error loading all team leaves in org view:', err)
          }
          
          // Load regularization cases for org view
          try {
            const regularizationEmpId = this.selectedTeamMember || this.selectedTeamWiseEmployee
              || (this.selectedAccountManager ? this.selectedAccountManager : null)
            await this.loadRegularizationCases(regularizationEmpId)
            if (this.canManageLeaves) {
              await this.loadPendingRegularizations()
            }
          } catch (err) {
            console.error('Error loading regularization cases in org view:', err)
          }

          try {
            await this.loadProbationData()
          } catch (err) {
            console.error('Error loading probation data in org view:', err)
            this.probationData = []
            this.probationSourceMissing = true
          }

          // Today's punch map for Present/Absent Org KPIs (same source as Team View)
          try {
            await this.loadTeamTodayAttendance()
          } catch (err) {
            console.error('Error loading org today attendance:', err)
            this.teamTodayPunchRecords = []
          }
          
          // Also load personal dashboard data for calendar context
          const personalRes = await attendanceService.getDashboardData(this.emp_id, {
            role: this.userRole,
            view_mode: 'my',
            month: this.selectedMonth,
            year: this.selectedYear
          })
          
          if (personalRes && personalRes.status === 'OK' && personalRes.data) {
            if (!this.dashboardData) {
              this.dashboardData = personalRes.data
            } else {
              this.$set(this.dashboardData, 'calendar_data', personalRes.data.calendar_data)
              this.$set(this.dashboardData, 'config', personalRes.data.config)
            }
            
            if (personalRes.data.holidays) {
              this.holidays = personalRes.data.holidays
            }
            if (personalRes.data.announcements) {
              this.announcements = personalRes.data.announcements
            }
          }
        }
        
        // Update chart after data load
        this.$nextTick(() => {
          this.updateLeaveBalanceChart()
        })
        
      } catch (err) {
        console.error('Error in loadDataV2:', err)
      } finally {
        this.loading = false
      }
    },
    
    /**
     * Load calendar data for specific month/year using dedicated calendar API
     */
    async loadCalendarData(month = null, year = null) {
      const targetMonth = month !== null ? month : this.selectedMonth
      const targetYear = year !== null ? year : this.selectedYear
      const targetEmpId = this.selectedTeamMember || this.selectedTeamWiseEmployee || this.emp_id
      
      try {
        const res = await attendanceService.getCalendarData(targetEmpId, targetMonth, targetYear)
        
        if (res && res.status === 'OK' && res.data) {
          // API returns: { data: { month, year, calendar: [...], summary: {...} } }
          const calendarResponse = res.data.data || res.data
          
          if (calendarResponse && calendarResponse.calendar) {
            // Ensure dashboardData exists with proper structure
            if (!this.dashboardData) {
              this.dashboardData = {
                kpi_stats: {
                  leave_balance: 0,
                  leaves_taken: 0,
                  approval_pending: 0,
                  regularization_count: 0,
                  wfh_count: 0
                },
                leave_balance_breakdown: [],
                calendar_data: [],
                chart_data: {},
                config: {}
              }
            }
            
            // Update calendar_data in dashboardData using Vue.set for reactivity
            this.$set(this.dashboardData, 'calendar_data', calendarResponse.calendar)
            
            // Also update month summary if provided
            if (calendarResponse.summary) {
              this.$set(this.dashboardData, 'summary', calendarResponse.summary)
            }
          }
        }
      } catch (err) {
        console.error('Error loading calendar data:', err)
        // Don't throw - allow fallback to legacy data
      }
    },
    
    /**
     * Main loadData method - uses new consolidated backend APIs
     */
    async loadData() {
      // Use new V2 method with consolidated backend APIs
      return this.loadDataV2()
    },
    onViewModeButtonClick(value) {
      this.viewMode = value
      this.handleViewModeChange()
    },
    handleViewModeChange() {
      if (this.viewMode === 'my') {
        // Switch to my view - show own data
        this.selectedTeamMember = null
        this.selectedAccountManager = null
        this.selectedTeamWiseEmployee = null
        this.currentViewEmpId = this.emp_id
        this.allTeamLeaves = [] // Clear team leaves
        this.loadData()
      } else if (this.viewMode === 'team') {
        // Switch to team view
        this.selectedTeamMember = null
        this.selectedAccountManager = null
        this.selectedTeamWiseEmployee = null
        this.currentViewEmpId = this.emp_id
        // Reset analytics
        this.teamWiseAnalytics = {
          totalEmployees: 0,
          totalLeaves: 0,
          totalWFH: 0,
          pendingApprovals: 0,
          regularizationCases: 0,
        }
        // Load data for Account Manager (team filtered by mgr_id) or HR/HR-Director (all employees)
        if (this.isAccountManager || this.isHRDirector || this.isHR) {
          this.loadData() // Will load team leaves (filtered for Account Manager, all employees for HR/HR-Director)
        }
      } else if (this.viewMode === 'org') {
        // Switch to org view - show organization-wide data
        this.selectedTeamMember = null
        this.selectedAccountManager = null
        this.selectedTeamWiseEmployee = null
        this.currentViewEmpId = this.emp_id
        // Reset analytics
        this.teamWiseAnalytics = {
          totalEmployees: 0,
          totalLeaves: 0,
          totalWFH: 0,
          pendingApprovals: 0,
          regularizationCases: 0,
        }
        this.loadData()
      }
    },
    getUserName(empId, member = null) {
      if (member) {
        // Check for title field first (API returns names in title field)
        if (member.title) {
          return member.title.trim()
        }
        if (member.first_name) {
          return `${member.first_name} ${member.last_name || ''}`.trim()
        }
        if (member.name) {
          return member.name || empId
        }
      }
      // Try to find in team members
      const teamMember = this.teamMembers.find(m => (m.emp_id === empId) || (m.id === empId))
      if (teamMember) {
        if (teamMember.title) {
          return teamMember.title.trim()
        }
        if (teamMember.first_name) {
          return `${teamMember.first_name || ''} ${teamMember.last_name || ''}`.trim() || empId
        }
        if (teamMember.name) {
          return teamMember.name
        }
      }
      // Try to find in team wise employees
      const teamWiseEmployee = this.teamWiseEmployees.find(m => (m.emp_id === empId) || (m.id === empId))
      if (teamWiseEmployee) {
        if (teamWiseEmployee.title) {
          return teamWiseEmployee.title.trim()
        }
        if (teamWiseEmployee.first_name) {
          return `${teamWiseEmployee.first_name} ${teamWiseEmployee.last_name || ''}`.trim()
        }
        if (teamWiseEmployee.name) {
          return teamWiseEmployee.name
        }
      }
      // Try to find in account managers
      const accountManager = this.accountManagers.find(m => (m.emp_id === empId) || (m.id === empId))
      if (accountManager) {
        if (accountManager.title) {
          return accountManager.title.trim()
        }
        if (accountManager.first_name) {
          return `${accountManager.first_name} ${accountManager.last_name || ''}`.trim()
        }
        if (accountManager.name) {
          return accountManager.name
        }
      }
      // Try to get from userData if it's self
      if (empId === this.emp_id) {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        if (userData.first_name) {
          return `${userData.first_name} ${userData.last_name || ''}`.trim()
        }
        if (userData.name) {
          return userData.name
        }
      }
      return empId
    },
    showNextHoliday() {
      if (this.currentHolidayIndex < this.upcomingHolidays.length - 1) {
        this.currentHolidayIndex++
      }
    },
    showPreviousHoliday() {
      if (this.currentHolidayIndex > 0) {
        this.currentHolidayIndex--
      }
    },
    showNextCompanyWfhDay() {
      if (this.currentCompanyWfhDayIndex < this.upcomingCompanyWfhDays.length - 1) {
        this.currentCompanyWfhDayIndex++
      }
    },
    showPreviousCompanyWfhDay() {
      if (this.currentCompanyWfhDayIndex > 0) {
        this.currentCompanyWfhDayIndex--
      }
    },
    async loadEmployeeConfig(empId = null) {
      const targetEmpId = empId || this.currentViewEmpId || this.emp_id
      try {
        const signal = this.abortController ? this.abortController.signal : null
        const res = await attendanceService.getEmployeeConfig(targetEmpId, signal)
        if (res && res.status === 'OK' && res.data) {
          this.employeeConfig = res.data
          
          // Set probation status from API
          this.isOnProbation = res.data.isOnProbation || false
          
          // Set apply_to based on probation status
          if (this.isOnProbation) {
            this.leaveForm.apply_to = 'hr'
          }
          
          // Store total leave balance from leave_balance field (for Total Leaves display)
          const totalLeaveBalance = res.data.leaveBalance || res.data.leave_balance || 0
          
          // Update leave stats breakdown from API if provided
          // Handle both camelCase and snake_case field names
          let breakdown = res.data.leaveBalanceBreakdown || res.data.leave_balance_breakdown
          
          // If breakdown is a string, try to parse it as JSON
          if (typeof breakdown === 'string') {
            try {
              breakdown = JSON.parse(breakdown)
            } catch (parseErr) {
              console.warn('Failed to parse leaveBalanceBreakdown as JSON:', parseErr)
              breakdown = null
            }
          }
          
          if (breakdown && typeof breakdown === 'object') {
            // Calculate available leave balance from individual leave types (for Available Leaves display)
            if (breakdown.privilege_leave !== undefined || 
                breakdown.casual_leave !== undefined || 
                breakdown.sick_leave !== undefined || 
                breakdown.compensatory_balance !== undefined ||
                breakdown.carry_forward !== undefined) {
              // Sum all leave type balances from breakdown
              const sickOrMedicalLeave = (breakdown.sick_leave || 0) + (breakdown.medical_leave || 0)
              
              this.leaveStats = {
                ...this.leaveStats,
                totalLeaveBalance: totalLeaveBalance, 
                ...breakdown
              }
              
            } else {
              // Use the breakdown as-is if it has leaveBalance field
              this.leaveStats = {
                ...this.leaveStats,
                totalLeaveBalance: totalLeaveBalance, // Total from leave_balance field
                ...breakdown
              }
            }
          } else {
            // If no breakdown, use totalLeaveBalance for both
            this.leaveStats = {
              ...this.leaveStats,
              totalLeaveBalance: totalLeaveBalance,
              leaveBalance: totalLeaveBalance
            }
          }
        }
      } catch (err) {
        console.error('Error loading employee config:', err)
      }
    },
    /**
     * Load global leave types from the database API (/api/leave-types)
     * This fetches all leave types configured in the system
     */
    async loadLeaveTypesFromDB() {
      try {
        const res = await attendanceService.getLeaveTypes()
        
        if (res && res.status === 'OK' && res.data) {
          this.leaveTypesFromDB = Array.isArray(res.data) ? res.data : []
          
          this.leaveTypeOptions = this.leaveTypesFromDB
            .filter(lt => lt.is_active === true || lt.is_active === 'true' || lt.is_active === 1)
            .filter(lt => (lt.code || '').toLowerCase() !== 'present')
            .map(lt => ({
              value: lt.code,
              text: lt.display_name,
              id: lt.id,
              isPaid: lt.is_paid,
              canBeHalfDay: lt.can_be_half_day,
              carryForwardLimit: lt.carry_forward_limit,
              encashmentAllowed: lt.encashment_allowed
            }))
          
          this.leaveTypeOptions.unshift(
            { value: 'Present', text: 'Present (Full Day)', isPaid: true, canBeHalfDay: false }
          )
        }
      } catch (err) {
        console.error('[loadLeaveTypesFromDB] Error:', err)
        this.leaveTypesFromDB = []
      }
    },
    async loadLeaveTypeOptions(date = null) {
      const targetEmpId = this.currentViewEmpId || this.emp_id
      try {
        const res = await attendanceService.getLeaveTypeOptions(targetEmpId, date)
        if (res && res.status === 'OK' && res.data) {
          let options = []
          
          // Convert API response to options format
          if (Array.isArray(res.data)) {
            options = res.data.map(option => {
              if (typeof option === 'string') {
                return { value: option, text: option }
              }
              return option
            })
          } else if (res.data.options) {
            options = res.data.options
          } else if (res.data.leaveTypes) {
            options = res.data.leaveTypes.map(lt => ({
              value: lt,
              text: lt
            }))
          }
          
          // Enrich options with id from leaveTypesFromDB if not present
          this.apiLeaveTypeOptions = options.map(opt => {
            if (opt.id) return opt // Already has id
            
            // Try to find matching leave type in leaveTypesFromDB
            const foundType = this.leaveTypesFromDB.find(lt => 
              lt.code === opt.value || 
              lt.display_name === opt.value ||
              lt.display_name === opt.text ||
              lt.code.toUpperCase() === (opt.value || '').toUpperCase()
            )
            
            if (foundType) {
              return {
                ...opt,
                id: foundType.id,
                code: foundType.code,
                isPaid: foundType.is_paid,
                canBeHalfDay: foundType.can_be_half_day
              }
            }
            return opt
          })
        }
      } catch (err) {
        console.error('Error loading leave type options:', err)
        this.apiLeaveTypeOptions = []
      }
    },
    async loadLeaveStats(empId = null) {
      const targetEmpId = empId || this.currentViewEmpId || this.emp_id
      try {
        const res = await attendanceService.getLeaveStats(targetEmpId)
        if (res && res.status === 'OK' && res.data) {
          this.leaveStats = {
            ...this.leaveStats,
            ...res.data
          }
        } else {
          console.warn('Leave stats API response not OK or missing data:', res)
        }
      } catch (err) {
        console.error('Error loading leave stats:', err)
      }
    },
    async loadMyLeaves(empId = null) {
      this.loadingLeaves = true
      const targetEmpId = empId || this.currentViewEmpId || this.emp_id
      try {
        // Use active filters if available (backend will use yearStart if dates not provided)
        const filters = this.activeFilters || {}
        const res = await attendanceService.getMyLeaves(targetEmpId, filters)
        if (res && res.status === 'OK') {
          this.myLeaves = res.data || []
        }
      } catch (err) {
        console.error('Error loading leaves:', err)
        this.myLeaves = []
      } finally {
        this.loadingLeaves = false
      }
    },
    async loadBiometricData(empId = null) {
      try {
        const targetEmpId = empId || this.currentViewEmpId || this.emp_id
        // Get empcode for the target employee
        let targetEmpcode = this.empcode || this.emp_id
        if (targetEmpId !== this.emp_id) {
          // Find team member's empcode
          const teamMember = this.teamMembers.find(m => m.emp_id === targetEmpId)
          if (teamMember) {
            targetEmpcode = teamMember.empcode || teamMember.emp_id
          } else {
            targetEmpcode = targetEmpId
          }
        }
        if (!targetEmpcode) return
        
        const monthStart = moment().month(this.selectedMonth).year(this.selectedYear).startOf('month').format('YYYY-MM-DD')
        const monthEnd = moment().month(this.selectedMonth).year(this.selectedYear).endOf('month').format('YYYY-MM-DD')
        const res = await attendanceService.getBiometricByDateRange(monthStart, monthEnd, targetEmpcode)
        if (res && res.status === 'OK') {
          this.biometricData = res.data || []
        }
      } catch (err) {
        console.error('Error loading biometric data:', err)
        this.biometricData = []
      }
    },
    async loadAttendanceData(empId = null) {
      try {
        const targetEmpId = empId || this.currentViewEmpId || this.emp_id
        const monthStart = moment().month(this.selectedMonth).year(this.selectedYear).startOf('month').format('YYYY-MM-DD')
        const monthEnd = moment().month(this.selectedMonth).year(this.selectedYear).endOf('month').format('YYYY-MM-DD')
        // Get attendance data (punch-data) for the month using date range
        const res = await attendanceService.getPunchAttendanceByDateRange(monthStart, monthEnd, targetEmpId)
        if (res && res.status === 'OK') {
          // Use attendance data directly from API - it contains worktime field
          this.attendanceData = res.data || []
        }
      } catch (err) {
        console.error('Error loading attendance data:', err)
        this.attendanceData = []
      }
    },
    async loadHolidays(force = false) {
      try {
        const signal = this.abortController ? this.abortController.signal : null
        const options = force ? { force: true, signal } : signal
        // If the attendanceService.getHolidayList API supports a 'force' argument, pass it; else fallback to previous.
        const res = await attendanceService.getHolidayList(options)
        if (res && res.status === 'OK') {
          this.holidays = res.data || []
        } else if (res && res.data) {
          // Handle case where data is directly in res.data
          this.holidays = Array.isArray(res.data) ? res.data : []
        }
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          return // Request was cancelled, ignore
        }
        console.error('Error loading holidays:', err)
        this.holidays = []
      }
    },
    isHolidayDate(dateStr) {
      // Helper method to check if a date is a holiday
      if (!this.holidays || this.holidays.length === 0) {
        return false
      }
      
      // Normalize the input date to YYYY-MM-DD format
      const normalizedDate = moment(dateStr).format('YYYY-MM-DD')
      
      // Check against all holidays
      return this.holidays.some(h => {
        const holidayDate = h.holiday_date || h.date
        if (!holidayDate) return false
        // Normalize holiday date to YYYY-MM-DD format for comparison
        const holidayDateNormalized = moment(holidayDate).format('YYYY-MM-DD')
        return holidayDateNormalized === normalizedDate
      })
    },
    async loadCompanyWfhDays() {
      // Mirrors loadHolidays() above exactly — see
      // attendance-service.js#getCompanyWfhDays for the backend-gap ->
      // localStorage fallback this reads from.
      try {
        const signal = this.abortController ? this.abortController.signal : null
        const res = await attendanceService.getCompanyWfhDays(signal)
        if (res && res.status === 'OK') {
          this.companyWfhDays = res.data || []
        } else if (res && res.data) {
          this.companyWfhDays = Array.isArray(res.data) ? res.data : []
        }
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          return // Request was cancelled, ignore
        }
        console.error('Error loading company-wide WFH days:', err)
        this.companyWfhDays = []
      }
    },
    // Mirrors isHolidayDate() — pure client-side date (+ location) membership
    // check against the loaded companyWfhDays list. Used by calendarWeeks()
    // (API + legacy paths), regularization filters, and punch-exemption UI.
    // Deliberately does NOT create/check any per-employee attendance or leave
    // record — this is a date-level lookup only (no personal WFH balance).
    //
    // Location: when a day is scoped to pune/delhi only, match against the
    // viewing employee's site when known; if site is unknown, treat any
    // declaration for that date as covering (same leniency as holidays).
    isCompanyWfhDate(dateStr) {
      if (!this.companyWfhDays || this.companyWfhDays.length === 0) {
        return false
      }
      const normalizedDate = moment(dateStr).format('YYYY-MM-DD')
      const empLocation = this.getEmployeeSiteForCompanyWfh()
      return this.companyWfhDays.some(d => {
        if (!d.date) return false
        if (moment(d.date).format('YYYY-MM-DD') !== normalizedDate) return false
        const puneYes = String(d.pune || '').toUpperCase() === 'YES'
        const delhiYes = String(d.delhi || '').toUpperCase() === 'YES'
        // Both / neither-flagged => all locations
        if ((puneYes && delhiYes) || (!puneYes && !delhiYes)) return true
        if (!empLocation) return true
        if (empLocation === 'pune') return puneYes
        if (empLocation === 'delhi') return delhiYes
        return true
      })
    },
    // Best-effort site for Company-Wide WFH location scoping (pune | delhi | null).
    getEmployeeSiteForCompanyWfh() {
      try {
        const user = JSON.parse(localStorage.getItem('userData') || '{}')
        const raw = (
          user.location || user.city || user.office_location ||
          user.base_location || user.work_location || user.site || ''
        ).toString().toLowerCase()
        if (raw.includes('pune')) return 'pune'
        if (raw.includes('delhi')) return 'delhi'
      } catch (e) { /* ignore */ }
      return null
    },
    // DEPRECATED: This method is no longer used for regularization cases
    // Regularization cases are now fetched directly from the API endpoint
    // Keeping for potential future use or reference
    // Optimized helper: Create date map from punch, biometric and attendance data
    createDateMap(punchData, biometricData, attendanceData = [], monthStart = null, monthEnd = null) {
      const dateMap = new Map()
      
      // First, add punch-data (primary source)
      if (Array.isArray(punchData)) {
        punchData.forEach(p => {
          const dateStr = p.date
          if (dateStr) {
            dateMap.set(dateStr, {
              punchData: p,
              biometric: null,
              attendance: null,
            })
          }
        })
      }
      
      // Then, add/merge biometric data (secondary source)
      if (Array.isArray(biometricData)) {
        biometricData.forEach(b => {
          const dateStr = b.datestring || b.date
          if (dateStr) {
            if (dateMap.has(dateStr)) {
              dateMap.get(dateStr).biometric = b
            } else {
              // If no punch-data exists, still add biometric for regularization check
              dateMap.set(dateStr, {
                punchData: null,
                biometric: b,
                attendance: null,
              })
            }
          }
        })
      }

      // Add/merge attendance records (from attendance-stats or leave table)
      if (Array.isArray(attendanceData)) {
        attendanceData.forEach(a => {
          const dateStr = a.date
          if (dateStr) {
            if (dateMap.has(dateStr)) {
              dateMap.get(dateStr).attendance = a
            } else {
              dateMap.set(dateStr, {
                punchData: null,
                biometric: null,
                attendance: a,
              })
            }
          }
        })
      }
      
      // Add all dates in month range that don't have data (for "not marked" detection)
      if (monthStart && monthEnd) {
        const startMoment = moment(monthStart)
        const endMoment = moment(monthEnd)
        const today = moment()
        let currentDate = startMoment.clone()
        
        while (currentDate.isSameOrBefore(endMoment, 'day')) {
          const dateStr = currentDate.format('YYYY-MM-DD')
          
          // Only add dates up to today (can't regularize future dates)
          if (currentDate.isSameOrBefore(today, 'day')) {
            // Exclude weekends
            const isWeekend = currentDate.day() === 0 || currentDate.day() === 6
            // Exclude holidays (punch not required). Company-Wide WFH still requires punch.
            const isHoliday = this.isHolidayDate(dateStr)
            
            // If date is not in map and not weekend/holiday, add it with no data
            if (!dateMap.has(dateStr) && !isWeekend && !isHoliday) {
              dateMap.set(dateStr, {
                punchData: null,
                biometric: null,
                attendance: null,
              })
            }
          }
          
          currentDate.add(1, 'day')
        }
      }
      
      return dateMap
    },
    // Optimized helper: Determine data source status
    determineDataSourceStatus(data) {
      const hasPunchData = !!data.punchData
      const hasBiometric = !!data.biometric
      
      if (!hasPunchData && !hasBiometric) {
        return 'Both Missing'
      }
      if (!hasPunchData && hasBiometric) {
        return 'Punch Data Missing'
      }
      if (hasPunchData && !hasBiometric) {
        return 'Biometric Missing'
      }
      
      // Both exist, check which one has the issue
      const punchIntime = data.punchData?.intime || null
      const punchOuttime = data.punchData?.outtime || null
      const biometricIntime = data.biometric?.intime || null
      const biometricOuttime = data.biometric?.outtime || null
      
      const punchHasInTime = punchIntime && punchIntime !== '0:00:00' && punchIntime !== '00:00:00' && punchIntime.trim() !== ''
      const punchHasOutTime = punchOuttime && punchOuttime !== '0:00:00' && punchOuttime !== '00:00:00' && punchOuttime.trim() !== ''
      const bioHasInTime = biometricIntime && biometricIntime !== '0:00:00' && biometricIntime !== '00:00:00' && biometricIntime.trim() !== ''
      const bioHasOutTime = biometricOuttime && biometricOuttime !== '0:00:00' && biometricOuttime !== '00:00:00' && biometricOuttime.trim() !== ''
      
      if (!punchHasInTime && !punchHasOutTime && (bioHasInTime || bioHasOutTime)) {
        return 'Punch Data Issue'
      }
      if ((punchHasInTime || punchHasOutTime) && !bioHasInTime && !bioHasOutTime) {
        return 'Biometric Issue'
      }
      return 'Both Present'
    },
    // Optimized helper: Check if date has half-day leave
    hasHalfDayLeaveForDate(dateStr, leavesArray) {
      if (!Array.isArray(leavesArray) || leavesArray.length === 0) {
        return false
      }
      
      const dateMoment = moment(dateStr)
      return leavesArray.some(leave => {
        if (!leave.from_date || !leave.to_date) return false
        const from = moment(leave.from_date)
        const to = moment(leave.to_date)
        const isInLeaveRange = dateMoment.isSameOrAfter(from, 'day') && dateMoment.isSameOrBefore(to, 'day')
        const isHalfDay = leave.leave_type === '1st Half Day' || leave.leave_type === '2nd Half Day' || leave.half_day === true
        const isApproved = leave.status === 'Approved' || leave.status === 'Manager Approved - Pending HR'
        return isInLeaveRange && isHalfDay && isApproved
      })
    },
    // DEPRECATED: This method is no longer used for regularization cases
    // Regularization cases are now fetched directly from the API endpoint
    // Keeping for potential future use or reference
    // Simplified helper: Process regularization issues based on missing punch data OR existing unapproved regularization
    processRegularizationIssues(dateMap, empId, holidays, today, options = {}) {
      const discrepancies = []
      const todayStr = today || moment().format('YYYY-MM-DD')
      
      dateMap.forEach((data, dateStr) => {
        const dateMoment = moment(dateStr)
        
        // Exclude weekends
        const isWeekend = dateMoment.day() === 0 || dateMoment.day() === 6
        if (isWeekend) return
        
        // Exclude holidays (punch not required). Company-Wide WFH still requires punch.
        const isHoliday = this.isHolidayDate(dateStr)
        if (isHoliday) return
        
        // Strictly exclude Today from discrepancies
        if (dateStr === todayStr) return

        // --- Data checks ---
        const punchIntime = data.punchData?.intime || null
        const punchOuttime = data.punchData?.outtime || null
        const hasPunchIn = punchIntime && punchIntime !== '0:00:00' && punchIntime !== '00:00:00' && punchIntime.trim() !== ''
        const hasPunchOut = punchOuttime && punchOuttime !== '0:00:00' && punchOuttime !== '00:00:00' && punchOuttime.trim() !== ''
        const hasPunchRecord = !!data.punchData

        const attendance = data.attendance
        const isRegularization = attendance?.source_type === 'REGULARIZATION' || (attendance?.attendance || '').toLowerCase().trim() === 'attendance-regularization'
        const regStatus = (attendance?.status || attendance?.regularization_status || '').toLowerCase().trim()
        const isApproved = regStatus === 'approved'
        const isPendingOrRaised = regStatus !== '' && !isApproved
        
        const worktime = data.punchData?.worktime || data.biometric?.worktime || 'N/A'
        const dataSourceStatus = this.determineDataSourceStatus(data)

        const isPunchMissingOrIncomplete = !hasPunchRecord || !hasPunchIn || !hasPunchOut

        // --- SIMPLIFIED RULE 2: Existing non-approved regularization ---
        // (This covers entries already in attendances table but not yet final)
        const isUnapprovedRegularization = isRegularization && !isApproved

          if (isPunchMissingOrIncomplete || isUnapprovedRegularization) {
            // Get best available times from all sources (priority: punch > biometric > attendance)
            const bestIntime = punchIntime || data.biometric?.intime || attendance?.intime || null
            const bestOuttime = punchOuttime || data.biometric?.outtime || attendance?.outtime || null
            
            discrepancies.push({
              datestring: dateStr,
              issue_type: isUnapprovedRegularization ? 'Existing Regularization' : 
                        (!hasPunchRecord ? 'No Punch Record' : (!hasPunchIn ? 'Punch IN missing' : 'Punch OUT missing')),
              worktime: worktime,
              emp_id: empId,
              intime: bestIntime,
              outtime: bestOuttime,
              // Include raw data objects for better access in modal
              punchData: data.punchData || null,
              biometric: data.biometric || null,
              biometricRecord: data.biometric ? {
                intime: data.biometric.intime,
                outtime: data.biometric.outtime,
                worktime: data.biometric.worktime
              } : null,
              attendance: attendance || null,
              data_source_status: dataSourceStatus,
              has_punch_data: hasPunchRecord,
              has_biometric: !!data.biometric,
              status: isPendingOrRaised ? (attendance?.status || 'Pending') : 'Pending',
              regularization_status: isPendingOrRaised ? (attendance?.regularization_status || 'Pending') : 'Pending',
              id: attendance?.id,
              attendance_id: attendance?.id
            })
          }
      })
      
      return discrepancies
    },
    getIssueTypeFromRecord(record) {
      // Determine issue type from record data
      const hasInTime = record.intime && record.intime !== '0:00:00' && record.intime !== '00:00:00' && record.intime.trim() !== ''
      const hasOutTime = record.outtime && record.outtime !== '0:00:00' && record.outtime !== '00:00:00' && record.outtime.trim() !== ''
      
      // Check remarks first
      if (record.remarks) {
        if (record.remarks.toLowerCase().includes('in time') || record.remarks.toLowerCase().includes('intime')) {
          return 'IN Time is missing'
        }
        if (record.remarks.toLowerCase().includes('out time') || record.remarks.toLowerCase().includes('outtime')) {
          return 'Out Time is missing'
        }
        if (record.remarks.toLowerCase().includes('less work') || record.remarks.toLowerCase().includes('work hours')) {
          return 'Less Work Hours'
        }
      }
      
      // If no remarks, determine from actual data
      if (!hasInTime && hasOutTime) {
        return 'IN Time is missing'
      }
      if (hasInTime && !hasOutTime) {
        return 'Out Time is missing'
      }
      if (hasInTime && hasOutTime) {
        // Check work hours
        const workHours = this.parseWorkTime(record.worktime || '0:00:00')
        if (workHours < 6) {
          return 'Less Work Hours'
        }
        return 'Regularization Required'
      }
      if (!hasInTime && !hasOutTime) {
        return 'NO Punch Data found'
      }
      
      // Default fallback
      if (record.attendance === 'attendance-regularization' || record.source_type === 'REGULARIZATION') {
        return 'Regularization Required'
      }
      return 'NO Punch Data found'
    },
    async checkManagerStatus() {
      if (!this.mgr_id) return
      
      try {
        // Get manager info
        const managerRes = await userService.getUserDetail(this.mgr_id)
        if (managerRes && managerRes.status === 'OK') {
          this.managerInfo = managerRes.data
          
          // Check if manager is on leave
          const managerLeavesRes = await attendanceService.getMyLeaves(this.mgr_id)
          if (managerLeavesRes && managerLeavesRes.status === 'OK') {
            const today = moment().format('YYYY-MM-DD')
            const managerOnLeave = managerLeavesRes.data.some(leave => {
              const from = moment(leave.from_date)
              const to = moment(leave.to_date)
              const todayMoment = moment(today)
              return (leave.status === 'Approved' || leave.status === 'Manager Approved - Pending HR') &&
                     todayMoment.isSameOrAfter(from, 'day') && todayMoment.isSameOrBefore(to, 'day')
            })
            
            this.managerAbsent = managerOnLeave
            
            // If manager is absent, find alternate approver (HR or higher manager)
            if (this.managerAbsent) {
              await this.findAlternateApprover()
            }
          }
        }
      } catch (err) {
        console.error('Error checking manager status:', err)
      }
    },
    async findAlternateApprover() {
      try {
        // Try to find HR users
        const allUsersRes = await userService.getAllUsers()
        if (allUsersRes && allUsersRes.status === 'OK') {
          const hrUsers = allUsersRes.data.filter(user => 
            user.roles && user.roles.some(role => 
              role === 'HR' || role === 'HR-DIRECTOR' || role === 'hr'
            )
          )
          
          if (hrUsers.length > 0) {
            this.alternateApprover = hrUsers[0]
          }
        }
      } catch (err) {
        console.error('Error finding alternate approver:', err)
      }
    },
    // NOTE: handleApplyLeaveLegacy has been removed - all validation is now done by backend
    async handleApplyWFH() {
      // Basic form validation only - backend handles all eligibility checks
      if (!this.wfhForm.date) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Please select a date',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        }, { timeout: 3000 })
        return
      }
      
      // Determine target employee ID — prefer wfhForm.selected_employee (set by dropdown),
      // fall back to selectedTeamMember (pre-filled from team view), then self
      const targetEmpId = (this.wfhForm.selected_employee && this.wfhForm.selected_employee !== this.emp_id)
        ? this.wfhForm.selected_employee
        : (this.viewMode === 'team' && this.selectedTeamMember && this.selectedTeamMember !== this.emp_id)
          ? this.selectedTeamMember
          : this.emp_id

      try {
        // Prepare WFH data - backend handles all validation (allow_ofc, WFH limits, probation routing)
        const wfhData = {
          emp_id: targetEmpId,
          from_date: this.wfhForm.date,
          to_date: this.wfhForm.date,
          leave_type: 'Work From Home',
          comment: this.wfhForm.comment,
          partial_wfh: this.wfhForm.partial_wfh === true,
          is_half_day: this.wfhForm.is_half_day === true,
          half_day_type: this.wfhForm.is_half_day === true ? this.wfhForm.half_day_type : null,
          applied_by: targetEmpId !== this.emp_id ? this.emp_id : null,
          // WFH must never be overridden to Probation Leave by the backend
          preserve_leave_type: true,
        }
        // Hard-coded safety override: WFH submissions always send 'Work From Home' regardless
        // of probation status. This prevents the backend probation override from firing.
        wfhData.leave_type = 'Work From Home'
        
        // Check if editing (updating) or creating new
        const isEditing = !!this.wfhForm.id
        if (isEditing) {
          wfhData.id = this.wfhForm.id
        }
        
        // Call update or apply based on whether we're editing
        const res = isEditing
          ? await attendanceService.updateLeave(wfhData)
          : await attendanceService.applyLeave(wfhData)
        
        if (res && res.status === 'OK') {
          const isEditing = !!this.wfhForm.id
          const successMessage = isEditing
            ? 'Work From Home updated successfully!'
            : targetEmpId !== this.emp_id 
              ? `WFH applied successfully on behalf of ${this.getUserName(targetEmpId)}!`
              : 'WFH applied successfully!'
          
          // Show warnings if any
          if (res.data && res.data.warnings && res.data.warnings.length > 0) {
            res.data.warnings.forEach(warning => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: warning.message || warning,
                  icon: 'AlertCircleIcon',
                  variant: 'warning',
                },
              }, { timeout: 4000 })
            })
          }
          
          this.$toast({
            component: ToastificationContent,
            props: {
              title: successMessage,
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          }, { timeout: 3000 })
          const wfhModalRef = this.$refs.wfhModal
          if (wfhModalRef && typeof wfhModalRef.hide === 'function') {
            wfhModalRef.hide()
          } else {
            this.$root.$emit('bv::hide::modal', 'modal-apply-wfh')
          }
          await this.loadData()
          
        } else if (res && res.status === 'VALIDATION_ERROR') {
          // Backend validation failed - show all errors
          const errors = res.errors || []
          
          if (errors.length > 0) {
            errors.forEach((error, index) => {
              setTimeout(() => {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.message || error,
                    icon: 'AlertCircleIcon',
                    variant: 'danger',
                    text: error.details || '',
                  },
                }, { timeout: 5000 })
              }, index * 100)
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: res.message || 'Validation failed',
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            }, { timeout: 5000 })
          }
          
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res?.message || 'Failed to apply WFH',
              icon: 'XCircleIcon',
              variant: 'danger',
            },
          }, { timeout: 3000 })
        }
      } catch (err) {
        console.error('Error applying WFH:', err)
        
        // Check if error response has validation errors
        if (err.response && err.response.data) {
          const errorData = err.response.data
          
          // Handle validation errors array
          if (errorData.errors && Array.isArray(errorData.errors)) {
            errorData.errors.forEach((error, index) => {
              setTimeout(() => {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.message || error,
                    icon: 'AlertCircleIcon',
                    variant: 'danger',
                    text: error.details || '',
                  },
                }, { timeout: 5000 })
              }, index * 100)
            })
            return
          }
          
          // Handle single error message
          if (errorData.message) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: errorData.message,
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            }, { timeout: 4000 })
            return
          }
        }
        
        // Fallback error message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'An error occurred while applying WFH',
            icon: 'XCircleIcon',
            variant: 'danger',
          },
          }, { timeout: 3000 })
      }
    },
    handleDateClick(day) {
      // Compact calendar handles apply UX via day popover + apply strip.
      // Keep hook for non-apply day interactions / future detail views.
      if (!day || day.isHoliday || day.isWeekOff || day.isCompanyWfh) return
    },
    /**
     * Prefill leave dates after opening the existing Apply Leave modal.
     * @param {string|null} date YYYY-MM-DD from calendar day click (optional)
     */
    async onCalendarApplyLeave(date = null) {
      await this.openApplyLeaveModal()
      if (!date) return
      this.leaveForm.from_date = date
      this.leaveForm.to_date = date
      const selectedDate = moment(date)
      this.calendarMonth = selectedDate.month()
      this.calendarYear = selectedDate.year()
      if (typeof this.loadLeaveTypeOptions === 'function') {
        await this.loadLeaveTypeOptions(date)
      }
    },
    /**
     * Prefill WFH date after opening the existing Apply WFH modal.
     * openWFHModal may return early when WFH is not allowed — leave form unset then.
     */
    async onCalendarApplyWfh(date = null) {
      await this.openWFHModal()
      if (!date) return
      // Modal may have aborted (eligibility); only prefill if form is active
      if (!this.wfhForm) return
      this.wfhForm.date = date
      const selectedDate = moment(date)
      this.wfhCalendarMonth = selectedDate.month()
      this.wfhCalendarYear = selectedDate.year()
    },
    async onCalendarApplyCompOff(date = null) {
      await this.openApplyCompOffModal()
      if (!date) return
      this.compOffForm.date = date
      const selectedDate = moment(date)
      this.compOffCalendarMonth = selectedDate.month()
      this.compOffCalendarYear = selectedDate.year()
    },
    async onCalendarRequestCompOff(date = null) {
      // Request Comp Off is for today and past work dates only — never future
      if (date && moment(date).startOf('day').isAfter(moment().startOf('day'), 'day')) {
        return
      }
      await this.openRequestCompOffBalanceModal()
      // Request CO uses a past/today work date; only prefill when a day was chosen
      if (!date) return
      this.compOffBalanceRequestForm.work_date = date
      const selectedDate = moment(date)
      this.compOffBalanceWorkCalendarMonth = selectedDate.month()
      this.compOffBalanceWorkCalendarYear = selectedDate.year()
    },
    async openWFHModal() {
      this.resetWFHForm()
      
      // Determine target employee ID
      const targetEmpId = (this.viewMode === 'team' && this.selectedTeamMember && this.canManageLeaves && this.selectedTeamMember !== this.emp_id) 
        ? this.selectedTeamMember 
        : this.emp_id
      
      // If in team view and a team member is selected, pre-fill the dropdown
      if (this.viewMode === 'team' && this.selectedTeamMember && this.selectedTeamMember !== this.emp_id) {
        this.wfhForm.selected_employee = this.selectedTeamMember
      }
      
      // Load punch config to check if permanent WFH and allow_ofc
      try {
        const configRes = await attendanceService.getPunchConfig(targetEmpId)
        if (configRes && configRes.status === 'OK' && configRes.data) {
          this.punchConfig = configRes.data
          const metadata = configRes.data.metadata?.metadata || configRes.data.metadata || {}
          // Check for permanent WFH (allow_wfm)
          this.isPermanentWFH = metadata.allow_wfm === 'TRUE' || metadata.allow_wfm === true
          // Check allow_ofc - if true, employee can apply for WFH (subject to 2 per quarter limit)
          // If false or not set, employee cannot apply for WFH
          const allowOfc = metadata.allow_ofc === 'TRUE' || metadata.allow_ofc === true
          
          // If not permanent WFH and allow_ofc is false, show error (only if applying for self)
          if (!this.isPermanentWFH && !allowOfc && targetEmpId === this.emp_id) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'WFH Not Allowed',
                icon: 'AlertCircleIcon',
                variant: 'warning',
                text: 'You are not allowed to apply for Work From Home. Please contact HR for more information.',
              },
            }, { timeout: 5000 })
            return // Don't open modal
          }
        } else {
          this.isPermanentWFH = false
          // If no config found and applying for self, don't allow WFH
          if (targetEmpId === this.emp_id) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Configuration Error',
                icon: 'AlertCircleIcon',
                variant: 'warning',
                text: 'Unable to verify WFH eligibility. Please contact HR.',
              },
            }, { timeout: 5000 })
            return
          }
        }
      } catch (err) {
        console.error('Error fetching punch config:', err)
        this.isPermanentWFH = false
        if (targetEmpId === this.emp_id) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: 'Failed to load WFH configuration. Please try again.',
            },
          }, { timeout: 3000 })
          return
        }
      }
      
      // Calculate current quarter WFH count (only if not permanent WFH and applying for self)
      if (targetEmpId === this.emp_id) {
        if (!this.isPermanentWFH) {
          await this.calculateQuarterWFHCount(targetEmpId)
        } else {
          this.currentQuarterWFHCount = 0
        }
      }
      
      // If an employee is pre-selected, load their WFH info
      if (this.wfhForm.selected_employee) {
        await this.handleWFHEmployeeChange()
      }
      
      this.$nextTick(() => {
        const wfhModal = this.$refs.wfhModal
        if (wfhModal && typeof wfhModal.show === 'function') {
          wfhModal.show()
        } else {
          this.$root.$emit('bv::show::modal', 'modal-apply-wfh')
        }
      })
    },
    async calculateQuarterWFHCount(empId) {
      try {
        // Get current quarter dates (Q1: Jan-Mar, Q2: Apr-Jun, Q3: Jul-Sep, Q4: Oct-Dec)
        const now = moment()
        const quarterStart = moment(now).startOf('quarter')
        const quarterEnd = moment(now).endOf('quarter')
        
        // Get approved WFH leaves from leave_applications table (not attendance table)
        // This ensures we only count approved WFH and previous quarter's unused WFH lapses
        const leavesRes = await attendanceService.getMyLeaves(empId)
        
        if (leavesRes && leavesRes.status === 'OK' && leavesRes.data) {
          // Filter for approved WFH leaves within current quarter
          const wfhCount = leavesRes.data.filter(leave => {
            // Check if it's WFH type
            const leaveType = (leave.leave_type || '').toLowerCase()
            const isWFH = leaveType === 'work from home' || leaveType === 'wfh'
            
            if (!isWFH) return false
            
            // Check if status is approved
            const status = (leave.status || '').toLowerCase()
            if (status !== 'approved') return false
            
            // Check if leave falls within current quarter
            const leaveFromDate = moment(leave.from_date).startOf('day')
            const leaveToDate = moment(leave.to_date).startOf('day')
            
            // Check if leave overlaps with current quarter
            return (leaveFromDate.isSameOrAfter(quarterStart, 'day') && leaveFromDate.isSameOrBefore(quarterEnd, 'day')) ||
                   (leaveToDate.isSameOrAfter(quarterStart, 'day') && leaveToDate.isSameOrBefore(quarterEnd, 'day')) ||
                   (leaveFromDate.isBefore(quarterStart, 'day') && leaveToDate.isAfter(quarterEnd, 'day'))
          }).length
          
          this.currentQuarterWFHCount = wfhCount
        } else {
          this.currentQuarterWFHCount = 0
        }
      } catch (err) {
        console.error('Error calculating quarter WFH count:', err)
        this.currentQuarterWFHCount = 0
      }
    },
    navigateWFHCalendarMonth(direction) {
      // direction: -1 for previous, 1 for next
      if (direction === -1) {
        if (this.wfhCalendarMonth === 0) {
          this.wfhCalendarMonth = 11
          this.wfhCalendarYear--
        } else {
          this.wfhCalendarMonth--
        }
      } else if (direction === 1) {
        if (this.wfhCalendarMonth === 11) {
          this.wfhCalendarMonth = 0
          this.wfhCalendarYear++
        } else {
          this.wfhCalendarMonth++
        }
      }
    },
    selectWFHCalendarDate(day) {
      // Navigate to the month if clicking on a date from another month
      if (!day.isCurrentMonth) {
        const clickedDate = moment(day.dateStr)
        this.wfhCalendarMonth = clickedDate.month()
        this.wfhCalendarYear = clickedDate.year()
        // After navigating, select the date
        this.$nextTick(() => {
          this.selectWFHCalendarDate({ ...day, isCurrentMonth: true })
        })
        return
      }
      
      // For WFH, set the selected date
      this.wfhForm.date = day.dateStr
      // Sync calendar to show the selected date
      const selectedDate = moment(day.dateStr)
      this.wfhCalendarMonth = selectedDate.month()
      this.wfhCalendarYear = selectedDate.year()
    },
    getWFHCalendarDayClasses(day) {
      return {
        'other-month': !day.isCurrentMonth,
        'selected': day.isSelected,
        'today': day.isToday,
        'disabled': day.isDisabled,
        'past-date': day.isPast,
        'already-applied': day.isAlreadyApplied,
        'holiday-disabled': day.isHoliday,
      }
    },
    clearWFHDateSelection() {
      this.wfhForm.date = null
      this.$forceUpdate()
    },
    // ===================================================================
    // Compensatory Off Methods - Two Separate Workflows
    // ===================================================================
    
    // WORKFLOW 1: Request CO Balance (Ask for balance credit)
    async openRequestCompOffBalanceModal() {
      this.resetCompOffBalanceRequestForm()

      if (this.viewMode === 'team' && this.selectedTeamMember && this.selectedTeamMember !== this.emp_id) {
        this.compOffBalanceRequestForm.selected_employee = this.selectedTeamMember
      }

      this.$bvModal.show('modal-request-compoff-balance')
    },
    
    async handleRequestCompOffBalance(event) {
      // Basic validation
      if (!this.compOffBalanceRequestForm.work_date || !this.compOffBalanceRequestForm.comment) {
        event.preventDefault()
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Please fill all required fields',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        }, { timeout: 3000 })
        return
      }

      // Determine target employee ID
      const targetEmpId = (this.compOffBalanceRequestForm.selected_employee && this.compOffBalanceRequestForm.selected_employee !== this.emp_id)
        ? this.compOffBalanceRequestForm.selected_employee
        : (this.viewMode === 'team' && this.selectedTeamMember && this.selectedTeamMember !== this.emp_id)
          ? this.selectedTeamMember
          : this.emp_id

      try {
        // This goes to backend API for CO balance request
        const requestData = {
          emp_id: targetEmpId,
          work_date: this.compOffBalanceRequestForm.work_date,
          comment: this.compOffBalanceRequestForm.comment,
          requested_by: this.emp_id,
        }

        // Call backend API to create CO balance request (e.g., /api/compoff/request-balance)
        const res = await attendanceService.requestCompOffBalance(requestData)

        if (res && res.status === 'OK') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.message || 'CO Balance Request Submitted',
              icon: 'CheckCircleIcon',
              variant: 'success',
              text: 'Your CO balance request has been sent to HR for approval.',
            },
          }, { timeout: 4000 })

          // Reset form and close modal
          this.resetCompOffBalanceRequestForm()
          this.$bvModal.hide('modal-request-compoff-balance')

          // Reload data
          await this.loadData()
          
        } else if (res && res.status === 'VALIDATION_ERROR') {
          event.preventDefault()
          
          const errors = res.errors || []
          if (errors.length > 0) {
            errors.forEach((error, index) => {
              setTimeout(() => {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.message || error,
                    icon: 'AlertCircleIcon',
                    variant: 'danger',
                  },
                }, { timeout: 5000 })
              }, index * 100)
            })
          }
        } else {
          event.preventDefault()
          
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res?.message || 'Failed to submit request',
              icon: 'XCircleIcon',
              variant: 'danger',
            },
          }, { timeout: 4000 })
        }
      } catch (err) {
        event.preventDefault()
        console.error('Error requesting CO balance:', err)
        
        if (err.response && err.response.data) {
          const errorData = err.response.data
          
          if (errorData.errors && Array.isArray(errorData.errors)) {
            errorData.errors.forEach((error, index) => {
              setTimeout(() => {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.message || error,
                    icon: 'AlertCircleIcon',
                    variant: 'danger',
                  },
                }, { timeout: 5000 })
              }, index * 100)
            })
            return
          }
          
          if (errorData.message) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: errorData.message,
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            }, { timeout: 4000 })
            return
          }
        }
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to Submit Request',
            icon: 'XCircleIcon',
            variant: 'danger',
            text: err.message || 'Please try again later',
          },
        }, { timeout: 4000 })
      }
    },
    
    resetCompOffBalanceRequestForm() {
      this.compOffBalanceRequestForm = {
        work_date: null,
        comment: '',
        selected_employee: null,
      }
      this.compOffBalanceWorkCalendarMonth = moment().month()
      this.compOffBalanceWorkCalendarYear = moment().year()
      this.showCompOffBalanceWorkCalendar = false
    },
    
    // Calendar methods for CO Balance Request work date
    navigateCompOffBalanceWorkCalendarMonth(direction) {
      if (direction === -1) {
        if (this.compOffBalanceWorkCalendarMonth === 0) {
          this.compOffBalanceWorkCalendarMonth = 11
          this.compOffBalanceWorkCalendarYear--
        } else {
          this.compOffBalanceWorkCalendarMonth--
        }
      } else if (direction === 1) {
        if (this.compOffBalanceWorkCalendarMonth === 11) {
          this.compOffBalanceWorkCalendarMonth = 0
          this.compOffBalanceWorkCalendarYear++
        } else {
          this.compOffBalanceWorkCalendarMonth++
        }
      }
    },
    
    selectCompOffBalanceWorkCalendarDate(day) {
      if (!day.isCurrentMonth) {
        const clickedDate = moment(day.dateStr)
        this.compOffBalanceWorkCalendarMonth = clickedDate.month()
        this.compOffBalanceWorkCalendarYear = clickedDate.year()
        this.$nextTick(() => {
          this.selectCompOffBalanceWorkCalendarDate({ ...day, isCurrentMonth: true })
        })
        return
      }
      
      this.compOffBalanceRequestForm.work_date = day.dateStr
      this.showCompOffBalanceWorkCalendar = false
    },
    
    clearCompOffBalanceWorkDateSelection() {
      this.compOffBalanceRequestForm.work_date = null
      this.$forceUpdate()
    },
    
    getCompOffBalanceWorkCalendarDayClasses(day) {
      return {
        'other-month': !day.isCurrentMonth,
        'selected-start': day.dateStr === this.compOffBalanceRequestForm.work_date,
        'today': day.isToday,
        'disabled': day.isDisabled,
        'past-date': day.isPast && !day.isToday,
      }
    },
    
    // View CO Balance Requests
    async showCompOffBalanceRequestsModal() {
      this.$bvModal.hide('modal-request-compoff-balance')
      this.$bvModal.show('modal-view-compoff-requests')
      await this.fetchCompOffBalanceRequests()
    },
    
    async fetchCompOffBalanceRequests() {
      this.loadingCompOffRequests = true
      try {
        // Call backend API to get CO balance requests - uses getMyLeaves with filter
        const res = await attendanceService.getMyCompOffBalanceRequests(this.emp_id)
        if (res && res.status === 'OK') {
          this.compOffBalanceRequests = res.data || []
        }
      } catch (err) {
        console.error('Error fetching CO balance requests:', err)
        this.compOffBalanceRequests = []
      } finally {
        this.loadingCompOffRequests = false
      }
    },

    viewCompOffRequestDetail(item) {
      this.selectedCompOffRequest = item
      this.$bvModal.show('modal-co-request-detail')
    },

    parseCoRequestReason(comment) {
      if (!comment) return ''
      // Strip "CO Balance Request - Work Date: YYYY-MM-DD. " prefix to expose just the user's reason
      const match = comment.match(/^CO Balance Request - Work Date: \d{4}-\d{2}-\d{2}\.\s*(.+)$/s)
      return match ? match[1].trim() : comment
    },

    async openApplyCompOffModal() {
      this.resetCompOffForm()

      // If in team view and a team member is selected, pre-fill the employee dropdown
      if (this.viewMode === 'team' && this.selectedTeamMember && this.selectedTeamMember !== this.emp_id) {
        this.compOffForm.selected_employee = this.selectedTeamMember
      }

      this.$bvModal.show('modal-apply-compoff')
    },
    
    async handleApplyCompOff(event) {
      event.preventDefault()
      // Check if CO balance is 0
      if (this.displayedCoBalance === 0) {
        event.preventDefault()
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Insufficient CO Balance',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'You do not have any CO balance. Please request CO balance first.',
          },
        }, { timeout: 4000 })
        return
      }
      
      // Basic validation
      if (!this.compOffForm.date || !this.compOffForm.comment) {
        event.preventDefault()
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Please fill all required fields',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        }, { timeout: 3000 })
        return
      }

      // Determine target employee ID
      const targetEmpId = (this.compOffForm.selected_employee && this.compOffForm.selected_employee !== this.emp_id)
        ? this.compOffForm.selected_employee
        : (this.viewMode === 'team' && this.selectedTeamMember && this.selectedTeamMember !== this.emp_id)
          ? this.selectedTeamMember
          : this.emp_id

      try {
        const compOffData = {
          emp_id: targetEmpId,
          from_date: this.compOffForm.date,
          to_date: this.compOffForm.date,
          leave_type: 'CO', // Compensatory Off code
          comment: this.compOffForm.comment,
          applied_by: targetEmpId !== this.emp_id ? this.emp_id : null,
          // Compensatory Off must never be overridden to Probation Leave by the backend
          preserve_leave_type: true,
        }
        
        // Check if editing
        const isEditing = !!this.compOffForm.id
        if (isEditing) {
          compOffData.id = this.compOffForm.id
        }

        // Send to backend - will use existing balance
        const res = isEditing 
          ? await attendanceService.updateLeave(compOffData)
          : await attendanceService.applyLeave(compOffData)

        if (res && res.status === 'OK') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.message || (isEditing ? 'Compensatory Off Updated' : 'Compensatory Off Applied'),
              icon: 'CheckCircleIcon',
              variant: 'success',
              text: 'Balance will be deducted after approval.',
            },
          }, { timeout: 4000 })

          // Reset form and close modal
          this.resetCompOffForm()
          this.$bvModal.hide('modal-apply-compoff')

          // Reload data
          await this.loadData()
          
        } else if (res && res.status === 'VALIDATION_ERROR') {
          event.preventDefault()
          
          const errors = res.errors || []
          if (errors.length > 0) {
            errors.forEach((error, index) => {
              setTimeout(() => {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.message || error,
                    icon: 'AlertCircleIcon',
                    variant: 'danger',
                  },
                }, { timeout: 5000 })
              }, index * 100)
            })
          }
        } else {
          event.preventDefault()
          
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res?.message || 'Failed to apply compensatory off',
              icon: 'XCircleIcon',
              variant: 'danger',
            },
          }, { timeout: 4000 })
        }
      } catch (err) {
        event.preventDefault()
        console.error('Error applying compensatory off:', err)
        
        if (err.response && err.response.data) {
          const errorData = err.response.data
          
          if (errorData.errors && Array.isArray(errorData.errors)) {
            errorData.errors.forEach((error, index) => {
              setTimeout(() => {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.message || error,
                    icon: 'AlertCircleIcon',
                    variant: 'danger',
                  },
                }, { timeout: 5000 })
              }, index * 100)
            })
            return
          }
          
          if (errorData.message) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: errorData.message,
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            }, { timeout: 4000 })
            return
          }
        }
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to Apply Compensatory Off',
            icon: 'XCircleIcon',
            variant: 'danger',
            text: err.message || 'Please try again later',
          },
        }, { timeout: 4000 })
      }
    },
    
    resetCompOffForm() {
      this.compOffForm = {
        id: null,
        date: null,
        comment: '',
        selected_employee: null,
      }
      this.compOffCalendarMonth = moment().month()
      this.compOffCalendarYear = moment().year()
      this.showCompOffDateCalendar = false
    },
    
    // Comp Off Calendar Helper Methods
    navigateCompOffCalendarMonth(direction) {
      if (direction === -1) {
        if (this.compOffCalendarMonth === 0) {
          this.compOffCalendarMonth = 11
          this.compOffCalendarYear--
        } else {
          this.compOffCalendarMonth--
        }
      } else if (direction === 1) {
        if (this.compOffCalendarMonth === 11) {
          this.compOffCalendarMonth = 0
          this.compOffCalendarYear++
        } else {
          this.compOffCalendarMonth++
        }
      }
    },
    
    selectCompOffCalendarDate(day) {
      if (!day.isCurrentMonth) {
        const clickedDate = moment(day.dateStr)
        this.compOffCalendarMonth = clickedDate.month()
        this.compOffCalendarYear = clickedDate.year()
        this.$nextTick(() => {
          this.selectCompOffCalendarDate({ ...day, isCurrentMonth: true })
        })
        return
      }
      this.compOffForm.date = day.dateStr
      const selectedDate = moment(day.dateStr)
      this.compOffCalendarMonth = selectedDate.month()
      this.compOffCalendarYear = selectedDate.year()
      this.showCompOffDateCalendar = false
    },
    
    getCompOffCalendarDayClasses(day) {
      return {
        'other-month': !day.isCurrentMonth,
        'selected-start': day.dateStr === this.compOffForm.date,
        'today': day.isToday,
        'disabled': day.isDisabled,
        'past-date': day.isPast && !day.isToday,
        'already-applied': day.isAlreadyApplied,
        'holiday-disabled': day.isHoliday,
      }
    },
    
    clearCompOffDateSelection() {
      this.compOffForm.date = null
      this.$forceUpdate()
    },
    
    async handleCompOffEmployeeChange(event) {
      // Prevent any default behavior that might close the modal
      if (event && event.preventDefault) {
        event.preventDefault()
      }
      
      // Similar to leave employee change
      if (this.compOffForm.selected_employee) {
        // Could load employee data if needed
        // Employee selected - modal should stay open
      } else {
        // Clear employee-specific data
      }
      
      // Ensure modal stays open
      return false
    },
    
    // Utility method to get status badge variant
    getStatusVariant(status) {
      const statusLower = (status || '').toLowerCase()
      if (statusLower.includes('approved')) return 'success'
      if (statusLower.includes('reject')) return 'danger'
      if (statusLower.includes('pending')) return 'warning'
      if (statusLower.includes('cancel')) return 'secondary'
      return 'info'
    },
    resetWFHForm() {
      this.wfhForm = {
        id: null,
        date: null,
        comment: '',
        partial_wfh: false,
        is_half_day: false,
        half_day_type: '1st Half Day',
        selected_employee: null,
      }
      // Reset selected employee WFH info
      this.selectedEmployeeWFHCount = 0
      this.selectedEmployeeIsPermanentWFH = false
      // Reset calendar to current month
      this.wfhCalendarMonth = moment().month()
      this.wfhCalendarYear = moment().year()
    },
    async handleWFHEmployeeChange(empId) {
      // When called from the @employee-change event (dropdown change), store the new selection.
      // Without this, wfhForm.selected_employee would never update from user-driven changes.
      if (empId !== undefined) {
        this.wfhForm.selected_employee = empId || null
      }
      // When employee is selected for WFH, load their WFH config and calculate quarter count
      if (this.wfhForm.selected_employee) {
        const selectedEmpId = this.wfhForm.selected_employee
        try {
          // Load employee config to check permanent WFH status
          const configRes = await attendanceService.getEmployeeConfig(selectedEmpId)
          if (configRes && configRes.status === 'OK' && configRes.data) {
            const metadata = configRes.data.metadata?.metadata || configRes.data.metadata || {}
            this.selectedEmployeeIsPermanentWFH = metadata.allow_wfm === 'TRUE' || metadata.allow_wfm === true
          } else {
            this.selectedEmployeeIsPermanentWFH = false
          }
          
          // Calculate WFH count for selected employee (only if not permanent WFH)
          if (!this.selectedEmployeeIsPermanentWFH) {
            await this.calculateSelectedEmployeeWFHCount(selectedEmpId)
          } else {
            this.selectedEmployeeWFHCount = 0
          }
        } catch (err) {
          console.error('Error loading selected employee WFH info:', err)
          this.selectedEmployeeIsPermanentWFH = false
          this.selectedEmployeeWFHCount = 0
        }
      } else {
        // Reset when no employee is selected
        this.selectedEmployeeWFHCount = 0
        this.selectedEmployeeIsPermanentWFH = false
      }
    },
    async calculateSelectedEmployeeWFHCount(empId) {
      try {
        // Get current quarter dates (Q1: Jan-Mar, Q2: Apr-Jun, Q3: Jul-Sep, Q4: Oct-Dec)
        const now = moment()
        const quarterStart = moment(now).startOf('quarter')
        const quarterEnd = moment(now).endOf('quarter')
        
        // Get approved WFH leaves from leave_applications table
        const leavesRes = await attendanceService.getMyLeaves(empId)
        
        if (leavesRes && leavesRes.status === 'OK' && leavesRes.data) {
          // Filter for approved WFH leaves within current quarter
          const wfhCount = leavesRes.data.filter(leave => {
            // Check if it's WFH type
            const leaveType = (leave.leave_type || '').toLowerCase()
            const isWFH = leaveType === 'work from home' || leaveType === 'wfh'
            
            if (!isWFH) return false
            
            // Check if approved
            const status = (leave.status || '').toLowerCase()
            if (status !== 'approved') return false
            
            // Check if within current quarter
            const leaveDate = moment(leave.from_date || leave.to_date)
            return leaveDate.isSameOrAfter(quarterStart, 'day') && leaveDate.isSameOrBefore(quarterEnd, 'day')
          }).length
          
          this.selectedEmployeeWFHCount = wfhCount
        } else {
          this.selectedEmployeeWFHCount = 0
        }
      } catch (err) {
        console.error('Error calculating selected employee quarter WFH count:', err)
        this.selectedEmployeeWFHCount = 0
      }
    },
    formatDate(date) {
      if (!date) return ''
      
      // Initialize cache if not present (defensive check)
      if (!this._dateFormatCache || !(this._dateFormatCache instanceof Map)) {
        this._dateFormatCache = new Map()
      }
      
      // Use cached formatted date if available
      const dateStr = moment(date).format('YYYY-MM-DD')
      if (this._dateFormatCache.has(dateStr)) {
        return this._dateFormatCache.get(dateStr)
      }
      const formatted = moment(date).format('DD MMM YYYY')
      this._dateFormatCache.set(dateStr, formatted)
      // Limit cache size to prevent memory issues
      if (this._dateFormatCache.size > 1000) {
        const firstKey = this._dateFormatCache.keys().next().value
        this._dateFormatCache.delete(firstKey)
      }
      return formatted
    },
    formatDateMonthOnly(date) {
      if (!date) return ''
      // Format date as "DD MMM" without year (for birthdays and anniversaries)
      return moment(date).format('DD MMM')
    },
    // Optimized helper: Check if date is weekend
    isWeekend(dateStr) {
      if (!dateStr) return false
      const dateMoment = moment(dateStr)
      return dateMoment.day() === 0 || dateMoment.day() === 6
    },
    formatDateRange(dateRange) {
      if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) {
        return ''
      }
      const startDate = moment(dateRange[0]).format('DD MMM YYYY')
      const endDate = moment(dateRange[1]).format('DD MMM YYYY')
      return `${startDate} - ${endDate}`
    },
    formatLeaveType(type) {
      return type || 'N/A'
    },
    formatApprovalStatus(status) {
      // Format status to show clear approval pending information
      if (!status) return 'Pending Manager'
      
      const statusStr = String(status).trim()
      
      // Map status to user-friendly display text
      if (statusStr === 'Pending Manager Approval' || statusStr.toLowerCase() === 'pending manager approval') {
        return 'Pending Manager'
      }
      if (statusStr === 'Manager Approved - Pending HR' || statusStr.toLowerCase() === 'manager approved - pending hr' || statusStr.toLowerCase().includes('pending hr')) {
        return 'Pending HR'
      }
      if (statusStr === 'Approved' || statusStr.toLowerCase() === 'approved') {
        return 'Approved'
      }
      if (statusStr === 'Rejected' || statusStr.toLowerCase() === 'rejected') {
        return 'Rejected'
      }
      if (statusStr === 'Pending' || statusStr.toLowerCase() === 'pending') {
        return 'Pending' // Keep as "Pending" - this is the initial status before regularization is raised
      }
      
      // For any other status, return as-is
      return statusStr
    },
    getApprovalStatusVariant(status) {
      // Returns badge variant based on formatted status
      if (!status) return 'warning'
      
      const statusStr = String(status).trim().toLowerCase()
      
      // Check for pending manager approval
      if (statusStr === 'pending manager approval' || statusStr === 'pending manager' || statusStr === 'pending') {
        return 'warning' // orange
      }
      // Check for pending HR approval
      if (statusStr === 'manager approved - pending hr' || statusStr.includes('pending hr')) {
        return 'info' // blue
      }
      // Check for approved
      if (statusStr === 'approved') {
        return 'success' // green
      }
      // Check for rejected
      if (statusStr === 'rejected') {
        return 'danger' // red
      }
      
      // Default variant
      return 'secondary'
    },
    getIssueVariant(issue) {
      if (!issue || typeof issue !== 'string') return 'info'
      const issueLower = issue.toLowerCase()
      if (issueLower.includes('missing') || issueLower.includes('no punch')) return 'danger'
      if (issueLower.includes('less work')) return 'warning'
      return 'info'
    },
    isRegularizationRaised(regularization) {
      // Check if a regularization request has been RAISED (submitted to backend)
      // Initial status is "Pending" - this means it hasn't been raised yet
      // Once raised, status becomes "Pending Manager Approval" or similar
      const status = (regularization.status || regularization.regularization_status || '').trim().toLowerCase()
      
      // If no status, it hasn't been raised
      if (!status) return false
      
      // If status is exactly "Pending", it hasn't been raised yet (initial status)
      if (status === 'pending') return false
      
      // Any other status means it has been raised (submitted)
      // This includes: "Pending Manager Approval", "Manager Approved - Pending HR", "Approved", "Rejected", etc.
      return true
    },
    getPendingRegularizationCount() {
      // Filter regularizationCases to exclude cases that have been raised (submitted to backend)
      // A case is "raised" if it has an attendance_id or id from the attendance table
      // Only count cases that haven't been raised yet (no attendance record)
      if (!this.regularizationCases || !Array.isArray(this.regularizationCases)) {
        return 0
      }
      
      return this.regularizationCases.filter(caseItem => {
        // Check if this case has been raised (submitted to backend)
        // If it has an attendance_id or id, it means it's been submitted
        const hasAttendanceRecord = !!(caseItem.attendance_id || caseItem.id)
        
        // Also check status - if status is "Pending Manager Approval" or similar, it's been raised
        const status = (caseItem.status || caseItem.regularization_status || '').trim().toLowerCase()
        const isRaisedStatus = status && (
          status === 'pending manager approval' ||
          status === 'manager approved - pending hr' ||
          status.includes('pending hr') ||
          status.includes('manager approved')
        )
        
        // Exclude cases that have been raised (have attendance record or raised status)
        // Only count cases that haven't been raised yet
        return !hasAttendanceRecord && !isRaisedStatus
      }).length
    },
    roundToHalf(value) {
      // Round to nearest 0.5 increment
      // Examples: 9.4 → 9.5, 9.8 → 10, 9.0 → 9.0, 9.2 → 9.0
      if (typeof value !== 'number' || isNaN(value)) {
        return 0
      }
      return Math.round(value * 2) / 2
    },
    downloadLowLeaveBalance() {
      try {
        const data = this.alerts && this.alerts.low_leave_balance ? this.alerts.low_leave_balance : this.employeesWithLowLeaveBalance
        const headers = ['Employee', 'Available Balance (Days)', 'Leaves Taken (Days)', 'Assigned Balance (Days)']
        const rows = (data || []).map(emp => [
          this.getUserName(emp.emp_id) || emp.emp_id,
          this.roundToHalf(emp.leaveBalance || 0),
          this.roundToHalf(emp.leavesTaken || 0),
          this.roundToHalf((emp.leave_balance ?? emp.leaveBalance ?? 0) + (emp.leaves_taken ?? emp.leavesTaken ?? 0))
        ])
        const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', `Low_Leave_Balance_${moment().format('YYYY-MM-DD')}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Low Leave Balance data downloaded successfully',
            icon: 'DownloadIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        console.error('Error downloading low leave balance:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to download',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    downloadTeamLeaveBalance() {
      try {
        const data = this.teamLeaveBalanceTable || []
        const headers = ['Employee', 'Leave Balance (Days)', 'Leaves Taken (Days)']
        const rows = data.map(item => [
          this.getUserName(item.emp_id) || item.emp_id,
          this.roundToHalf(item.leaveBalance || 0),
          this.roundToHalf(item.leavesApplied || 0),
        ])
        const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', `Team_Leave_Balance_${moment().format('YYYY-MM-DD')}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Team Leave Balance data downloaded successfully',
            icon: 'DownloadIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        console.error('Error downloading team leave balance:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to download data',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    downloadFrequentRegularization() {
      try {
        const data = this.alerts && this.alerts.frequent_regularization ? this.alerts.frequent_regularization : this.frequentRegularizationCases
        const headers = ['Employee', 'Regularization Count (Last 30 Days)']
        const rows = (data || []).map(item => [
          this.getUserName(item.emp_id) || item.emp_id,
          item.count || 0
        ])
        const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', `Frequent_Regularization_${moment().format('YYYY-MM-DD')}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Frequent Regularization data downloaded successfully',
            icon: 'DownloadIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        console.error('Error downloading frequent regularization:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to download data',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    downloadLeaveConflicts() {
      try {
        const data = this.effectiveLeaveConflicts
        const headers = ['Date', 'Number of Employees', 'Employees']
        const rows = (data || []).map(item => [
          this.formatDate(item.date),
          item.employees ? item.employees.length : 0,
          item.employees ? item.employees.map(emp => this.getUserName(emp.emp_id) || emp.emp_id).join('; ') : ''
        ])
        const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', `Leave_Conflicts_${moment().format('YYYY-MM-DD')}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Leave Conflicts data downloaded successfully',
            icon: 'DownloadIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        console.error('Error downloading leave conflicts:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to download data',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    applyTeamPunchRosterFilter(filter) {
      this.teamPunchRosterFilter = {
        empId: (filter && filter.empId) || null,
        active: !!(filter && filter.empId),
      }
    },

    clearTeamPunchRosterFilter() {
      this.teamPunchRosterFilter = {
        empId: null,
        active: false,
      }
    },

    async downloadTeamSnapshotPunchData(payload) {
      const from = payload && payload.from
      const to = payload && payload.to
      const empId = (payload && payload.empId) || null
      if (!from || !to) {
        this.$bvToast.toast('Select a From and To date before exporting.', {
          title: 'Date required',
          variant: 'warning',
          solid: true,
        })
        return
      }
      this.punchDownloadLoading = true
      try {
        const res = await attendanceService.getPunchAttendanceByDateRange(from, to, empId)
        const rows = Array.isArray(res && res.data) ? res.data : []
        // Keep download scoped to current team when "All Employees"; honor emp filter when set
        const teamIds = new Set(
          (this.teamMembers || []).map(m => String(m.emp_id || m.id)).filter(Boolean)
        )
        let scoped = rows
        if (empId) {
          const empKey = String(empId)
          scoped = scoped.filter(r => r && String(r.emp_id) === empKey)
        } else {
          scoped = scoped.filter(r => r && r.emp_id && teamIds.has(String(r.emp_id)))
        }
        if (!scoped.length) {
          this.$bvToast.toast('No punch records to export', {
            title: 'No Data',
            variant: 'warning',
            solid: true,
          })
          return
        }
        const csvRows = scoped.map(r => ({
          'Employee ID': r.emp_id || '',
          'Employee Name': this.getUserName(r.emp_id) || r.emp_id || '',
          Date: r.date || '',
          'Punch-In Time': r.intime || '',
          'Punch-Out Time': r.outtime || '',
          'Work Hours': r.worktime || '',
          Status: r.attendance || '',
        }))
        const headers = Object.keys(csvRows[0])
        const csv = [
          headers.join(','),
          ...csvRows.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(',')),
        ].join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.setAttribute('download', `Punch_Data_${from}_to_${to}.csv`)
        a.style.visibility = 'hidden'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.$bvToast.toast(`Exported ${scoped.length} punch record${scoped.length === 1 ? '' : 's'}.`, {
          title: 'Download ready',
          variant: 'success',
          solid: true,
        })
      } catch (err) {
        console.error('Error downloading team snapshot punch data:', err)
        this.$bvToast.toast('Failed to download punch data.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.punchDownloadLoading = false
      }
    },

    downloadProbationEnding() {
      try {
        const data = this.effectiveProbationEnding || []
        const headers = ['Employee', 'Probation End Date', 'Days Remaining']
        const rows = data.map(item => [
          this.getUserName(item.emp_id) || item.emp_id,
          this.formatDate(item.probation_end_date || item.probationEndDate),
          item.days_remaining ?? item.daysRemaining ?? 0
        ])
        const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', `Probation_Ending_${moment().format('YYYY-MM-DD')}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Probation Ending data downloaded successfully',
            icon: 'DownloadIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        console.error('Error downloading probation ending:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to download data',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    formatTime(timeValue) {
      if (!timeValue || timeValue === '0:00:00' || timeValue === '00:00:00' || (typeof timeValue === 'string' && timeValue.trim() === '')) {
        return 'N/A'
      }
      
      // Handle Date objects
      if (timeValue instanceof Date) {
        const hours = String(timeValue.getHours()).padStart(2, '0')
        const minutes = String(timeValue.getMinutes()).padStart(2, '0')
        return `${hours}:${minutes}`
      }
      
      // If it's already in HH:MM or HH:MM:SS format, return as is (but extract just HH:MM)
      if (typeof timeValue === 'string') {
        const timeMatch = timeValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
        if (timeMatch) {
          return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`
        }
        
        // If it's a full ISO datetime string, extract time part
        if (timeValue.includes('T')) {
          const timePart = timeValue.split('T')[1]?.split('.')[0] || timeValue.split(' ')[1]
          if (timePart) {
            // Extract HH:MM from HH:MM:SS
            const timeMatch = timePart.match(/^(\d{1,2}):(\d{2})/)
            if (timeMatch) {
              return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`
            }
            return timePart.substring(0, 5)
          }
        }
        
        // If it's a space-separated datetime, extract time part
        if (timeValue.includes(' ')) {
          const parts = timeValue.split(' ')
          if (parts.length > 1) {
            const timePart = parts[1]
            const timeMatch = timePart.match(/^(\d{1,2}):(\d{2})/)
            if (timeMatch) {
              return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`
            }
            return timePart.substring(0, 5)
          }
        }
      }
      
      return timeValue || 'N/A'
    },
    isManagerApproved(regularization) {
      // Check if manager has approved the regularization
      // ONLY return true if a regularization request has been RAISED (has a status)
      if (!this.isRegularizationRaised(regularization)) return false
      
      const status = (regularization.status || regularization.regularization_status || '').trim()
      const statusLower = status.toLowerCase()
      // Manager approved statuses: "Manager Approved - Pending HR", "Manager Approved", etc.
      return statusLower.includes('manager approved') || statusLower === 'pending hr approval'
    },
    needsManagerApproval(regularization) {
      // Check if regularization needs manager approval
      // ONLY return true if a regularization request has been RAISED (has a status)
      if (!this.isRegularizationRaised(regularization)) return false
      
      const status = (regularization.status || regularization.regularization_status || '').trim()
      const statusLower = status.toLowerCase()
      // Needs manager approval if status is "Pending Manager Approval" or "Pending"
      return statusLower.includes('pending manager') || statusLower === 'pending'
    },
    needsLeaveApproval(leave) {
      // Check if leave needs approval (pending status)
      // Returns TRUE only for pending leaves (can be edited/cancelled)
      // Returns FALSE for approved leaves (cannot be cancelled)
      if (!leave || !leave.status) return false
      const status = (leave.status || '').trim().toLowerCase()
      // Include all intermediate approval statuses (manager/operation head/HR pending)
      const isPendingStage = status.includes('pending') ||
        status.includes('manager approved - pending hr') ||
        status.includes('manager approved - pending operation head') ||
        status.includes('operation head approved - pending hr')
      return isPendingStage && !(status === 'approved') // Exclude fully approved
    },
    isLeavePendingManagerApproval(leave) {
      // Check if leave is pending manager approval
      if (!leave || !leave.status) return false
      const status = (leave.status || '').trim().toLowerCase()
      // Pending manager approval if status is "Pending Manager Approval" or "Pending"
      return status.includes('pending manager') || status === 'pending'
    },
    isLeavePendingOperationHeadApproval(leave) {
      // Check if leave is pending operation head approval (Operations dept flow)
      if (!leave || !leave.status) return false
      const status = (leave.status || '').trim().toLowerCase()
      return status.includes('pending operation head') || status.includes('manager approved - pending operation head')
    },
    getApplicantDepartment(leave) {
      // Applicant = employee who applied (leave.emp_id). Used for Operations vs other department flow.
      if (!leave || leave.emp_id == null) return ''
      if (String(leave.emp_id) === String(this.emp_id)) return this.currentUserDepartment || ''
      if (leave.department != null && leave.department !== '') return String(leave.department).trim()
      const map = this.employeeDepartmentMap || {}
      return map[leave.emp_id] || ''
    },
    isApplicantOperations(leave) {
      const dept = (this.getApplicantDepartment(leave) || '').trim().toLowerCase()
      return dept === 'operations'
    },
    isApplicantOnProbation(leave) {
      if (!leave || leave.emp_id == null) return false
      if (leave.is_probation === true) return true
      if (this.teamMemberConfigs && this.teamMemberConfigs.length > 0) {
        const configItem = this.teamMemberConfigs.find(c => String(c.emp_id) === String(leave.emp_id))
        const config = configItem && configItem.config
        return !!(config && config.is_probation === true)
      }
      return false
    },
    canCancelOwnApprovedLeave(leave) {
      // User can cancel their own approved leave (for Cancel button in detail modal and table)
      // Show Cancel only when from_date is today or in the future (not for past leaves)
      if (!leave || !leave.id) return false
      const isOwn = String(leave.emp_id) === String(this.emp_id)
      if (!isOwn) return false
      const status = (leave.status || '').trim().toLowerCase()
      const isApprovedOrPendingHr = status === 'approved' || status.includes('pending hr') || status.includes('approved')
      if (!isApprovedOrPendingHr) return false
      // Must not be cancelled/rejected
      if (status === 'cancelled' || status.includes('reject')) return false
      const fromDate = leave.from_date ? moment(leave.from_date).startOf('day') : null
      if (!fromDate || !fromDate.isValid()) return true
      const today = moment().startOf('day')
      if (fromDate.isBefore(today)) return false
      return true
    },
    /**
     * Unified: show Cancel for own leaves that are either (1) approved with from_date >= today, or (2) pending with from_date >= today.
     * Used in leaves table and leave detail modal.
     */
    canCancelLeave(leave) {
      if (!leave || !leave.id) return false
      const isOwn = String(leave.emp_id) === String(this.emp_id)
      if (!isOwn) return false
      const status = (leave.status || '').trim().toLowerCase()
      if (status === 'cancelled' || status.includes('reject')) return false
      const fromDate = leave.from_date ? moment(leave.from_date).startOf('day') : null
      if (!fromDate || !fromDate.isValid()) return true
      const today = moment().startOf('day')
      if (fromDate.isBefore(today)) return false
      const isPending = status.includes('pending') || status.includes('manager approved - pending hr') || status.includes('manager approved - pending operation head') || status.includes('operation head approved - pending hr')
      const isApprovedOrPendingHr = status === 'approved' || status.includes('pending hr') || status.includes('approved')
      return isPending || isApprovedOrPendingHr
    },
    isManagerOfEmployee(empId) {
      // Check if current user is the manager of the given employee
      if (!empId) return false
      
      // If employee is in teamMembers, current user is their manager
      if (this.teamMembers && this.teamMembers.length > 0) {
        const isInTeam = this.teamMembers.some(member => 
          (member.emp_id === empId) || (member.id === empId)
        )
        if (isInTeam) return true
        
        // Also check if employee's mgr_id matches current user's emp_id
        const employee = this.teamMembers.find(member => 
          (member.emp_id === empId) || (member.id === empId)
        )
        if (employee) {
          const employeeMgrId = employee.mgr_id || employee.manager_id || employee.manager || employee.account_manager
          if (employeeMgrId === this.emp_id) return true
        }
      }
      
      // Also check teamWiseEmployees for HR/HR Director views
      if (this.teamWiseEmployees && this.teamWiseEmployees.length > 0) {
        const isInTeamWise = this.teamWiseEmployees.some(employee => 
          (employee.emp_id === empId) || (employee.id === empId)
        )
        if (isInTeamWise) {
          const employee = this.teamWiseEmployees.find(emp => 
            (emp.emp_id === empId) || (emp.id === empId)
          )
          if (employee) {
            const employeeMgrId = employee.mgr_id || employee.manager_id || employee.manager || employee.account_manager
            // For HR viewing account manager's team, check if account manager is the manager
            if (this.selectedAccountManager && employeeMgrId === this.selectedAccountManager) {
              // HR can approve on behalf, but for manager approval, check if account manager matches
              return false // HR is not the direct manager
            }
            // For account manager viewing their own team
            if (employeeMgrId === this.emp_id) return true
          }
        }
      }
      
      // For Account Managers: if they can view team and have canManageLeaves, they are managers
      if (this.isAccountManager && this.canManageLeaves && this.canViewTeam) {
        // If employee is in their team, they are the manager
        if (this.teamMembers && this.teamMembers.some(m => (m.emp_id === empId) || (m.id === empId))) {
          return true
        }
      }
      
      return false
    },
    /**
     * Whether current user can approve this leave (any stage: manager, operation head, HR, or leave_all in org)
     */
    canApproveLeave(leave) {
      if (!leave || !this.needsLeaveApproval(leave)) return false
      // leave_all: only in org view, can approve at any stage
      if (this.viewMode === 'org' && (this.$can('manage', 'all') || this.$can('approve', ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_ALL))) return true
      // Manager: must be manager of employee and have leave_manager
      if (this.$can('approve', ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_MANAGER) && this.isManagerOfEmployee(leave.emp_id) && this.isLeavePendingManagerApproval(leave)) return true
      // Operation Head: only for Operations applicant, leave at pending operation head
      if (this.isApplicantOperations(leave) && this.$can('approve', ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_OPERATION_HEAD) && this.isLeavePendingOperationHeadApproval(leave)) return true
      // HR: leave at pending HR
      if (this.$can('approve', ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_HR) && this.isLeavePendingHRApproval(leave)) return true
      return false
    },
    /**
     * Unified method to check if current user can approve a leave or regularization item
     * This replaces scattered role-based checks throughout the component
     * 
     * @param {Object} item - The leave or regularization item to check
     * @param {string} type - Either 'leave' or 'regularization'
     * @returns {boolean} - Whether the current user can approve this item
     */
    canApproveItem(item, type = 'leave') {
      if (!item) return false
      
      const empId = item.emp_id
      
      // Determine if item is at manager stage
      const isPendingManager = type === 'leave' 
        ? this.isLeavePendingManagerApproval(item)
        : this.needsManagerApproval(item)
      
      // Determine if item is at HR stage (pending HR approval after manager approved)
      const isPendingHR = type === 'leave'
        ? this.isLeavePendingHRApproval(item)
        : this.isManagerApproved(item)
      
      // Leave: use department-based flow (leave_all only in org; manager/operation head/HR by stage)
      if (type === 'leave') {
        if (this.canApproveLeave(item)) return true
        return false
      }
      // Regularization: super approvers can approve anything at any stage
      if (type === 'regularization' && this.canApproveRegularizationAllStages) return true
      // Manager-stage approvers for regularization
      if (type === 'regularization' && this.canApproveRegularizationManagerStage && isPendingManager) {
        return this.isManagerOfEmployee(empId)
      }
      
      return false
    },
    
    /**
     * Check if a leave is pending HR approval (after manager approved)
     * @param {Object} leave - The leave item
     * @returns {boolean}
     */
    isLeavePendingHRApproval(leave) {
      if (!leave || !leave.status) return false
      const status = (leave.status || '').trim().toLowerCase()
      // After manager approval (non-Ops) or after operation head approval (Ops)
      return status.includes('pending hr')
    },
    
    downloadRegularizationExcel() {
      // Export regularization cases to Excel
      try {
        const data = this.regularizationCases.map(reg => ({
          'Employee Name': this.getUserName(reg.emp_id),
          'Date': this.formatDate(reg.datestring),
          'Issue': reg.issue_type,
          'Punch Data': reg.has_punch_data ? 'Yes' : 'No',
          'Biometric Data': reg.has_biometric ? 'Yes' : 'No',
          'IN Time': reg.intime || '-',
          'OUT Time': reg.outtime || '-',
          'Work Hours': reg.worktime || 'N/A',
          'Status': reg.status || reg.regularization_status || 'Pending',
          'Remarks by Manager': reg.manager_remarks || reg.manager_comment || '-',
        }))
        
        // Convert to CSV
        const headers = Object.keys(data[0])
        const csvContent = [
          headers.join(','),
          ...data.map(row => headers.map(header => `"${row[header]}"`).join(','))
        ].join('\n')
        
        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `regularization_${moment().format('YYYY-MM-DD')}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Regularization data exported successfully!',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        }, { timeout: 3000 })
      } catch (err) {
        console.error('Error exporting regularization data:', err)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to export data',
            icon: 'XCircleIcon',
            variant: 'danger',
          },
        }, { timeout: 3000 })
      }
    },
    downloadLeavesExcel() {
      try {
        const leaves = this.leavesData || []
        if (leaves.length === 0) {
          this.$toast({
            component: ToastificationContent,
            props: { title: 'No leaves to export', icon: 'AlertCircleIcon', variant: 'warning' },
          }, { timeout: 3000 })
          return
        }
        const data = leaves.map(leave => ({
          'Employee': leave.employee_name || this.getUserName(leave.emp_id) || leave.emp_id || 'N/A',
          'From Date': leave.from_date ? this.formatDate(leave.from_date) : (leave.datestring ? this.formatDate(leave.datestring) : '-'),
          'To Date': leave.to_date ? this.formatDate(leave.to_date) : '-',
          'Total Days': leave.total_days != null ? (parseFloat(leave.total_days) === 1 ? '1 day' : `${parseFloat(leave.total_days)} days`) : '-',
          'Issue': leave.issue_type || leave.leave_type || '-',
          'Data Source': leave.data_source_status || leave.source_type || 'Leave',
          'Status': this.formatApprovalStatus(leave.status || leave.regularization_status) || 'Pending',
          'Remarks by Manager': leave.manager_remarks || leave.manager_comment || '-',
        }))
        const worksheet = XLSX.utils.json_to_sheet(data)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Leaves')
        const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        saveAs(blob, `leaves_${moment().format('YYYY-MM-DD')}.xlsx`)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Leaves data exported successfully!',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        }, { timeout: 3000 })
      } catch (err) {
        console.error('Error exporting leaves data:', err)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to export leaves data',
            icon: 'XCircleIcon',
            variant: 'danger',
          },
        }, { timeout: 3000 })
      }
    },
    getBiometricStatus(biometric) {
      if (!biometric) return 'absent'
      const hasInTime = biometric.intime && biometric.intime !== '0:00:00' && biometric.intime !== '00:00:00'
      const hasOutTime = biometric.outtime && biometric.outtime !== '0:00:00' && biometric.outtime !== '00:00:00'
      if (hasInTime && hasOutTime) return 'present'
      if (hasInTime || hasOutTime) return 'partial'
      return 'absent'
    },
    getBiometricIntime(item) {
      if (!item) return ''
      if (item.biometric && item.biometric.intime) return item.biometric.intime
      if (item.biometricRecord && item.biometricRecord.intime) return item.biometricRecord.intime
      return ''
    },
    getBiometricOuttime(item) {
      if (!item) return ''
      if (item.biometric && item.biometric.outtime) return item.biometric.outtime
      if (item.biometricRecord && item.biometricRecord.outtime) return item.biometricRecord.outtime
      return ''
    },
    getBiometricWorktime(item) {
      if (!item) return ''
      if (item.biometric && item.biometric.worktime) return item.biometric.worktime
      if (item.biometricRecord && item.biometricRecord.worktime) return item.biometricRecord.worktime
      return ''
    },
    parseWorkTime(worktime) {
      if (!worktime) return 0
      const parts = worktime.split(':')
      if (parts.length === 3) {
        const hours = parseInt(parts[0]) || 0
        const minutes = parseInt(parts[1]) || 0
        return hours + (minutes / 60)
      }
      return 0
    },
    calculateWorkTime(intime, outtime) {
      if (!intime || !outtime) return null
      
      // Try multiple time formats to handle different input formats
      const timeFormats = ['HH:mm:ss', 'HH:mm', 'H:mm:ss', 'H:mm']
      let inTime = null
      let outTime = null
      
      // Parse intime
      for (const format of timeFormats) {
        const parsed = moment(intime, format, true)
        if (parsed.isValid()) {
          inTime = parsed
          break
        }
      }
      
      // Parse outtime
      for (const format of timeFormats) {
        const parsed = moment(outtime, format, true)
        if (parsed.isValid()) {
          outTime = parsed
          break
        }
      }
      
      // If parsing failed, return null
      if (!inTime || !outTime) {
        console.warn('Invalid time format:', { intime, outtime })
        return null
      }
      
      // Calculate difference in minutes
      let diff = outTime.diff(inTime, 'minutes')
      
      // Handle overnight shifts (if outtime is earlier than intime, assume next day)
      if (diff < 0) {
        diff = diff + (24 * 60) // Add 24 hours
      }
      
      const hours = Math.floor(diff / 60)
      const minutes = diff % 60
      
      return {
        worktime: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`,
        status: hours >= 8 ? 'present' : hours >= 4 ? 'half-day' : 'absent'
      }
    },
    isWFH(leaveType) {
      return isWFHLeaveType(leaveType)
    },
    getDayTooltip(day) {
      const details = []
      const date = moment(day.date).format('DD MMM YYYY')
      
      details.push(`Date: ${date}`)
      
      if (day.isHoliday) {
        const holiday = this.holidays.find(h => h.holiday_date === day.date || h.date === day.date)
        details.push(`Status: Holiday${holiday ? ` - ${holiday.holiday_name || holiday.name || ''}` : ''}`)
      } else if (day.isWeekOff) {
        details.push('Status: Week Off')
      } else if (day.isLeave) {
        details.push(`Status: Leave - ${day.leaveType || 'Leave'}`)
      } else if (day.isWFH) {
        details.push(`Status: Work From Home`)
      } else if (day.isRegularization) {
        details.push(`Status: Regularization Required`)
        if (day.regularizationIssue) {
          details.push(`Issue: ${day.regularizationIssue}`)
        }
      } else if (day.isPresent) {
        details.push('Status: Present')
      } else if (day.isAbsent) {
        details.push('Status: Absent')
      } else if (day.isCompanyWfh) {
        const companyWfhDay = (this.companyWfhDays || []).find(d => d.date === day.date)
        details.push(`Status: Company-Wide WFH Day${companyWfhDay ? ` - ${companyWfhDay.title || ''}` : ''}`)
      } else {
        details.push('Status: Not Marked')
      }

      // Co. WFH is a marker alongside punch status — punch remains required.
      if (day.isCompanyWfh && !day.isHoliday && !day.isWeekOff) {
        const companyWfhDay = (this.companyWfhDays || []).find(d => d.date === day.date)
        const hasCompanyWfhStatus = details.some(line => line.includes('Company-Wide WFH'))
        if (!hasCompanyWfhStatus) {
          details.push(`Company-Wide WFH Day${companyWfhDay ? ` - ${companyWfhDay.title || ''}` : ''}`)
        }
        details.push('Punch-in/out is still required — does not affect personal WFH balance')
      }

      // Employee View: apply hints by date (future: Leave/WFH/CO; today/past: Request CO)
      if (
        this.viewMode === 'my'
        && day.isCurrentMonth
        && !day.isHoliday
        && !day.isWeekOff
        && !day.isCompanyWfh
        && day.date
      ) {
        const dayStart = moment(day.date).startOf('day')
        const todayStart = moment().startOf('day')
        if (dayStart.isSameOrAfter(todayStart, 'day')) {
          details.push('Click to apply Leave / WFH / Comp Off')
        }
        if (dayStart.isSameOrBefore(todayStart, 'day')) {
          details.push('Click to Request Comp Off')
        }
      }
      
      if (day.biometricRecord) {
        if (day.biometricRecord.intime && day.biometricRecord.intime !== '0:00:00' && day.biometricRecord.intime !== '00:00:00') {
          details.push(`IN Time: ${day.biometricRecord.intime}`)
        }
        if (day.biometricRecord.outtime && day.biometricRecord.outtime !== '0:00:00' && day.biometricRecord.outtime !== '00:00:00') {
          details.push(`OUT Time: ${day.biometricRecord.outtime}`)
        }
        if (day.workHours) {
          details.push(`Work Hours: ${day.workHours}h`)
        }
      }
      
      return details.join('<br>')
    },
    mapRegularizationCaseFromAPI(apiCase) {
      // Extract punch data from API response
      const punchInTime = apiCase.punch_in_time || apiCase.punchInTime || apiCase.punch_in || apiCase.in_time || apiCase.intime || null
      const punchOutTime = apiCase.punch_out_time || apiCase.punchOutTime || apiCase.punch_out || apiCase.out_time || apiCase.outtime || null
      const punchWorktime = apiCase.punch_worktime || apiCase.punchWorktime || apiCase.working_hours || apiCase.worktime || null
      
      // Extract biometric data from API response
      const bmInTime = apiCase.bm_in_time || apiCase.bmInTime || apiCase.biometric_in_time || apiCase.biometricInTime || apiCase.bm_in || null
      const bmOutTime = apiCase.bm_out_time || apiCase.bmOutTime || apiCase.biometric_out_time || apiCase.biometricOutTime || apiCase.bm_out || null
      const bmWorktime = apiCase.bm_worktime || apiCase.bmWorktime || apiCase.biometric_worktime || apiCase.biometricWorktime || null
      
      // Create punchData object if punch data exists
      const punchData = (punchInTime || punchOutTime) ? {
        intime: punchInTime,
        outtime: punchOutTime,
        worktime: punchWorktime
      } : null
      
      // Create biometric object if biometric data exists
      const biometric = (bmInTime || bmOutTime) ? {
        intime: bmInTime,
        outtime: bmOutTime,
        worktime: bmWorktime
      } : null
      
      // Create biometricRecord for backward compatibility
      const biometricRecord = biometric ? {
        intime: biometric.intime,
        outtime: biometric.outtime,
        worktime: biometric.worktime
      } : null
      
      // Extract employee name from API response (try multiple possible field names)
      const employeeName = apiCase.employee_name || apiCase.employeeName || apiCase.name || apiCase.emp_name || 
                          (apiCase.employee ? (apiCase.employee.name || apiCase.employee.employee_name || apiCase.employee.first_name) : null) ||
                          null
      
      // Map API response fields to expected frontend format
      return {
        emp_id: apiCase.emp_id || apiCase.empId || apiCase.employee_id,
        employee_name: employeeName, // Include employee name if available from API
        datestring: apiCase.date || apiCase.datestring,
        date: apiCase.date || apiCase.datestring,
        issue_type: apiCase.issue || apiCase.issue_type,
        issue: apiCase.issue || apiCase.issue_type,
        intime: apiCase.in_time || apiCase.intime || punchInTime || bmInTime,
        outtime: apiCase.out_time || apiCase.outtime || punchOutTime || bmOutTime,
        worktime: apiCase.working_hours || apiCase.worktime || punchWorktime || bmWorktime,
        working_hours: apiCase.working_hours || apiCase.worktime || punchWorktime || bmWorktime,
        status: apiCase.current_status || apiCase.status || apiCase.regularization_status || 'Pending Regularization',
        regularization_status: apiCase.current_status || apiCase.status || apiCase.regularization_status || 'Pending Regularization',
        current_status: apiCase.current_status || apiCase.status || 'Pending Regularization',
        data_source_status: apiCase.data_source || apiCase.data_source_status,
        data_source: apiCase.data_source || apiCase.data_source_status,
        has_punch_data: !!punchData,
        has_biometric: !!biometric,
        id: apiCase.id || apiCase.attendance_id,
        attendance_id: apiCase.id || apiCase.attendance_id,
        remarks: apiCase.remarks || apiCase.comment,
        comment: apiCase.comment || apiCase.remarks,
        manager_remarks: apiCase.manager_remarks || apiCase.manager_comment,
        manager_comment: apiCase.manager_comment || apiCase.manager_remarks,
        hr_update_dt: apiCase.hr_update_dt,
        manager_update_dt: apiCase.manager_update_dt,
        hr_id: apiCase.hr_id,
        // Map punch times from API (try multiple possible field names)
        punch_in_time: punchInTime,
        punch_out_time: punchOutTime,
        // Map biometric times from API (try multiple possible field names)
        bm_in_time: bmInTime,
        bm_out_time: bmOutTime,
        // Map attendance times (submitted by user) from API (try multiple possible field names)
        att_in_time: apiCase.att_in_time || apiCase.attInTime || apiCase.attendance_in_time || apiCase.attendanceInTime || apiCase.att_intime || apiCase.attIntime || null,
        att_out_time: apiCase.att_out_time || apiCase.attOutTime || apiCase.attendance_out_time || apiCase.attendanceOutTime || apiCase.att_outtime || apiCase.attOuttime || null,
        // Map leave type
        leave_type: apiCase.leave_type || null,
        // Include structured data objects for modal display
        punchData: punchData,
        biometric: biometric,
        biometricRecord: biometricRecord
      }
    },
    calculateDays(fromDate, toDate) {
      if (!fromDate || !toDate) return 0
      
      const from = moment(fromDate)
      const to = moment(toDate)
      let days = 0
      let current = from.clone()
      
      while (current.isSameOrBefore(to, 'day')) {
        const dayOfWeek = current.day()
        const dateStr = current.format('YYYY-MM-DD')
        
        // Check if it's a weekend (Saturday = 6, Sunday = 0)
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
        
        // Check if it's a holiday (using moment-based comparison for format consistency)
        const isHoliday = this.holidays.some(h => {
          const holidayDate = moment(h.holiday_date || h.date).startOf('day')
          return current.isSame(holidayDate, 'day')
        })
        const isCompanyWfh = this.isCompanyWfhDate(dateStr)
        
        // Exclude weekends/holidays/Co. WFH from personal leave-day counts
        // (Co. WFH never deducts personal WFH balance; punch is still required)
        if (!isWeekend && !isHoliday && !isCompanyWfh) {
          days++
        }
        
        current.add(1, 'day')
      }
      
      return days
    },
    async loadAnnouncements() {
      try {
        const res = await attendanceService.getAnnouncements()
        if (res && res.status === 'OK' && res.data) {
          // Filter to show only ACTIVE announcements
          this.announcements = res.data
            .filter(a => (a.status || 'ACTIVE') === 'ACTIVE')
            .sort((a, b) => new Date(a.date) - new Date(b.date)) // Ascending order
        } else {
          // Fallback to empty array if API fails
          this.announcements = []
        }
      } catch (err) {
        console.error('Error loading announcements:', err)
        this.announcements = []
      }
    },
    canCancelAnnouncement(announcement) {
      // HR/HR Director can always cancel announcements
      if (this.isHRDirector || this.isHR) {
        return true
      }
      // Users can cancel their own announcements
      if (announcement && announcement.created_by) {
        return announcement.created_by === this.emp_id
      }
      return false
    },
    async cancelAnnouncement(announcementId) {
      try {
        const res = await attendanceService.updateAnnouncementStatus(announcementId, 'INACTIVE')
        if (res && res.status === 'OK') {
          // Remove announcement from list immediately (optimistic update)
          this.announcements = this.announcements.filter(a => a.id !== announcementId)
          
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Announcement canceled successfully',
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          }, { timeout: 3000 })
          
          // Reload announcements to ensure consistency
          await this.loadAnnouncements()
        } else {
          // Check for permission errors
          const errorMessage = res?.message || 'Failed to cancel announcement'
          const isPermissionError = errorMessage.toLowerCase().includes('access denied') || 
                                   errorMessage.toLowerCase().includes('forbidden') ||
                                   errorMessage.toLowerCase().includes('permission')
          
          this.$toast({
            component: ToastificationContent,
            props: {
              title: isPermissionError ? 'Permission Denied' : 'Failed to cancel announcement',
              icon: 'XCircleIcon',
              variant: 'danger',
              text: isPermissionError 
                ? 'You do not have permission to cancel this announcement. Only HR/HR Director or the announcement creator can cancel it.'
                : errorMessage,
            },
          }, { timeout: 4000 })
        }
      } catch (err) {
        console.error('Error canceling announcement:', err)
        
        // Check if it's a 403 Forbidden error
        const isForbidden = err.response && err.response.status === 403
        const errorMessage = err.response?.data?.message || err.message || 'Failed to cancel announcement'
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: isForbidden ? 'Permission Denied' : 'Error',
            icon: 'XCircleIcon',
            variant: 'danger',
            text: isForbidden
              ? 'You do not have permission to cancel this announcement. Only HR/HR Director or the announcement creator can cancel it.'
              : errorMessage,
          },
        }, { timeout: 4000 })
      }
    },
    async loadBirthdaysAndAnniversaries() {
      try {
        // Fetch all users to get birthdays and work anniversaries
        const res = await userService.getAllUsers()
        if (res && res.status === 'OK' && res.data) {
          const allUsers = Array.isArray(res.data) ? res.data : []
          
          // Filter for active employees only
          const users = allUsers.filter(user => {
            const status = user.status || user.employee_status || user.is_active
            return status === 'Active' || status === 'active' || status === true || status === 'ACTIVE'
          })
          
          // Define range: Next 20 days
          const today = moment().startOf('day')
          const endLimit = moment().add(20, 'days').endOf('day')
          
          // Process Birthdays
          this.birthdays = users
            .map(user => {
              if (!user.dob && !user.date_of_birth && !user.birthday) return null
              const dob = moment(user.dob || user.date_of_birth || user.birthday)
              // Calculate next birthday
              let nextBirthday = dob.clone().year(today.year())
              if (nextBirthday.isBefore(today, 'day')) {
                nextBirthday.add(1, 'year')
              }
              return { user, nextBirthday }
            })
            .filter(item => item && item.nextBirthday.isBetween(today, endLimit, 'day', '[]'))
            .map(({ user, nextBirthday }) => {
              const dateStr = nextBirthday.format('YYYY-MM-DD')
              return {
                id: `birthday-${user.emp_id}`,
                type: 'birthday',
                title: `🎉 ${user.first_name || user.name || user.emp_id}'s Birthday`,
                message: `${user.first_name || user.name || user.emp_id} is celebrating their birthday`,
                birthday_date: dateStr,
                date: dateStr,
                emp_id: user.emp_id,
                name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || user.emp_id,
              }
            })
            .sort((a, b) => moment(a.date).diff(moment(b.date))) // Ascending
          
          // Process Work Anniversaries
          this.workAnniversaries = users
            .map(user => {
               if (!user.doj && !user.date_of_joining && !user.joining_date) return null
               const doj = moment(user.doj || user.date_of_joining || user.joining_date)
               // Calculate next anniversary
               let nextAnniversary = doj.clone().year(today.year())
               if (nextAnniversary.isBefore(today, 'day')) {
                 nextAnniversary.add(1, 'year')
               }
               return { user, nextAnniversary, years: nextAnniversary.year() - doj.year() }
            })
            .filter(item => item && item.nextAnniversary.isBetween(today, endLimit, 'day', '[]'))
            .map(({ user, nextAnniversary, years }) => {
              const dateStr = nextAnniversary.format('YYYY-MM-DD')
              return {
                id: `anniversary-${user.emp_id}`,
                type: 'anniversary',
                title: `🎊 ${user.first_name || user.name || user.emp_id}'s Work Anniversary`,
                message: `${user.first_name || user.name || user.emp_id} is completing ${years} year${years !== 1 ? 's' : ''} with us`,
                anniversary_date: dateStr,
                date: dateStr,
                emp_id: user.emp_id,
                name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || user.emp_id,
                years: years,
              }
            })
            .sort((a, b) => moment(a.date).diff(moment(b.date))) // Ascending
        } else {
          this.birthdays = []
          this.workAnniversaries = []
        }
      } catch (err) {
        console.error('Error loading birthdays and anniversaries:', err)
        this.birthdays = []
        this.workAnniversaries = []
      }
    },
    /** Open the celebration download modal; default range = current month */
    openCelebrationDownloadModal() {
      // Always default to current month when opening
      this.setCelebrationRange('thisMonth')
      this.showCelebrationDownloadModal = true
    },

    /** Set a quick preset on the celebration download range */
    setCelebrationRange(preset) {
      const now = moment()
      switch (preset) {
        case 'thisMonth':
          this.celebrationDownloadFrom = now.clone().startOf('month').format('YYYY-MM-DD')
          this.celebrationDownloadTo   = now.clone().endOf('month').format('YYYY-MM-DD')
          break
        case 'nextMonth':
          this.celebrationDownloadFrom = now.clone().add(1, 'month').startOf('month').format('YYYY-MM-DD')
          this.celebrationDownloadTo   = now.clone().add(1, 'month').endOf('month').format('YYYY-MM-DD')
          break
        case 'thisQuarter':
          this.celebrationDownloadFrom = now.clone().startOf('quarter').format('YYYY-MM-DD')
          this.celebrationDownloadTo   = now.clone().endOf('quarter').format('YYYY-MM-DD')
          break
        case 'thisYear':
          this.celebrationDownloadFrom = now.clone().startOf('year').format('YYYY-MM-DD')
          this.celebrationDownloadTo   = now.clone().endOf('year').format('YYYY-MM-DD')
          break
        default:
          break
      }
      this.celebrationActivePreset = preset
    },

    /** Open the punch In/Out data download modal; defaults to current month */
    openPunchDataDownloadModal() {
      this.setPunchDownloadRange('month')
      this.showPunchDataDownloadModal = true
    },

    /** Set a quick preset for the punch download date range */
    setPunchDownloadRange(preset) {
      const now = new Date()
      const y = now.getFullYear()
      const m = now.getMonth()
      const d = now.getDate()
      const pad = n => String(n).padStart(2, '0')
      if (preset === 'week') {
        const day = now.getDay()
        const mon = new Date(now)
        mon.setDate(d - (day === 0 ? 6 : day - 1))
        this.punchDownloadFrom = `${mon.getFullYear()}-${pad(mon.getMonth() + 1)}-${pad(mon.getDate())}`
        this.punchDownloadTo   = `${y}-${pad(m + 1)}-${pad(d)}`
      } else if (preset === 'month') {
        this.punchDownloadFrom = `${y}-${pad(m + 1)}-01`
        this.punchDownloadTo   = `${y}-${pad(m + 1)}-${pad(d)}`
      } else if (preset === 'lastMonth') {
        const lm = m === 0 ? 11 : m - 1
        const ly = m === 0 ? y - 1 : y
        const lastDay = new Date(ly, lm + 1, 0).getDate()
        this.punchDownloadFrom = `${ly}-${pad(lm + 1)}-01`
        this.punchDownloadTo   = `${ly}-${pad(lm + 1)}-${pad(lastDay)}`
      } else if (preset === 'quarter') {
        const qStart = Math.floor(m / 3) * 3
        this.punchDownloadFrom = `${y}-${pad(qStart + 1)}-01`
        this.punchDownloadTo   = `${y}-${pad(m + 1)}-${pad(d)}`
      }
      this.punchDownloadActivePreset = preset
    },

    async downloadPunchData() {
      if (!this.punchDownloadFrom || !this.punchDownloadTo) {
        this.$bvToast.toast('Select a From and To date before exporting.', {
          title: 'Date required',
          variant: 'warning',
        })
        return
      }
      this.punchDownloadLoading = true
      try {
        const empId = this.punchDownloadEmployeeFilter || null
        const res = await attendanceService.getPunchAttendanceByDateRange(
          this.punchDownloadFrom, this.punchDownloadTo, empId
        )
        let rows = Array.isArray(res && res.data) ? res.data : []
        if (empId) {
          const empKey = String(empId)
          rows = rows.filter(r => r && String(r.emp_id) === empKey)
        }
        if (!rows.length) {
          this.$bvToast.toast('No punch records to export', { title: 'No Data', variant: 'warning' })
          return
        }
        const csvRows = rows.map(r => ({
          'Employee ID':    r.emp_id    || '',
          'Date':           r.date      || '',
          'Punch-In Time':  r.intime    || '',
          'Punch-Out Time': r.outtime   || '',
          'Work Hours':     r.worktime  || '',
          'Status':         r.attendance || '',
        }))
        const headers = Object.keys(csvRows[0])
        const csv = [
          headers.join(','),
          ...csvRows.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(',')),
        ].join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.setAttribute('download', `punch-data-${this.punchDownloadFrom}-to-${this.punchDownloadTo}.csv`)
        a.style.visibility = 'hidden'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.showPunchDataDownloadModal = false
      } catch (err) {
        console.error('Error downloading punch data:', err)
        this.$bvToast.toast('Failed to download punch data.', { title: 'Error', variant: 'danger' })
      } finally {
        this.punchDownloadLoading = false
      }
    },

    async downloadBirthdaysAnniversaries() {
      // Guard: ensure both dates are set (should always be true since modal validates)
      if (!this.celebrationDownloadFrom || !this.celebrationDownloadTo) {
        this.setCelebrationRange('thisMonth')
      }

      try {
        const startDate = moment(this.celebrationDownloadFrom).startOf('day')
        const endDate = moment(this.celebrationDownloadTo).endOf('day')
        const rangeLabel = `${startDate.format('DD-MMM-YYYY')}_to_${endDate.format('DD-MMM-YYYY')}`

        // Fetch all users
        const res = await userService.getAllUsers()
        if (!res || res.status !== 'OK' || !res.data) {
          throw new Error('Failed to fetch user data')
        }

        const allUsers = Array.isArray(res.data) ? res.data : []
        const activeUsers = allUsers.filter(u => {
          const s = u.status || u.employee_status || u.is_active
          return s === 'Active' || s === 'active' || s === true || s === 'ACTIVE'
        })

        const rows = []

        // Birthdays
        activeUsers.forEach(user => {
          const dobRaw = user.dob || user.date_of_birth || user.birthday
          if (!dobRaw) return
          const dob = moment(dobRaw)
          if (!dob.isValid()) return

          // Check current-year birthday; if before startDate, check next year
          let candidate = dob.clone().year(startDate.year())
          if (candidate.isBefore(startDate, 'day')) candidate.add(1, 'year')

          if (candidate.isBetween(startDate, endDate, 'day', '[]')) {
            const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || user.emp_id
            rows.push([
              name,
              user.emp_id || '',
              user.department || '',
              'Birthday',
              candidate.format('DD-MMM-YYYY'),
              '',
            ])
          }
        })

        // Work Anniversaries
        activeUsers.forEach(user => {
          const dojRaw = user.doj || user.date_of_joining || user.joining_date
          if (!dojRaw) return
          const doj = moment(dojRaw)
          if (!doj.isValid()) return

          let candidate = doj.clone().year(startDate.year())
          if (candidate.isBefore(startDate, 'day')) candidate.add(1, 'year')

          if (candidate.isBetween(startDate, endDate, 'day', '[]')) {
            const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || user.emp_id
            const years = candidate.year() - doj.year()
            rows.push([
              name,
              user.emp_id || '',
              user.department || '',
              'Work Anniversary',
              candidate.format('DD-MMM-YYYY'),
              `${years} year${years !== 1 ? 's' : ''}`,
            ])
          }
        })

        // Sort by date ascending
        rows.sort((a, b) => moment(a[4], 'DD-MMM-YYYY').diff(moment(b[4], 'DD-MMM-YYYY')))

        if (rows.length === 0) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'No Data',
              text: 'No birthdays or anniversaries found for the selected duration.',
              icon: 'InfoIcon',
              variant: 'info',
            },
          })
          return
        }

        const headers = ['Employee Name', 'Emp ID', 'Department', 'Type', 'Date', 'Years Completed']
        const escapeCSV = val => {
          const str = String(val ?? '')
          return str.includes(',') || str.includes('"') || str.includes('\n')
            ? `"${str.replace(/"/g, '""')}"`
            : str
        }
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(escapeCSV).join(',')),
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', `Birthdays_Anniversaries_${rangeLabel}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Downloaded',
            text: `${rows.length} record(s) exported successfully.`,
            icon: 'DownloadIcon',
            variant: 'success',
          },
        })
      } catch (err) {
        console.error('Error downloading birthdays & anniversaries:', err)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to download celebrations data. Please try again.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },

    openCreateAnnouncementModal() {
      this.resetAnnouncementForm()
      this.showCreateAnnouncementModal = true
    },
    resetAnnouncementForm() {
      this.announcementForm = {
        title: '',
        message: '',
        date: '',
      }
      this.creatingAnnouncement = false
    },
    openCreateHolidayModal() {
      this.resetHolidayForm()
      this.$bvModal.show('modal-create-holiday')
    },
    resetHolidayForm() {
      const currentYear = new Date().getFullYear()
      this.holidayForm = {
        holiday: '',
        date: '',
        location: null,
        year: currentYear,
      }
      this.creatingHoliday = false
    },
    async handleCreateHoliday(bvModalEvt) {
      if (bvModalEvt && typeof bvModalEvt.preventDefault === 'function') {
        bvModalEvt.preventDefault()
      }
      if (!(this.holidayForm.holiday || '').trim() || !this.holidayForm.date || !this.holidayForm.location) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: 'Please fill in holiday name, date, and location.',
          },
        })
        return
      }

      this.creatingHoliday = true
      try {
        const dateMoment = moment(this.holidayForm.date)
        // Determine pune and delhi based on location selection
        let pune = 'NO'
        let delhi = 'NO'
        if (this.holidayForm.location === 'both') {
          pune = 'YES'
          delhi = 'YES'
        } else if (this.holidayForm.location === 'pune') {
          pune = 'YES'
        } else if (this.holidayForm.location === 'delhi') {
          delhi = 'YES'
        }
        
        const holidayData = {
          holiday: this.holidayForm.holiday.trim(),
          date: this.holidayForm.date,
          day: dateMoment.format('dddd'),
          pune: pune,
          delhi: delhi,
          year: this.holidayForm.year || dateMoment.year(),
        }

        const res = await attendanceService.createHoliday(holidayData)
        
        if (res && res.status === 'OK') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              variant: 'success',
              text: 'Holiday added successfully!',
            },
          })

          // Company-wide FCM + local inbox (fire-and-forget). No email for
          // holidays today — see attendance-service.js#notifyHolidayAdded.
          if (this.canManageHolidays) {
            const currentUserData = JSON.parse(localStorage.getItem('userData') || '{}')
            const postedByName = `${currentUserData.first_name || ''} ${currentUserData.last_name || ''}`.trim()
              || currentUserData.name || currentUserData.title || 'A team member'
            const locationLabel = (this.locationOptions.find(opt => opt.value === this.holidayForm.location) || {}).text
              || 'All Locations'
            const createdHoliday = res.data && (Array.isArray(res.data) ? res.data[0] : res.data)
            attendanceService.notifyHolidayAdded({
              holiday: holidayData.holiday,
              date: holidayData.date,
              location: locationLabel,
              year: holidayData.year,
              holidayId: createdHoliday && (createdHoliday.id ?? createdHoliday.holiday_id),
              postedByName,
            })
          }
          
          // Close modal and reload holidays
          this.$bvModal.hide('modal-create-holiday')
          await this.loadHolidays()
        } else {
          throw new Error(res.message || 'Failed to create holiday')
        }
      } catch (err) {
        console.error('Error creating holiday:', err)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: err.message || 'Failed to add holiday. Please try again.',
          },
        })
      } finally {
        this.creatingHoliday = false
      }
    },
    openCreateCompanyWfhDayModal() {
      this.resetCompanyWfhDayForm()
      this.$bvModal.show('modal-create-company-wfh-day')
    },
    resetCompanyWfhDayForm() {
      this.companyWfhDayForm = {
        title: '',
        date: '',
        location: null,
      }
      this.creatingCompanyWfhDay = false
    },
    /**
     * Declares a new Company-Wide WFH Day.
     *
     * IMPORTANT — structural guarantee: this method calls ONLY
     * `attendanceService.createCompanyWfhDay()` (a pure date-level record,
     * mirroring `createHoliday()`). It must NEVER call
     * `attendanceService.applyLeave()` / `handleApplyLeaveV2()` for any
     * employee — doing so would be the exact code path that decrements
     * personal WFH balance, which this feature is explicitly designed to
     * never touch. Do not "helpfully" loop this over every employee's
     * individual leave application.
     */
    async handleCreateCompanyWfhDay() {
      if (!this.companyWfhDayForm.title.trim() || !this.companyWfhDayForm.date || !this.companyWfhDayForm.location) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: 'Please fill in the reason/title, date, and location.',
          },
        })
        return
      }

      this.creatingCompanyWfhDay = true
      try {
        // Same pune/delhi location-scoping convention as handleCreateHoliday()
        let pune = 'NO'
        let delhi = 'NO'
        if (this.companyWfhDayForm.location === 'both') {
          pune = 'YES'
          delhi = 'YES'
        } else if (this.companyWfhDayForm.location === 'pune') {
          pune = 'YES'
        } else if (this.companyWfhDayForm.location === 'delhi') {
          delhi = 'YES'
        }

        const companyWfhDayData = {
          title: this.companyWfhDayForm.title.trim(),
          date: this.companyWfhDayForm.date,
          pune: pune,
          delhi: delhi,
          created_by: this.emp_id,
        }

        const res = await attendanceService.createCompanyWfhDay(companyWfhDayData)

        if (res && res.status === 'OK') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              variant: 'success',
              text: 'Company-wide WFH day announced successfully!',
            },
          })

          // Notify sattva-all@sattvahuman.com of the new company-wide WFH
          // day — same fire-and-forget, defensively-re-checked-permission
          // pattern as handleCreateAnnouncement()'s notifyAnnouncementPosted
          // call below. Never awaited/blocking, and never throws — see
          // `attendance-service.js#notifyCompanyWfhDayAnnounced`.
          if (this.canManageCompanyWfhDays) {
            const currentUserData = JSON.parse(localStorage.getItem('userData') || '{}')
            const postedByName = `${currentUserData.first_name || ''} ${currentUserData.last_name || ''}`.trim()
              || currentUserData.name || currentUserData.title || 'A team member'
            const locationLabel = (this.locationOptions.find(opt => opt.value === this.companyWfhDayForm.location) || {}).text
              || 'All Locations'
            const createdWfh = res.data && (Array.isArray(res.data) ? res.data[0] : res.data)
            attendanceService.notifyCompanyWfhDayAnnounced({
              date: companyWfhDayData.date,
              reason: companyWfhDayData.title,
              location: locationLabel,
              postedByName,
              companyWfhId: createdWfh && (createdWfh.id ?? createdWfh.company_wfh_id),
            })
          }

          // Close modal and reload the company-wide WFH day list
          this.$bvModal.hide('modal-create-company-wfh-day')
          await this.loadCompanyWfhDays()
        } else {
          throw new Error(res.message || 'Failed to announce company-wide WFH day')
        }
      } catch (err) {
        console.error('Error creating company-wide WFH day:', err)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: err.message || 'Failed to announce company-wide WFH day. Please try again.',
          },
        })
      } finally {
        this.creatingCompanyWfhDay = false
      }
    },
    async handleCreateAnnouncement(bvModalEvt) {
      if (bvModalEvt && typeof bvModalEvt.preventDefault === 'function') {
        bvModalEvt.preventDefault()
      }
      if (!(this.announcementForm.title || '').trim() || !(this.announcementForm.message || '').trim() || !this.announcementForm.date) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: 'Please fill in all fields including title, date, and message.',
          },
        })
        return
      }

      this.creatingAnnouncement = true
      try {
        const announcementData = {
          title: this.announcementForm.title.trim(),
          message: this.announcementForm.message.trim(),
          date: this.announcementForm.date,
          created_by: this.emp_id,
        }

        const res = await attendanceService.createAnnouncement(announcementData)
        
        if (res && res.status === 'OK') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              variant: 'success',
              text: 'Announcement published successfully!',
            },
          })

          // Notify sattva-all@sattvahuman.com of the new announcement.
          // Defensive re-check of the "Add Announcement" ability here even
          // though the "+" button that opens this modal is already
          // `v-if="canManageAnnouncements"`-gated (see AnnouncementsCard.vue)
          // — this handler is reachable programmatically (e.g. dev tools,
          // future callers), so it should never email out on an announcement
          // an unauthorized user somehow managed to create. Fire-and-forget:
          // never awaited/blocking, and never throws — see
          // `attendance-service.js#notifyAnnouncementPosted`.
          if (this.canManageAnnouncements) {
            const currentUserData = JSON.parse(localStorage.getItem('userData') || '{}')
            const postedByName = `${currentUserData.first_name || ''} ${currentUserData.last_name || ''}`.trim()
              || currentUserData.name || currentUserData.title || 'A team member'
            const createdAnnouncement = res.data && (Array.isArray(res.data) ? res.data[0] : res.data)
            attendanceService.notifyAnnouncementPosted({
              title: announcementData.title,
              message: announcementData.message,
              date: announcementData.date,
              postedByName,
              announcementId: createdAnnouncement && (createdAnnouncement.id ?? createdAnnouncement.announcement_id),
            })
          }
          
          // Close modal and reload announcements
          this.showCreateAnnouncementModal = false
          await this.loadAnnouncements()
        } else {
          throw new Error(res.message || 'Failed to create announcement')
        }
      } catch (err) {
        console.error('Error creating announcement:', err)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: err.message || 'Failed to publish announcement. Please try again.',
          },
        })
      } finally {
        this.creatingAnnouncement = false
      }
    },
    updateLeaveBalanceChart() {
      // Use chart data from API if available (for individual/my view)
      if (this.dashboardData && this.dashboardData.chart_data && this.dashboardData.chart_data.leave_balance_chart) {
        const chartData = this.dashboardData.chart_data.leave_balance_chart
        
        // Map categories to short labels
        const categoryMapping = {
          'Privilege': 'PL',
          'Casual': 'CL',
          'Sick': 'SL',
          'Compensatory': 'CO',
          'Compensatory Off': 'CO',
        }
        
        // Update chart categories
        this.leaveBalanceChartOptions.xaxis.categories = chartData.categories.map(cat => categoryMapping[cat] || cat)
        
        // Update chart series from API data
        this.leaveBalanceChartSeries = chartData.series.map(series => ({
          name: series.name,
          data: [...series.data]
        }))
        
        // Calculate max value for y-axis
        const allValues = chartData.series.flatMap(s => s.data)
        const maxValue = allValues.length > 0 ? Math.max(...allValues, 5) : 10
        const dynamicMax = Math.ceil(maxValue * 1.2)
        
        // Update y-axis max
        this.leaveBalanceChartOptions = {
          ...this.leaveBalanceChartOptions,
          yaxis: {
            ...this.leaveBalanceChartOptions.yaxis,
            max: dynamicMax
          }
        }
        
        // Add click event handler for "Leaves Taken" bars
        const self = this
        if (!this.leaveBalanceChartOptions.chart.events) {
          this.leaveBalanceChartOptions.chart.events = {}
        }
        this.leaveBalanceChartOptions.chart.events.dataPointSelection = function(event, chartContext, config) {
          if (config.seriesIndex === 1 && config.dataPointIndex !== undefined) {
            const leaveType = config.w.globals.labels[config.dataPointIndex]
            self.handleLeavesTakenClick(leaveType)
          }
        }
        
        return // Exit early, chart is updated from API data
      }
      
      // Legacy fallback: Calculate chart data from leaves (for team view or when API data not available)
      // Check if we're in team view showing all team members
      const isShowingAllTeam = this.viewMode === 'team' && 
        ((this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) || 
         (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) || 
         (this.canApplyOnBehalfAll && !this.selectedAccountManager))
      
      // Get leaves data - use displayedLeaves or myLeaves based on view mode
      let targetEmpId = this.currentViewEmpId || this.emp_id
      
      // If a specific team member or employee is selected, use their emp_id
      if (this.selectedTeamMember) {
        targetEmpId = this.selectedTeamMember
      } else if (this.selectedTeamWiseEmployee) {
        targetEmpId = this.selectedTeamWiseEmployee
      }
      
      // Use allTeamLeaves when filtering by employee or showing all team
      // In employee view, use myLeaves instead of allTeamLeaves (which may be empty)
      const leavesToAnalyze = isShowingAllTeam
        ? this.allTeamLeaves
        : (this.viewMode === 'my' ? this.myLeaves.filter(leave => leave && leave.emp_id === targetEmpId) : this.allTeamLeaves.filter(leave => leave && leave.emp_id === targetEmpId))

      // Filter only approved leaves for "taken" calculation (case-insensitive)
      const approvedLeaves = leavesToAnalyze.filter(leave => {
        const status = (leave.status || '').toLowerCase()
        return status === 'approved' || status === 'manager approved - pending hr'
      })

      // Group leaves by type and calculate days
      const leaveTypeStats = {}
      
      // Initialize with all leave types (Half Day is NOT a separate category - it goes to Privilege Leave)
      const allLeaveTypes = ['Casual','Privilege Leave', 'Paternity', 'Maternity', 'Sick', 'Compensatory','Exam']
      allLeaveTypes.forEach(type => {
        leaveTypeStats[type] = {
          taken: 0,
        }
      })

      // Calculate taken leaves by type
      approvedLeaves.forEach(leave => {
        const leaveType = leave.leave_type || 'Present'
        // Map leave types - normalize common variations
        let mappedType = leaveType
        const leaveTypeLower = leaveType.toLowerCase().trim()
        
        // Check if it's a half-day leave
        const isHalfDay = leaveType === '1st Half Day' ||
                         leaveType === '2nd Half Day' ||
                         leaveTypeLower.includes('half day') ||
                         leave.half_day === true
        
        // Normalize leave type names to match chart categories
        if (isHalfDay) {
          // Half days are counted as Privilege Leave
          mappedType = 'Privilege Leave'
        } else if (leaveType === 'Present') {
          // Present leaves can be mapped to Casual as default
          mappedType = 'Casual'
        } else if (leaveTypeLower.includes('casual')) {
          // "Casual Leave", "Casual", etc. -> "Casual"
          mappedType = 'Casual'
        } else if (leaveTypeLower.includes('medical') || leaveTypeLower.includes('sick')) {
          // "Medical Leave", "Sick Leave", "Medical", "Sick" -> "Sick"
          mappedType = 'Sick'
        } else if (leaveTypeLower.includes('privilege') || leaveTypeLower.includes('pto')) {
          // "Privilege Leave", "PTO", "Privilege" -> "Privilege Leave"
          mappedType = 'Privilege Leave'
        } else if (leaveTypeLower.includes('paternity')) {
          mappedType = 'Paternity'
        } else if (leaveTypeLower.includes('maternity')) {
          mappedType = 'Maternity'
        } else if (leaveTypeLower.includes('compensatory')) {
          mappedType = 'Compensatory'
        }
        
        // Calculate days - for half days, always use 0.5 per day in range (ignore total_days which might be wrong)
        let days = 0
        if (isHalfDay) {
          // For half-day leaves, always count as 0.5 per day (don't trust total_days from backend)
          if (leave.from_date && leave.to_date) {
            // Calculate days in range and multiply by 0.5
            const from = moment(leave.from_date)
            const to = moment(leave.to_date)
            const daysInRange = to.diff(from, 'days') + 1
            days = daysInRange * 0.5
          } else {
            days = 0.5 // Default to 0.5 for half day
          }
        } else {
          // For full day leaves, use total_days or calculate
          days = leave.total_days !== undefined && leave.total_days !== null 
            ? parseFloat(leave.total_days) 
            : this.calculateDays(leave.from_date, leave.to_date)
        }
        
        if (leaveTypeStats[mappedType] !== undefined) {
          leaveTypeStats[mappedType].taken += days
        } else if (leaveTypeStats[leaveType]) {
          // If exact match exists in stats
          leaveTypeStats[leaveType].taken += days
        } else {
          // If it's a new type not in our list, add it
          if (!leaveTypeStats[leaveType]) {
            leaveTypeStats[leaveType] = { taken: 0 }
          }
          leaveTypeStats[leaveType].taken += days
        }
      })

      // Get total available leaves from displayKPIStats
      const totalAvailable = (this.displayKPIStats && this.displayKPIStats.leaveBalance) ? this.displayKPIStats.leaveBalance : 0
      // Calculate total taken from leaveTypeStats instead of displayKPIStats for accuracy
      const totalTakenFromStats = Object.values(leaveTypeStats).reduce((sum, stat) => sum + (stat.taken || 0), 0)
      const totalTaken = totalTakenFromStats > 0 ? totalTakenFromStats : ((this.displayKPIStats && this.displayKPIStats.leavesTaken) ? this.displayKPIStats.leavesTaken : 0)

      // For team view, aggregate breakdown from all team members
      let breakdown, usage, assignedMapping, remainingPL, remainingCF
      if (isShowingAllTeam && this.teamMemberConfigs && this.teamMemberConfigs.length > 0) {
        // Aggregate breakdown from all team members
        const aggregatedBreakdown = {
          carry_forward: 0,
          casual_leave: 0,
          sick_leave: 0,
          medical_leave: 0,
          compensatory_balance: 0,
          paternity_leave: 0,
          maternity_leave: 0,
        }
        
        // Aggregate assigned balances from all team members
        this.teamMemberConfigs.forEach(memberConfig => {
          if (!memberConfig.config) return
          const config = memberConfig.config
          let memberBreakdown = config.leaveBalanceBreakdown || config.leave_balance_breakdown
          
          // Parse breakdown if it's a string
          if (typeof memberBreakdown === 'string') {
            try {
              memberBreakdown = JSON.parse(memberBreakdown)
            } catch (e) {
              memberBreakdown = null
            }
          }
          
          if (memberBreakdown && typeof memberBreakdown === 'object') {
            aggregatedBreakdown.carry_forward += memberBreakdown.carry_forward || 0
            aggregatedBreakdown.casual_leave += memberBreakdown.casual_leave || 0
            aggregatedBreakdown.sick_leave += memberBreakdown.sick_leave || 0
            aggregatedBreakdown.medical_leave += memberBreakdown.medical_leave || 0
            aggregatedBreakdown.compensatory_balance += memberBreakdown.compensatory_balance || 0
            aggregatedBreakdown.paternity_leave += memberBreakdown.paternity_leave || 0
            aggregatedBreakdown.maternity_leave += memberBreakdown.maternity_leave || 0
          }
        })
        
        breakdown = aggregatedBreakdown
        
        // Aggregate usage from all team leaves
        const aggregatedUsage = {
          privilege_leave: 0,
          casual_leave: 0,
          sick_leave: 0,
          compensatory_balance: 0,
          paternity_leave: 0,
          maternity_leave: 0,
        }
        
        approvedLeaves.forEach(leave => {
          const leaveType = (leave.leave_type || '').toLowerCase()
          const isHalfDay = leaveType.includes('half day') || 
                           leave.leave_type === '1st Half Day' || 
                           leave.leave_type === '2nd Half Day' ||
                           leave.half_day === true
          
          let days = 0
          if (isHalfDay) {
            if (leave.from_date && leave.to_date) {
              const from = moment(leave.from_date)
              const to = moment(leave.to_date)
              const daysInRange = to.diff(from, 'days') + 1
              days = daysInRange * 0.5
            } else {
              days = 0.5
            }
          } else {
            days = parseFloat(leave.total_days || 0)
          }
          
          if (leaveType.includes('casual')) {
            aggregatedUsage.casual_leave += days
          } else if (leaveType.includes('sick') || leaveType.includes('medical')) {
            aggregatedUsage.sick_leave += days
          } else if (leaveType.includes('privilege') || leaveType.includes('pto') || isHalfDay) {
            aggregatedUsage.privilege_leave += days
          } else if (leaveType.includes('compensatory')) {
            aggregatedUsage.compensatory_balance += days
          } else if (leaveType.includes('paternity')) {
            aggregatedUsage.paternity_leave += days
          } else if (leaveType.includes('maternity')) {
            aggregatedUsage.maternity_leave += days
          }
        })
        
        usage = aggregatedUsage
        
        // Calculate PL/CF for team (aggregated)
        const plTaken = usage.privilege_leave || 0
        const cfTotal = aggregatedBreakdown.carry_forward
        const currentMonth = moment().month() + 1
        const plAccrued = this.teamMemberConfigs.length * currentMonth // 1 per month per employee
        
        remainingCF = Math.max(0, cfTotal - plTaken)
        remainingPL = plAccrued
        if (plTaken > cfTotal) {
          remainingPL = Math.max(0, plAccrued - (plTaken - cfTotal))
        }
        
        // Static Assigned Mapping for team (aggregated)
        assignedMapping = {
          'Casual': aggregatedBreakdown.casual_leave,
          'Privilege Leave': cfTotal + plAccrued,  // Total CF + total accrued months
          'Sick': aggregatedBreakdown.sick_leave + aggregatedBreakdown.medical_leave,
          'Paternity': aggregatedBreakdown.paternity_leave,
          'Maternity': aggregatedBreakdown.maternity_leave,
          'Compensatory': aggregatedBreakdown.compensatory_balance,
        }
      } else {
        // Individual view: use individual breakdown
        breakdown = (this.employeeConfig && this.employeeConfig.leaveBalanceBreakdown) 
          ? this.employeeConfig.leaveBalanceBreakdown 
          : this.leaveStats
        
        // Calculate PL/CF Remaining (1 per month accrual, CF-first deduction)
        usage = this.leaveUsageByType
        const plTaken = usage['privilege_leave'] || 0
        const cfTotal = (breakdown && breakdown.carry_forward) ? breakdown.carry_forward : 0
        const currentMonth = moment().month() + 1
        
        remainingCF = Math.max(0, cfTotal - plTaken)
        remainingPL = currentMonth
        if (plTaken > cfTotal) {
          remainingPL = Math.max(0, currentMonth - (plTaken - cfTotal))
        }
        
        // Static Assigned Mapping - Shows original allocation (not reduced by usage)
        // This allows users to see if they've exceeded their allocation
        assignedMapping = {
          'Casual': (breakdown && breakdown.casual_leave) ? breakdown.casual_leave : 0,
          'Privilege Leave': cfTotal + currentMonth,  // CF + accrued months (total allocated up to current month)
          'Sick': ((breakdown && breakdown.sick_leave) || 0) + ((breakdown && breakdown.medical_leave) || 0),
          'Paternity': (breakdown && breakdown.paternity_leave) ? breakdown.paternity_leave : 0,
          'Maternity': (breakdown && breakdown.maternity_leave) ? breakdown.maternity_leave : 0,
          'Compensatory': (breakdown && breakdown.compensatory_balance) ? breakdown.compensatory_balance : 0,
        }
      }
      

      // Build categories and data arrays
      const categories = []
      const availableData = []
      const takenData = []

      // Show leave types that have data or are common types
      const commonTypes = ['Casual', 'Privilege Leave', 'Sick', 'Paternity', 'Maternity']
      
      commonTypes.forEach(type => {
        const taken = leaveTypeStats[type] ? leaveTypeStats[type].taken : 0
        // Use assignedMapping for static assigned values (not reduced by usage)
        const assigned = assignedMapping[type] || 0
        
        // Show types that have taken leaves, assigned leaves, or are important types
        if (taken > 0 || assigned > 0 || ['Casual', 'Sick', 'Privilege Leave'].includes(type)) {
          categories.push(type)
          // Preserve decimals for taken data (e.g., 0.5 for half days)
          takenData.push(parseFloat(taken.toFixed(1)))
          // Always use assigned value (static total available) - not reduced by usage
          // This shows the total allocated/assigned balance, not the remaining balance
          availableData.push(parseFloat(assigned.toFixed(1)))
        }
      })

      // Also include any other leave types that have data (excluding Half Day as it's now part of Privilege Leave)
      Object.keys(leaveTypeStats).forEach(type => {
        if (!commonTypes.includes(type) && leaveTypeStats[type].taken > 0 && type !== 'Half Day') {
          categories.push(type)
          // Preserve decimals for taken data
          takenData.push(parseFloat(leaveTypeStats[type].taken.toFixed(1)))
          // Always use assigned value (static total available) - not reduced by usage
          const assigned = assignedMapping[type] || 0
          availableData.push(parseFloat(assigned.toFixed(1)))
        }
      })

      // If no data, show at least the main types with actual breakdown or fallback to averages
      if (categories.length === 0) {
        categories.push('Casual', 'Sick', 'Privilege Leave')
        // Use assigned values (static) if available, otherwise distribute total available
        const casualAssigned = assignedMapping['Casual'] || (totalAvailable > 0 ? Math.round(totalAvailable / 3) : 0)
        const sickAssigned = assignedMapping['Sick'] || (totalAvailable > 0 ? Math.round(totalAvailable / 3) : 0)
        const privilegeAssigned = assignedMapping['Privilege Leave'] || (totalAvailable > 0 ? Math.round(totalAvailable / 3) : 0)
        availableData.push(casualAssigned, sickAssigned, privilegeAssigned)
        const avgTaken = totalTaken > 0 ? Math.round(totalTaken / 3) : 0
        takenData.push(avgTaken, avgTaken, avgTaken)
      }

      // Update chart categories and series
      const categoryMapping = {
        'Casual': 'CL',
        'Casual Leave': 'CL',
        'Privilege Leave': 'PL',
        'Privilege': 'PL',
        'Sick': 'SL',
        'Sick Leave': 'SL',
        'Compensatory': 'CO',
        'Compensatory Leave': 'CO',
        'Work From Home': 'WFH',
        'Work from Home': 'WFH',
        'Work From Office': 'WFO'
      }
      this.leaveBalanceChartOptions.xaxis.categories = categories.map(cat => categoryMapping[cat] || cat)

      // Add click event handler for "Leaves Taken" bars
      const self = this
      if (!this.leaveBalanceChartOptions.chart.events) {
        this.leaveBalanceChartOptions.chart.events = {}
      }
      this.leaveBalanceChartOptions.chart.events.dataPointSelection = function(event, chartContext, config) {
        // Only handle clicks on "Leaves Taken" series (seriesIndex 1)
        if (config.seriesIndex === 1 && config.dataPointIndex !== undefined) {
          const leaveType = config.w.globals.labels[config.dataPointIndex]
          self.handleLeavesTakenClick(leaveType)
        }
      }

      // Update chart series with leave type data
      // Force Vue reactivity by creating new array and objects
      this.leaveBalanceChartSeries = [
        {
          name: 'Leaves Remaining',
          data: [...availableData], // Create new array
        },
        {
          name: 'Leaves Taken',
          data: [...takenData], // Create new array
        },
      ]

      // Update y-axis max to accommodate values with some padding
      const allValues = [...availableData, ...takenData]
      const maxValue = allValues.length > 0 ? Math.max(...allValues, 5) : 10
      const dynamicMax = Math.ceil(maxValue * 1.2)
      
      // Force chart update by creating a new options object with updated yaxis
      this.leaveBalanceChartOptions = {
        ...this.leaveBalanceChartOptions,
        yaxis: {
          ...this.leaveBalanceChartOptions.yaxis,
          max: dynamicMax
        }
      }
    },
  },
}
</script>

<style src="./AttendanceDashboard.css" scoped></style>

<!-- Unscoped: light page chrome under Attendance (overrides dark-layout body) -->
<style>
.dark-layout .app-content:has(.attendance-dashboard),
.dark-layout .content-body:has(.attendance-dashboard),
.dark-layout .content-wrapper:has(.attendance-dashboard) {
  background-color: #f8f8f8 !important;
}

/* Fallback when :has() unsupported — root already paints #f8f8f8 */
.attendance-dashboard {
  background-color: #f8f8f8;
}
</style>

<!-- Modals teleport to <body>; must stay unscoped so phone/tablet width rules apply -->
<style>
/* Vuexy scroll-top FAB: clear Leaves mobile action buttons on attendance */
@media (max-width: 991px) {
  body:has(.attendance-dashboard) .scroll-top {
    bottom: 5.5rem;
  }
}

@media (max-width: 767px) {
  .apply-leave-modal .modal-dialog,
  .attendance-apply-modal .modal-dialog,
  .leave-action-modal .modal-dialog,
  #modal-leave-detail .modal-dialog,
  #modal-filters .modal-dialog,
  #modal-regularization-filters .modal-dialog,
  #modal-approve-leave .modal-dialog,
  #modal-reject-leave .modal-dialog,
  #modal-approve-regularization .modal-dialog,
  #modal-reject-regularization .modal-dialog,
  #modal-apply-leave .modal-dialog,
  #modal-apply-compoff .modal-dialog,
  #modal-request-compoff-balance .modal-dialog,
  #modal-apply-wfh .modal-dialog,
  #modal-regularization .modal-dialog,
  #modal-date-range-filter .modal-dialog,
  #modal-punch-data-download .modal-dialog,
  #modal-celebration-download .modal-dialog {
    margin: 0.4rem auto;
    max-width: calc(100% - 0.8rem);
    width: calc(100% - 0.8rem);
  }

  .apply-leave-modal .modal-content,
  .attendance-apply-modal .modal-content,
  #modal-leave-detail .modal-content {
    max-height: calc(100vh - 0.8rem);
  }

  #modal-filters .modal-body,
  #modal-regularization-filters .modal-body,
  #modal-approve-leave .modal-body,
  #modal-reject-leave .modal-body,
  .apply-leave-modal .modal-body {
    padding: 0.65rem 0.75rem;
  }

  #modal-filters input[type='date'],
  #modal-regularization-filters input[type='date'],
  #modal-approve-leave input[type='date'],
  #modal-reject-leave input[type='date'],
  .apply-leave-modal input[type='date'],
  .attendance-apply-modal input[type='date'],
  #modal-filters .form-control,
  #modal-regularization-filters .form-control {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  #modal-filters .form-row > [class*='col'],
  #modal-regularization-filters .form-row > [class*='col'] {
    min-width: 0;
  }

  .leave-action-modal .leave-action-columns > [class*='col'] {
    flex: 0 0 100%;
    max-width: 100%;
    margin-bottom: 0.65rem;
  }

  #modal-leave-detail .leave-detail-footer-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 0.4rem;
    width: 100%;
  }

  #modal-leave-detail .leave-detail-footer-actions .btn {
    flex: 0 0 auto;
    width: 100%;
    min-height: 38px;
    margin: 0 !important;
  }
}

@media (max-width: 575px) {
  .apply-leave-modal .modal-dialog,
  .attendance-apply-modal .modal-dialog,
  .leave-action-modal .modal-dialog,
  #modal-leave-detail .modal-dialog,
  #modal-filters .modal-dialog,
  #modal-regularization-filters .modal-dialog,
  #modal-approve-leave .modal-dialog,
  #modal-reject-leave .modal-dialog,
  #modal-approve-regularization .modal-dialog,
  #modal-reject-regularization .modal-dialog,
  #modal-apply-leave .modal-dialog,
  #modal-apply-compoff .modal-dialog,
  #modal-request-compoff-balance .modal-dialog,
  #modal-apply-wfh .modal-dialog {
    margin: 0.25rem auto;
    max-width: calc(100% - 0.5rem);
    width: calc(100% - 0.5rem);
  }

  #modal-filters .form-row > [class*='col'],
  #modal-regularization-filters .form-row > [class*='col'] {
    flex: 0 0 100%;
    max-width: 100%;
  }
}
</style>
